/* aviaf — pages: distinct visual languages per concept */
const { useState: usePg, useEffect: usePgE, useMemo: usePgM, useRef: usePgR } = React;

/* Tiny "live" pulse — fades in on each window.__LIVE.rev bump so the
 * operator can see at a glance whether the page is actively receiving
 * data or stuck on a stale snapshot.  Caller passes lastUpdateMs (the
 * timestamp at which the current rev was first observed); we re-render
 * once a second to update the "Ns ago" tooltip and fade. */
function LiveDot({ lastUpdateMs }) {
  const [, tick] = usePg(0);
  usePgE(() => {
    const id = setInterval(() => tick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);
  const age = Math.max(0, Math.floor((Date.now() - (lastUpdateMs || 0)) / 1000));
  const fresh = age < 3;
  const stale = age > 60;
  const color = stale ? "var(--muted-2)" : fresh ? "var(--pos)" : "var(--warn)";
  const label = stale
    ? `stale · last update ${age}s ago`
    : fresh
      ? `live · just updated`
      : `updated ${age}s ago`;
  return (
    <span title={label} aria-label={label} style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      marginLeft: 10, fontFamily: "var(--mono)",
      fontSize: 10, color: "var(--muted)", letterSpacing: "0.04em",
      textTransform: "uppercase", verticalAlign: "middle",
    }}>
      <span style={{
        width: 7, height: 7, background: color,
        borderRadius: "50%",
        animation: fresh ? "pulse 1.4s ease-out infinite" : "none",
        flexShrink: 0,
      }} />
      <span style={{ color: stale ? "var(--muted-2)" : "var(--muted)" }}>
        {stale ? "stale" : fresh ? "live" : `${age}s`}
      </span>
    </span>
  );
}

/* ── Research-vs-Live curves ──────────────────────────────────
 * Live: cumulative P&L per (account, strategy) computed from exit fills
 *   in window.TRADES — this is the only source that's actually keyed
 *   per-strategy.  PNL_BY_STRATEGY (built from equity events) only knows
 *   account balance, so it can't split a multi-strategy account; falling
 *   back to it would attribute all of prac's P&L to its primary strategy
 *   and leave the other tabs (mr, orb_test) empty.
 * Research: deterministic straight-line projection from 0 to
 *   bench.eR × risk × max(trades, 20), drawn over the same grid.
 */
window.genCurvePair = function (strat, rangeDays = 30, gridLen = 60) {
  const def = (window.STRATEGY_DEFS || []).find((d) => d.id === strat.defId);
  const nowUs = Date.now() * 1000;
  const windowUs = rangeDays * 86_400 * 1_000_000;
  const startUs = nowUs - windowUs;

  // Build per-(acct, strategy) cumulative P&L from exit fills.  Each
  // closed round-trip in window.TRADES has acct + strategy + pnl + an
  // exitTime ISO string.  Sort by exit time, walk cumulative sums,
  // record [ts_us, cum_pnl] points.
  const trades = window.TRADES || [];
  const matched = trades.filter((t) =>
    t.isExit &&
    t.strategy !== "reconciler" &&
    (!strat.acct || t.account === strat.acct) &&
    t.strategy === strat.defId &&
    t.exitTime
  ).sort((a, b) => new Date(a.exitTime) - new Date(b.exitTime));

  // Cumulative-from-inception over all matched trades, then we slice the
  // points inside the window.  The pre-window cumulative becomes the
  // anchor so the windowed curve still starts at 0.
  let cum = 0;
  let preWindowCum = 0;
  const inWindowPnl = [];
  const inWindowTs = [];
  for (const t of matched) {
    const ts = new Date(t.exitTime).getTime() * 1000;
    cum += t.pnl || 0;
    if (ts < startUs) {
      preWindowCum = cum;
    } else {
      inWindowTs.push(ts);
      inWindowPnl.push(cum);
    }
  }

  // Anchor the window to 0: subtract preWindowCum so we show "P&L change
  // over the window," matching the research projection's $0 start.
  // Prepend a synthetic (startUs, 0) point so the line begins at the
  // left edge of the window even when the first real fill is mid-window;
  // append (nowUs, lastValue) so the right edge reflects current state
  // even on quiet days.
  const live = [0, ...inWindowPnl.map((v) => v - preWindowCum)];
  const tsWindow = [startUs, ...inWindowTs];
  if (tsWindow[tsWindow.length - 1] < nowUs) {
    live.push(live[live.length - 1] ?? 0);
    tsWindow.push(nowUs);
  }
  // Skip the research projection when the bench lacks the inputs to draw
  // a meaningful target slope: eR (R-units), risk_R ($ per 1R loss), or
  // tpd (trades per day).  Falling back to placeholder risk (0.5% of
  // account capital) and a fake TPD made the line look identical across
  // wildly different strategies — drawing it then was misleading, so we
  // hide the line + the legend entry instead.
  const eR = def?.bench?.eR;
  const riskR = Number(def?.bench?.risk_R) || Number(strat.riskPerTrade) || 0;
  const tpd = Number(def?.bench?.tpd) || 0;
  if (!def || !Number.isFinite(eR) || eR === 0 || riskR === 0 || tpd === 0) {
    return { research: null, live, ts: tsWindow };
  }
  // Research projection: a fixed target slope determined by the strategy's
  // research bench, not by what the user has actually traded.  Each step
  // is one trading day; over `rangeDays` the line ends at
  // eR × risk_R × tpd × rangeDays.  Drawn over the same point count as
  // the live curve so aggregateCurves can sum across strategies cleanly.
  const expectedEnd = eR * riskR * tpd * rangeDays;
  const research = [];
  const n = Math.max(live.length, 2);
  for (let i = 0; i < n; i++) research.push(expectedEnd * i / (n - 1));
  return { research, live, ts: tsWindow };
};

window.aggregateCurves = function (strats, rangeDays = 30) {
  // research is null when no strategy in `strats` has bench data;
  // ResearchVsLivePlot checks for this and hides the research line +
  // legend entry.  Each strategy contributes its own time-windowed live
  // curve; we align by index since they all read the same EQUITY_TS axis.
  let anyResearch = false;
  let maxLen = 1;
  let tsOut = [];
  const pairs = (strats || []).map((s) => {
    const p = window.genCurvePair(s, rangeDays);
    if (p.live.length > maxLen) maxLen = p.live.length;
    if (Array.isArray(p.ts) && p.ts.length > tsOut.length) tsOut = p.ts;
    return p;
  });
  const out = { research: new Array(maxLen).fill(0), live: new Array(maxLen).fill(0), ts: tsOut };
  pairs.forEach((p) => {
    // Right-align: a strategy with fewer points gets zero-padded at the
    // start so totals don't jump when one strategy's series is shorter.
    const liveOffset = maxLen - p.live.length;
    for (let i = 0; i < p.live.length; i++) out.live[liveOffset + i] += p.live[i] || 0;
    if (p.research) {
      anyResearch = true;
      const rOffset = maxLen - p.research.length;
      for (let i = 0; i < p.research.length; i++) out.research[rOffset + i] += p.research[i] || 0;
    }
  });
  if (!anyResearch) out.research = null;
  return out;
};

/* ── Research vs Live plot ──────────────────────────────────── */
function ResearchVsLivePlot({ strats, height = 240, initial = "all" }) {
  const [selected, setSelected] = usePg(initial);
  const [range, setRange] = usePg("30D");
  // Range maps to days, not point count — the live series is hourly so
  // a count-based tail would collapse 5D into the last 10 hours.
  const rangeDaysMap = { "5D": 5, "30D": 30, "90D": 90 };
  const rangeDays = rangeDaysMap[range] || 30;

  if (!strats || strats.length === 0) {
    return (
      <div style={{ padding: "32px var(--pad)", color: "var(--muted)", fontFamily: "var(--mono)", fontSize: 12, textAlign: "center", borderBottom: "1px solid var(--rule)" }}>
        No strategies deployed — nothing to compare.
      </div>);

  }

  const filtered = selected === "all" ? strats : strats.filter((s) => s.id === selected);
  const pair = window.aggregateCurves(filtered, rangeDays);
  // Research curve is null when none of the selected strategies have
  // bench data (STRATEGY_DEFS entry + risk-per-trade + non-zero E[R]).
  // We hide the research line, divergence area, divergence histogram,
  // and the research legend entry in that case — drawing a flat zero
  // line as "research" was misleading.
  const hasResearch = Array.isArray(pair.research);
  const divergence = hasResearch ? pair.live.map((v, i) => v - pair.research[i]) : [];

  const w = 1000,h = height,padL = 60,padR = 16,padT = 14,padB = 56;
  const divBandH = hasResearch ? 28 : 0;
  const all = [...(pair.research || []), ...pair.live, 0];
  let lo = Math.min(...all),hi = Math.max(...all);
  if (lo === hi) hi = lo + 1;
  const span = hi - lo;
  lo -= span * 0.08;hi += span * 0.08;
  const x = (i, n) => padL + i / Math.max(1, n - 1) * (w - padL - padR);
  const y = (v) => padT + (1 - (v - lo) / (hi - lo)) * (h - padT - padB - divBandH);

  const pathOf = (arr) => arr.map((v, i) => (i ? "L" : "M") + x(i, arr.length).toFixed(1) + " " + y(v).toFixed(1)).join(" ");

  // divergence area between research and live (only when both exist)
  let areaPath = "";
  if (hasResearch) {
    const areaPts = [];
    for (let i = 0; i < pair.research.length; i++) areaPts.push([x(i, pair.research.length), y(pair.research[i])]);
    for (let i = pair.live.length - 1; i >= 0; i--) areaPts.push([x(i, pair.live.length), y(pair.live[i])]);
    areaPath = "M " + areaPts.map((p) => p[0].toFixed(1) + " " + p[1].toFixed(1)).join(" L ") + " Z";
  }

  const liveLast = pair.live.at(-1) || 0;
  const resLast = hasResearch ? (pair.research.at(-1) || 0) : 0;
  const divLast = hasResearch ? (liveLast - resLast) : 0;
  const divPct = (hasResearch && resLast !== 0) ? divLast / Math.abs(resLast) * 100 : 0;

  const divBaseY = h - padB + divBandH * 0.5;
  const divMax = hasResearch ? Math.max(...divergence.map(Math.abs), 1) : 1;

  return (
    <div className="plot-wrap rv-plot">
      <div className="rv-tabs">
        <div className="rv-tab" data-active={selected === "all"} onClick={() => setSelected("all")}>
          ALL <span className="muted">· {strats.length} cfg</span>
        </div>
        {strats.map((s) => {
          const def = window.STRATEGY_DEFS.find((d) => d.id === s.defId);
          const div = s.eR - (def ? def.bench.eR : 0);
          return (
            <div key={s.id} className="rv-tab" data-active={selected === s.id} onClick={() => setSelected(s.id)}>
              <span className="rv-led" data-state={s.state} />
              {s.name}
              <span className="muted" style={{ marginLeft: 6 }}>· {s.acct} · {s.contracts}c</span>
              <span className={"rv-div tnum " + (div >= 0 ? "pos" : "neg")}>{div >= 0 ? "↑" : "↓"} {Math.abs(div).toFixed(2)}</span>
            </div>);

        })}
        <div style={{ flex: 1 }} />
        <div className="rv-range">
          {["5D", "30D", "90D"].map((r) =>
          <div key={r} className="rv-tab small" data-active={range === r} onClick={() => setRange(r)}>{r}</div>
          )}
        </div>
      </div>

      <div className="rv-legend">
        {hasResearch && (
          <span className="rv-leg">
            <svg width="22" height="6" style={{ verticalAlign: "middle" }}><line x1="0" y1="3" x2="22" y2="3" stroke="var(--muted)" strokeWidth="1.4" strokeDasharray="3 3" /></svg>
            <span style={{ color: "var(--ink-2)", marginLeft: 6 }}>Research promise</span>
            <span className="muted tnum" style={{ marginLeft: 6 }}>{window.fmt$(resLast, 0)}</span>
          </span>
        )}
        <span className="rv-leg">
          <svg width="22" height="6" style={{ verticalAlign: "middle" }}><line x1="0" y1="3" x2="22" y2="3" stroke="var(--ink)" strokeWidth="2" /></svg>
          <span style={{ color: "var(--ink-2)", marginLeft: 6 }}>Live actual</span>
          <span className="muted tnum" style={{ marginLeft: 6 }}>{window.fmt$(liveLast, 0)}</span>
        </span>
        <span style={{ flex: 1 }} />
        {hasResearch && (
          <span className="rv-leg" style={{ color: "var(--ink-2)" }}>
            <span style={{ fontFamily: "var(--mono)", fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--muted)" }}>divergence</span>
            <span className="tnum" style={{ marginLeft: 6 }}>{divLast >= 0 ? "+" : ""}{window.fmt$(divLast, 0)}</span>
            <span className="muted tnum" style={{ marginLeft: 6, fontSize: 10.5 }}>({divPct >= 0 ? "+" : ""}{divPct.toFixed(1)}%)</span>
          </span>
        )}
        {!hasResearch && (
          <span className="rv-leg" style={{ color: "var(--muted)" }}>no research reference for this strategy</span>
        )}
      </div>

      <svg className="plot-svg" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" style={{width:"100%",height:height+"px",display:"block"}}>
        {/* y-grid */}
        {[0, 0.25, 0.5, 0.75, 1].map((t, i) => {
          const v = lo + (hi - lo) * t;
          return (
            <g key={i}>
              <line x1={padL} x2={w - padR} y1={y(v)} y2={y(v)} stroke="var(--rule)" strokeWidth="1" vectorEffect="non-scaling-stroke" />
              <text x={padL - 8} y={y(v) + 3} textAnchor="end" fontFamily="var(--mono)" fontSize="10" fill="var(--muted)">{window.fmt$(v, 0)}</text>
            </g>);

        })}

        {/* zero line */}
        {lo < 0 && hi > 0 &&
        <line x1={padL} x2={w - padR} y1={y(0)} y2={y(0)} stroke="var(--ink)" strokeWidth="1" strokeDasharray="1 4" opacity="0.5" vectorEffect="non-scaling-stroke" />
        }

        {hasResearch && <>
          {/* divergence shaded area */}
          <path d={areaPath} fill="var(--ink)" opacity="0.07" />

          {/* research path (dashed, muted) */}
          <path d={pathOf(pair.research)} fill="none" stroke="var(--muted)" strokeWidth="1.4" strokeDasharray="4 4" vectorEffect="non-scaling-stroke" />
          <circle cx={x(pair.research.length - 1, pair.research.length)} cy={y(resLast)} r="2.6" fill="var(--paper)" stroke="var(--muted)" strokeWidth="1.4" />
          <text x={w - padR - 4} y={y(resLast) - 6} textAnchor="end" fontFamily="var(--mono)" fontSize="10" fill="var(--muted)">research</text>
        </>}

        {/* live path (solid, ink) */}
        <path d={pathOf(pair.live)} fill="none" stroke="var(--ink)" strokeWidth="1.9" vectorEffect="non-scaling-stroke" />
        <circle cx={x(pair.live.length - 1, pair.live.length)} cy={y(liveLast)} r="3.2" fill="var(--ink)" />
        <text x={w - padR - 4} y={y(liveLast) + 12} textAnchor="end" fontFamily="var(--mono)" fontSize="10" fill="var(--ink)">live</text>

        {hasResearch && <>
          {/* divergence histogram strip */}
          <line x1={padL} x2={w - padR} y1={divBaseY} y2={divBaseY} stroke="var(--rule-2)" strokeWidth="1" vectorEffect="non-scaling-stroke" />
          {divergence.map((v, i) => {
            const bw = Math.max(1.5, (w - padL - padR) / divergence.length * 0.55);
            const dh = v / divMax * (divBandH * 0.45);
            return (
              <rect key={i}
              x={x(i, divergence.length) - bw / 2}
              y={dh >= 0 ? divBaseY - dh : divBaseY}
              width={bw} height={Math.max(0.6, Math.abs(dh))}
              fill="var(--muted)" opacity="0.55" />);
          })}
          <text x={padL - 8} y={divBaseY + 3} textAnchor="end" fontFamily="var(--mono)" fontSize="9" fill="var(--muted-2)" letterSpacing="0.08em">DIV</text>
        </>}

        {/* x axis date labels — driven by the actual ts axis when present
            so the labels match the window we sliced.  Fallback: span the
            selected rangeDays back from today. */}
        {Array.from({ length: 6 }, (_, i) => {
          const n = pair.live.length;
          const ix = Math.floor((n - 1) * (i / 5));
          let d;
          const ts = pair.ts || [];
          if (ts.length === n && ts[ix]) {
            d = new Date(ts[ix] / 1000);
          } else if (ts.length > 0) {
            // ts is shorter than live (zero-pad case) — interpolate
            const frac = i / 5;
            const tStart = ts[0];
            const tEnd = ts[ts.length - 1];
            d = new Date((tStart + (tEnd - tStart) * frac) / 1000);
          } else {
            d = new Date();
            d.setDate(d.getDate() - Math.round(rangeDays * (1 - i / 5)));
          }
          return (
            <text key={i} x={x(ix, n)} y={h - 8} textAnchor="middle" fontFamily="var(--mono)" fontSize="10" fill="var(--muted)">
              {d.toLocaleDateString("en-US", { month: "short", day: "numeric" })}
            </text>);

        })}
      </svg>
    </div>);

}

/* ── Divergence metrics row (research vs live) ──────────────── */
function DivergenceMetricsRow({ strats }) {
  if (!strats || strats.length === 0) return null;
  const benches = strats.map((s) => window.STRATEGY_DEFS.find((d) => d.id === s.defId)?.bench).filter(Boolean);
  if (benches.length === 0) return null;

  const totalTrades = strats.reduce((s, x) => s + x.trades, 0) || 1;
  const wL = strats.reduce((s, x) => s + x.wr * x.trades, 0) / totalTrades;
  const wR = benches.reduce((s, b) => s + b.wr, 0) / benches.length;
  const pL = strats.reduce((s, x) => s + x.pf * x.trades, 0) / totalTrades;
  const pR = benches.reduce((s, b) => s + b.pf, 0) / benches.length;
  const eL = strats.reduce((s, x) => s + x.eR * x.trades, 0) / totalTrades;
  const eR = benches.reduce((s, b) => s + b.eR, 0) / benches.length;
  const pnlLive = strats.reduce((s, x) => s + x.pnl30, 0);

  // research expected $: bench.eR × risk × approx_trades summed
  const pnlR = strats.reduce((s, x) => {
    const b = window.STRATEGY_DEFS.find((d) => d.id === x.defId)?.bench;
    return s + (b ? b.eR * x.riskPerTrade * Math.max(x.trades, 20) : 0);
  }, 0);

  // composite divergence score: live/research ratio across metrics
  const score = (wL / wR + pL / pR + (eR !== 0 ? eL / eR : 1)) / 3;

  const items = [
  { l: "Win rate", live: (wL * 100).toFixed(1) + "%", research: (wR * 100).toFixed(1) + "%",
    delta: (wL - wR) * 100, suffix: "pp", deltaFmt: (v) => (v >= 0 ? "+" : "") + v.toFixed(1) + "pp" },
  { l: "Profit factor", live: pL.toFixed(2), research: pR.toFixed(2),
    delta: pL - pR, deltaFmt: (v) => (v >= 0 ? "+" : "") + v.toFixed(2) },
  { l: "E[R]", live: window.fmtP(eL, 2), research: window.fmtP(eR, 2),
    delta: eL - eR, deltaFmt: (v) => (v >= 0 ? "+" : "") + v.toFixed(2) },
  // "Realized 30d" = Σ per-strategy realized P&L from attributed exit fills.
  // Deliberately distinct from the Overview "30d P&L" KPI, which is an
  // equity-curve diff (current AUM − AUM 30d ago); labelling both "30d P&L"
  // made the two legitimately-different numbers look like a bug.
  { l: "Realized 30d", live: window.fmt$(pnlLive, 0), research: window.fmt$(pnlR, 0),
    delta: pnlLive - pnlR, deltaFmt: (v) => (v >= 0 ? "+" : "−") + "$" + Math.abs(v).toFixed(0) },
  { l: "Divergence", live: score.toFixed(2) + "×", research: "1.00×",
    delta: score - 1, deltaFmt: (v) => (v >= 0 ? "+" : "") + (v * 100).toFixed(0) + "%",
    hint: score >= 1.05 ? "outperforming" : score <= 0.95 ? "under" : "on-track" }];


  return (
    <div className="div-metrics">
      {items.map((m, i) => {
        const deltaTxt = (m.delta>=0?"↑ ":"↓ ") + m.deltaFmt(Math.abs(m.delta)).replace(/^[+−]/, "");
        return (
          <div className="div-metric" key={i}>
            <span className="dm-l">{m.l}</span>
            <span className="dm-vals mono">
              <span className="dm-v tnum">{m.live}</span>
              <span className="dm-sep">/</span>
              <span className="dm-v-r tnum">{m.research}</span>
            </span>
            <span className={"dm-delta mono tnum "+(m.delta>=0?"pos":"neg")}>{deltaTxt}</span>
            {m.hint && <span className={"dm-hint "+(m.delta>=0?"pos":"neg")}>{m.hint}</span>}
          </div>
        );
      })}
    </div>);

}

/* ── Account z-score panel (overview-style) ─────────────────── */
/* Map a z-score to the {-2,-1,0,1,2,3} data-v palette used by .spc-cell
 * CSS in index.html.  Sign convention follows spc_monitor.py:
 *   - WR / PnL / TPD are lower-is-worse: negative z → red, positive → blue
 *   - DD is higher-is-worse: positive z → red, negative → blue
 * For DD we flip the sign before mapping so red still means "bad".
 */
function _spcCellDataV(z, track) {
  if (!Number.isFinite(z)) return 0;
  const v = (track === "dd") ? -z : z;
  if (v >= 3) return 3;
  if (v >= 2) return 2;
  if (v >= 1) return 1;
  if (v <= -2) return -2;
  if (v <= -1) return -1;
  return 0;
}

/* Compute the WR-z series for the last `window` trades, one z per
 * trade index. Standard proportion z-test:
 *   z = (p̂ - p₀) / sqrt(p₀(1-p₀)/n)
 * where p̂ is the rolling-window win rate and n is the window size
 * (or trades-so-far, whichever is smaller). */
function _rollingWrZ(trades, expectedWr, window) {
  const out = [];
  if (!Number.isFinite(expectedWr) || expectedWr <= 0 || expectedWr >= 1) return out;
  const wins = trades.map((t) => (t.pnl || 0) > 0 ? 1 : 0);
  for (let i = 0; i < trades.length; i++) {
    const start = Math.max(0, i - window + 1);
    const slice = wins.slice(start, i + 1);
    const n = slice.length;
    const wr = slice.reduce((a, b) => a + b, 0) / n;
    const se = Math.sqrt(expectedWr * (1 - expectedWr) / n);
    out.push(se > 0 ? (wr - expectedWr) / se : 0);
  }
  return out;
}

/* Avg-P&L per-trade z-series. Uses pooled std from the window itself
 * (Student-t-ish, no global σ from the artifact). Robust when expected
 * is mean of a heavy-tailed dist — falls back to expected magnitude
 * when σ is undefined or zero. */
function _rollingPnlZ(trades, expectedPnl, window) {
  const out = [];
  if (!Number.isFinite(expectedPnl)) return out;
  const pnls = trades.map((t) => Number(t.pnl) || 0);
  for (let i = 0; i < trades.length; i++) {
    const start = Math.max(0, i - window + 1);
    const slice = pnls.slice(start, i + 1);
    const n = slice.length;
    const mean = slice.reduce((a, b) => a + b, 0) / n;
    const variance = n > 1 ? slice.reduce((a, b) => a + (b - mean) ** 2, 0) / (n - 1) : Math.abs(expectedPnl);
    const se = Math.sqrt(variance / n);
    out.push(se > 0 ? (mean - expectedPnl) / se : 0);
  }
  return out;
}

/* Trades-per-day z-series. For each trade we compute observed TPD over
 * the window — # trades in window / # distinct days touched — and
 * z-test against expected_tpd with a Poisson-ish stderr (√λ/n). */
function _rollingTpdZ(trades, expectedTpd, window) {
  const out = [];
  if (!Number.isFinite(expectedTpd) || expectedTpd <= 0) return out;
  const dayOf = (t) => {
    if (!t.exitTime) return null;
    const d = new Date(t.exitTime);
    return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
  };
  for (let i = 0; i < trades.length; i++) {
    const start = Math.max(0, i - window + 1);
    const slice = trades.slice(start, i + 1);
    const days = new Set(slice.map(dayOf).filter(Boolean));
    if (days.size === 0) { out.push(0); continue; }
    const observed = slice.length / days.size;
    // SE ≈ sqrt(λ / nDays) — Poisson rate stderr from sample size.
    const se = Math.sqrt(expectedTpd / days.size);
    out.push(se > 0 ? (observed - expectedTpd) / se : 0);
  }
  return out;
}

/* Drawdown z-series. Tracks running peak/trough of cumulative P&L and
 * computes current dd at each trade index. z = (dd - expected_dd)/dd_std.
 * Positive z = drawdown larger than expected = bad (DD flip applies). */
function _rollingDdZ(trades, expectedDd, ddStd) {
  const out = [];
  if (!Number.isFinite(expectedDd) || !Number.isFinite(ddStd) || ddStd <= 0) return out;
  let cum = 0, peak = 0;
  for (const t of trades) {
    cum += Number(t.pnl) || 0;
    if (cum > peak) peak = cum;
    const dd = peak - cum;
    out.push((dd - expectedDd) / ddStd);
  }
  return out;
}

function _spcTrackRow({ label, track, zSeries, window }) {
  // Show up to `window` cells.  Pad with neutral (data-v=0) on the
  // left if we have fewer trades than the window — so the grid width
  // stays stable as trades accumulate.
  const cells = zSeries.slice(-window);
  const padCount = Math.max(0, window - cells.length);
  const padded = [...Array(padCount).fill(null), ...cells];
  const latest = cells.length ? cells[cells.length - 1] : null;
  const latestStr = latest == null ? "—" : (latest >= 0 ? "+" : "") + latest.toFixed(2);
  const latestCls = latest == null
    ? ""
    : (track === "dd" ? -latest : latest) <= -1 ? "neg" : (track === "dd" ? -latest : latest) >= 1 ? "warn" : "";
  return (
    <div key={track}>
      <div className="acct-spc-row-h mono">
        <span>{label}</span>
        <span className={"tnum " + latestCls}>z {latestStr}</span>
      </div>
      <div className="spc-grid" style={{ gridTemplateColumns: `repeat(${window},1fr)` }}>
        {padded.map((z, j) => (
          <div key={j}
               className="spc-cell"
               data-v={z == null ? 0 : _spcCellDataV(z, track)}
               title={z == null ? "no data" : `t-${cells.length - 1 - (j - padCount)}: z=${z >= 0 ? "+" : ""}${z.toFixed(2)}`} />
        ))}
      </div>
    </div>
  );
}

function AccountSpcPanel({ acctStrats }) {
  // Build one 4-track grid (WR / PnL / TPD / DD) per (account, strategy)
  // present on this account.  Z-scores are computed client-side from
  // window.TRADES filtered to each strategy + baselines shipped by the
  // agent at startup (window.SPC_BASELINES).
  const baselines = window.SPC_BASELINES || {};
  const trades = window.TRADES || [];
  const blocks = [];
  for (const s of (acctStrats || [])) {
    const key = `${s.acct}|${s.id ? s.id.split("-")[0] : ""}`;
    // STRATS.id is the deployment id ("orb-prac-abcd"); try the slug
    // prefix first, then fall back to s.defId, then s.id verbatim.
    const candidates = [
      `${s.acct}|${(s.defId || "").replace(/^sd-/, "")}`,
      `${s.acct}|${(s.id || "").split(/[-_]/)[0]}`,
      `${s.acct}|${s.id}`,
    ];
    const base = candidates.map(k => baselines[k]).find(Boolean);
    if (!base) continue;
    const stratSlug = base.strategy;
    // Filter window.TRADES to this (account, strategy).  Older fills
    // without per-fill strategy attribution fall through to the account's
    // primary strategy via window.STRATS rebuild — skip them here so the
    // z-series is built on attributed fills only.
    const myTrades = trades.filter((t) =>
      t.account === s.acct && t.strategy === stratSlug && t.isExit
    );
    if (myTrades.length === 0) continue;
    myTrades.sort((a, b) => (a.exitTime || "").localeCompare(b.exitTime || ""));
    const W = Math.max(8, Math.min(40, base.window || 20));
    const wrZ  = _rollingWrZ(myTrades, base.expected_wr, W);
    const pnlZ = _rollingPnlZ(myTrades, base.expected_pnl, W);
    const tpdZ = _rollingTpdZ(myTrades, base.expected_tpd, W);
    const ddZ  = _rollingDdZ(myTrades, base.expected_dd, base.dd_std);
    blocks.push(
      <div key={`${s.acct}-${stratSlug}`} style={{marginBottom:14}}>
        <div className="mono" style={{fontSize:11, color:"var(--muted)", letterSpacing:"0.06em", textTransform:"uppercase", marginBottom:4}}>
          {stratSlug} · {myTrades.length} closed trades
        </div>
        {_spcTrackRow({label:"Win rate",   track:"wr",  zSeries:wrZ,  window:W})}
        {_spcTrackRow({label:"$/trade",    track:"pnl", zSeries:pnlZ, window:W})}
        {_spcTrackRow({label:"Trades/day", track:"tpd", zSeries:tpdZ, window:W})}
        {_spcTrackRow({label:"Drawdown",   track:"dd",  zSeries:ddZ,  window:W})}
      </div>
    );
  }
  if (blocks.length === 0) {
    return (
      <div className="acct-spc-empty mono">
        No SPC baselines on file for this account's strategies.
      </div>
    );
  }
  return (
    <>
      <div className="acct-spc-list">{blocks}</div>
      <div className="spc-axis" style={{ marginTop: 8 }}>
        <span>oldest</span>
        <span>latest trade</span>
      </div>
    </>
  );
}

/* ── Configuration row (strategy detail) ────────────────────── */
function ConfigurationRow({ s, onOpen }) {
  const def = window.STRATEGY_DEFS.find((d) => d.id === s.defId);
  const acct = window.ACCOUNTS.find((a) => a.id === s.acct);
  if (!def || !acct) return null;
  // A deployment with zero attributed exits has wr/pf/eR=null
  // (data-live.jsx:1057-1083 — deliberate so KPIs read "—" instead of NaN).
  // Render dash for non-finite metrics and skip divergence coloring; pf can
  // also be Infinity (all-wins, no losses) which we render as "∞".
  const wrFinite = Number.isFinite(s.wr);
  const pfFinite = Number.isFinite(s.pf);
  const eRFinite = Number.isFinite(s.eR);
  const wrDiv = wrFinite ? (s.wr - def.bench.wr) * 100 : null;
  const pfDiv = pfFinite ? s.pf - def.bench.pf : null;
  const eRDiv = eRFinite ? s.eR - def.bench.eR : null;
  const diffCls = (d) => d == null ? "muted" : (d >= 0 ? "pos" : "neg");
  const pfTxt = pfFinite ? s.pf.toFixed(2) : (s.pf === Infinity ? "∞" : "—");
  // sparkData was previously seeded from the strategy name — deterministic
  // but fabricated.  Until a real per-strategy equity series is available
  // from the hub, render nothing rather than a curve the operator might
  // read as live P&L.
  return (
    <div className="cfg-row" data-state={s.state} onClick={onOpen}>
      <div className="cfg-led" />
      <div className="cfg-acct">
        <div className="cfg-acct-l">
          <span className={"badge " + acct.kind}>{acct.kind.toUpperCase()}</span>
          <span className="cfg-acct-name">{acct.label}</span>
        </div>
        <div className="cfg-acct-s mono">
          <span>{s.contracts}c</span>
          <span className="dotsep">·</span>
          <span>risk {window.fmt$(s.riskPerTrade, 0)}</span>
          <span className="dotsep">·</span>
          <span>since {s.since}</span>
        </div>
      </div>
      <div className="cfg-state">
        <window.StateBadge state={s.state} />
      </div>
      <div className="cfg-perf">
        <div className="cfg-perf-grid">
          <div className="cfg-perf-cell">
            <div className="cfg-perf-l">WR</div>
            <div className="cfg-perf-v mono"><span className={diffCls(wrDiv)}>{wrFinite ? (s.wr * 100).toFixed(1) + "%" : "—"}</span>
              <span className="cfg-perf-cmp mono"> / {(def.bench.wr * 100).toFixed(0)}</span></div>
          </div>
          <div className="cfg-perf-cell">
            <div className="cfg-perf-l">PF</div>
            <div className="cfg-perf-v mono"><span className={diffCls(pfDiv)}>{pfTxt}</span>
              <span className="cfg-perf-cmp mono"> / {def.bench.pf.toFixed(2)}</span></div>
          </div>
          <div className="cfg-perf-cell">
            <div className="cfg-perf-l">E[R]</div>
            <div className="cfg-perf-v mono"><span className={diffCls(eRDiv)}>{eRFinite ? window.fmtP(s.eR, 2) : "—"}</span>
              <span className="cfg-perf-cmp mono"> / {window.fmtP(def.bench.eR, 2)}</span></div>
          </div>
          <div className="cfg-perf-cell">
            <div className="cfg-perf-l">TRADES</div>
            <div className="cfg-perf-v mono tnum">{s.trades}</div>
          </div>
        </div>
      </div>
      <div className="cfg-pnl">
        <div className="cfg-pnl-l">30d P&L</div>
        <div className={"cfg-pnl-v mono tnum " + (s.pnl30 >= 0 ? "pos" : "neg")}>{window.fmt$(s.pnl30, 0)}</div>
        <div className="cfg-pnl-spark muted" style={{fontFamily:"var(--mono)",fontSize:11}}>
          {/* per-strategy 30d sparkline lands when the hub serves the series */}—
        </div>
      </div>
      <div className="cfg-cta mono">configure →</div>
    </div>);

}

/* ── Overview ─────────────────────────────────────────────── */
window.PageOverview = function PageOverview({ tweaks }) {
  // Keep useDataLoad mounted: it subscribes this page to live-data bumps
  // (useSyncExternalStore via window.useLive) so per-source loaded-flag
  // changes and WS frames repaint the Overview the instant they arrive —
  // no polling.  Per-block loading is driven by isLoaded(source) below.
  window.useDataLoad(1600);
  const accts = window.ACCOUNTS || [];
  const strats = window.STRATS || [];
  const insts = window.INSTANCES || [];
  const dash = "—";
  // The page re-renders on each live-data bump (a WS frame / loaded-flag
  // flip, coalesced to one render per animation frame), but the heavy
  // reducers below only need to run when their source arrays change
  // identity.  data-live.jsx replaces
  // ACCOUNTS/STRATS/INSTANCES wholesale on every WS frame / bootstrap,
  // so reference equality is a reliable cache key.  Without this memo,
  // a 50-strategy fleet was running 8 reduces + 1 filter + 1
  // Object.entries 4 times per second from render alone.
  //
  // AUM / day-P&L / 30d P&L only count xfa + funded accounts (real
  // profit).  Combine + practice are virtual; their P&L feeds strategy
  // evaluation but not the fleet KPIs.
  const {
    aum, dayPnl, running, trades, wrAvg, pfAvg,
    pnl30, pnl30Avail, has30Hist, acctsSub, liveInsts, fundedMode,
  } = usePgM(() => {
    const REAL_KINDS = { xfa: 1, funded: 1 };
    // Externally-mirrored accounts (read-only instances — see
    // window.EXTERNAL_INSTANCES) are excluded from the FLEET KPIs.  They are
    // real trades on real accounts and they belong on the Accounts /
    // Strategies pages, but this system doesn't place them: folding 21
    // mirrored 369979 accounts (~$2.15M equity, +$55k/30d) into AUM / day P&L
    // / win rate / profit factor would retroactively change what those
    // headline numbers measure — "how is aviaf doing" becomes "how is aviaf
    // plus somebody else's book doing".  Same reason `running` and `trades`
    // stay ours-only.
    const isExternal = (x) => !!(x && window.EXTERNAL_INSTANCES
      && window.EXTERNAL_INSTANCES.has(x.instance));
    const ownAccts = accts.filter(a => !isExternal(a));
    const ownStrats = strats.filter(s => !s.external);
    const realAccts = ownAccts.filter(a => REAL_KINDS[a.kind]);
    // When real-money (xfa/funded) accounts exist the headline tracks ONLY
    // those — combine/practice equity is virtual.  But when there are none
    // (the common early state), collapsing the headline to $0 hides the live
    // tracked equity (e.g. ~$447k across practice/combine) and makes the
    // dashboard look broken.  Fall back to all verified accounts and relabel
    // so $0 is never shown while real equity is live.  `fundedMode` drives
    // the KPI sub-labels below, and mobile's MHero already sums all accounts,
    // so this also makes the two surfaces agree.
    const fundedMode = realAccts.length > 0;
    const aumAccts = fundedMode ? realAccts : ownAccts;
    const aum = aumAccts.reduce((s, a) => a.equityVerified ? s + (a.equity || 0) : s, 0);
    const dayPnl = aumAccts.reduce((s, a) => a.equityVerified ? s + (a.dayPnl || 0) : s, 0);
    const running = ownStrats.filter((s) => s.state === "running" || s.state === "armed").length;
    const trades = ownStrats.reduce((s, x) => s + (x.trades || 0), 0);
    const wrTotal = ownStrats.reduce((s, x) => s + ((x.wr ?? 0) * (x.trades || x.n || 0)), 0);
    const nTotal  = ownStrats.reduce((s, x) => s + (x.trades || x.n || 0), 0);
    const wrAvg = nTotal > 0 ? wrTotal / nTotal : null;
    // Fleet profit factor = Σ grossWin / Σ grossLoss, via the shared selector
    // so desktop and mobile never diverge (mobile previously averaged
    // per-strategy PF ratios and read 0.86 vs the correct 1.23).
    const pfAvg = window.fleetProfitFactor(ownStrats);
    const aum30Real = aumAccts.reduce((s, a) => s + (Number(a.eq30) || 0), 0);
    const has30Hist = aumAccts.some(a => a.eq30HasHist);
    const pnl30Avail = aumAccts.some(a => a.equityVerified);
    const pnl30 = pnl30Avail ? (aum - aum30Real) : 0;
    if (realAccts.length && typeof window.__AVIAF_KPI_LOG === "undefined") {
      window.__AVIAF_KPI_LOG = true;
      console.log("[aviaf] Overview KPI realAccts:", realAccts.map(a => ({
        id: a.id, kind: a.kind, equity: a.equity, dayPnl: a.dayPnl,
        eq30: a.eq30, equityVerified: a.equityVerified, code: a.code,
      })));
    }
    // Sub-label must describe what the KPI actually counted (own accounts),
    // and name the mirrored ones separately so they aren't silently invisible.
    const nExternal = accts.length - ownAccts.length;
    const acctsSub = ownAccts.length
      ? `${ownAccts.length} account${ownAccts.length === 1 ? "" : "s"}` +
        Object.entries(ownAccts.reduce((m,a)=>{m[a.kind||"unknown"]=(m[a.kind||"unknown"]||0)+1;return m;},{}))
          .map(([k,n])=>` · ${n} ${k}`).join("") +
        (nExternal ? ` · +${nExternal} mirrored (excluded)` : "")
      : (nExternal
        ? `${nExternal} mirrored account${nExternal === 1 ? "" : "s"} only (excluded from KPIs)`
        : "no accounts");
    const liveInsts = insts.filter(i => i.state === "running").length;
    return { aum, dayPnl, running, trades, wrAvg, pfAvg, pnl30, pnl30Avail, has30Hist, acctsSub, liveInsts, fundedMode };
  }, [accts, strats, insts]);

  // Per-source loading.  Each KPI/block shimmers until its specific
  // /api/* has resolved (window.__LIVE.loaded.<source> === true).  A
  // source that resolved to an empty array shows the "confirmed empty"
  // state, not shimmer.  This replaces the global hasData gate, which
  // dissolved shimmer too eagerly into "$0 / no fills yet" while
  // downstream data was still arriving.
  const loadedFlags = window.__LIVE?.loaded || {};
  const isLoaded = (key) => loadedFlags[key] === true;

  const items = [
  { lbl: fundedMode ? "AUM" : "Tracked equity",
    loading: !isLoaded("equitySnapshot"),
    val: window.fmt$(aum, 0),
    sub: (fundedMode ? "xfa+funded only · " : "all accounts · ") + acctsSub },
  { lbl: "Day P&L",
    loading: !isLoaded("equitySnapshot"),
    // When no agent is connected, the day P&L tile reads from the
    // last-cached account.dayPnl snapshot — accurate as of the agent's
    // final equity push but increasingly stale.  Surface this clearly
    // ("stale · agent offline") instead of letting the operator read it
    // as a live number.
    val: window.fmt$(dayPnl, 2),
    sub: liveInsts === 0
      ? "stale · 0 agents online"
      : (aum > 0
          ? window.pct(dayPnl / aum, 2) + " session · " + (fundedMode ? "xfa+funded" : "all accounts")
          : "today · " + (fundedMode ? "xfa+funded only" : "all accounts")),
    cls: liveInsts === 0 ? "muted" : (dayPnl >= 0 ? "pos" : "neg") },
  { lbl: "30d P&L",
    loading: !isLoaded("equitySnapshot"),
    val: pnl30Avail ? window.fmt$(pnl30, 0) : dash,
    sub: !pnl30Avail ? "no equity history"
                     : (has30Hist ? "trailing 30d · " : "since inception · ")
                       + (fundedMode ? "xfa+funded only" : "all accounts"),
    cls: pnl30 >= 0 ? "pos" : "neg",
    spark: (window.PNL_TOTAL && window.PNL_TOTAL.length > 1) ? window.PNL_TOTAL : null },
  { lbl: "Live instances",
    loading: !isLoaded("accounts"),
    val: insts.length ? `${liveInsts}/${insts.length}` : "0",
    sub: insts.length ? "running / total" : "no agents connected" },
  { lbl: "Win rate · 30d",
    loading: !isLoaded("fills"),
    val: wrAvg != null ? (wrAvg * 100).toFixed(1) + "%" : dash,
    sub: trades ? `${trades} trades` : "no fills yet" },
  { lbl: "Profit factor",
    loading: !isLoaded("fills"),
    val: pfAvg == null
      ? dash
      : (Number.isFinite(pfAvg) ? pfAvg.toFixed(2) : "∞"),
    sub: trades ? "vol-adj across syms" : "no fills yet" }];


  return (
    <>
      <div className="kpis skel-stack">
        {items.map((k, i) =>
        <div className="kpi" key={i}>
            <div className="lbl">{k.lbl}</div>
            {k.loading ? (
              <>
                <window.Skel kind="line-lg" w="68%" style={{marginTop:2}} />
                <window.Skel kind="line-sm" w="54%" style={{marginTop:6}} />
              </>
            ) : (<>
              <div className={"val mono tnum " + (k.cls || "")}>{k.val}</div>
              {k.spark ?
            <span style={{ color: "var(--accent)" }}><window.Sparkline data={k.spark} fill /></span> :
            <div className="delta mono">{k.sub}</div>}
            </>)}
          </div>
        )}
      </div>

      <div className="main">
        <div className="col-left">
          <OverviewAccountsSection accts={accts} acctsLoading={!isLoaded("accounts")} />


          {(() => {
            const plotLoading = !isLoaded("equitySeries");
            return (<>
              <window.SectionHeader title="Performance" sub="portfolio" />
              {plotLoading
                ? <div style={{padding:"14px var(--pad) 22px"}}>
                    <div className="skel-stack" style={{display:"flex",gap:14,marginBottom:12}}>
                      <window.Skel kind="line" w={68} />
                      <window.Skel kind="line" w={68} />
                      <window.Skel kind="line" w={68} />
                    </div>
                    <window.Skel h={240} style={{display:"block",width:"100%"}} />
                  </div>
                : <window.EquityPlot showGrid={tweaks.showGrid} />}
            </>);
          })()}

          {(() => {
            // Strategies-live depends on both fills (for win-rate/PF
            // aggregation) and accounts (for the strategy roster).
            const stratsLoading = !isLoaded("fills") || !isLoaded("accounts");
            return (<>
              <window.SectionHeader title="Strategies · live" count={stratsLoading ? "—" : (window.STRATS || []).length} />
              {stratsLoading
                ? <StratMiniTableSkel />
                : <window.ErrorBoundary label="strategies table"><StratMiniTable /></window.ErrorBoundary>}
            </>);
          })()}
        </div>

        <div className="col-right">
          {!isLoaded("spc")
            ? <SpcPanelSkel />
            : <window.ErrorBoundary label="SPC panel"><SpcPanel /></window.ErrorBoundary>}
          {!isLoaded("alerts") ? null : <window.ErrorBoundary label="alert mini"><CriticalAlertPanel /></window.ErrorBoundary>}
          {!isLoaded("accounts") ? <HealthPanelSkel /> : <window.ErrorBoundary label="health panel"><HealthPanel /></window.ErrorBoundary>}
          {!isLoaded("feed") ? <FeedPanelSkel /> : <window.ErrorBoundary label="event feed"><FeedPanel /></window.ErrorBoundary>}
        </div>
      </div>
    </>);

};

/* Account-kind filter chips reused on Overview + PageAccounts.
 * - "All"      → no filter
 * - "Practice" → kind == "practice" (PRAC… prefix; paper accounts)
 * - "Combine"  → kind == "combine"  (KTC combines in evaluation)
 * - "Funded"   → kind == "funded"   (LFA/FT/PA funded accounts)
 * - "XFA"      → kind == "xfa"      (express funded)
 * Order matches how operators think about account lifecycle: paper →
 * combine → funded.  Hide chips that have zero accounts so the bar
 * doesn't show categories that aren't real for this fleet.
 */
function AccountKindFilter({ accts, value, onChange }) {
  const counts = { all: accts.length, practice: 0, combine: 0, funded: 0, xfa: 0 };
  for (const a of accts) {
    if (counts[a.kind] !== undefined) counts[a.kind] += 1;
  }
  const chips = [
    ["all",      "All",      counts.all],
    ["practice", "Practice", counts.practice],
    ["combine",  "Combine",  counts.combine],
    ["funded",   "Funded",   counts.funded],
    ["xfa",      "XFA",      counts.xfa],
  ];
  return (
    <>
      {chips
        .filter(([k, _, n]) => k === "all" || n > 0)
        .map(([k, label, n]) => (
          <button key={k}
            data-active={value === k ? "true" : "false"}
            onClick={() => onChange(k)}>
            {label}{n > 0 && k !== "all" ? ` ${n}` : ""}
          </button>
        ))}
    </>);
}
function filterAccts(accts, kind) {
  if (kind === "all" || !kind) return accts;
  return accts.filter(a => a.kind === kind);
}

/* Overview's "Accounts" section split out so the filter state is
 * local — the parent (PageOverview) re-renders every tick and shouldn't
 * own filter UI state.
 */
function OverviewAccountsSection({ accts, acctsLoading }) {
  const [kind, setKind] = usePg("all");
  const filtered = filterAccts(accts, kind);
  return (<>
    <window.SectionHeader title="Accounts" count={acctsLoading ? "—" : filtered.length}
      right={<AccountKindFilter accts={accts} value={kind} onChange={setKind} />} />
    <div className="accounts skel-stack">
      {acctsLoading
        ? [0,1,2].map(i => <AccountCardSkel key={i} />)
        : filtered.length === 0
          ? <div style={{padding:"24px var(--pad)",color:"var(--muted)",fontFamily:"var(--mono)",fontSize:12}}>
              {accts.length === 0 ? "No accounts registered." : `No accounts in the "${kind}" filter.`}
            </div>
          : filtered.map((a) =>
              <window.ErrorBoundary key={a.uid || a.id} label={`account ${a.label || a.id}`}>
                <AccountCard a={a} />
              </window.ErrorBoundary>)}
    </div>
  </>);
}

/* skeleton siblings to the real overview blocks */
function AccountCardSkel() {
  return (
    <div className="acct">
      <div className="acct-head">
        <div style={{display:"flex",flexDirection:"column",gap:6,minWidth:0}}>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <window.Skel w={42} h={16} />
            <window.Skel kind="line" w={180} />
          </div>
          <window.Skel kind="line-sm" w={220} style={{marginTop:2}} />
        </div>
        <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:6}}>
          <window.Skel kind="line-lg" w={110} />
          <window.Skel kind="line-sm" w={140} />
        </div>
      </div>
      <div className="mll">
        <div className="mll-row">
          <window.Skel kind="line-sm" w={80} />
          <window.Skel kind="line-sm" w={120} />
        </div>
        <window.Skel h={6} style={{display:"block",width:"100%"}} />
        <div className="mll-row">
          <window.Skel kind="line-sm" w={100} />
          <window.Skel kind="line-sm" w={100} />
        </div>
      </div>
      <div className="instances skel-stack">
        {[0,1,2].map(i => (
          <div className="inst" key={i}>
            <window.Skel w={8} h={8} />
            <window.Skel kind="line" w={90} />
            <window.Skel kind="line-sm" w="80%" />
            <window.Skel kind="line" w={64} />
          </div>
        ))}
      </div>
    </div>
  );
}

function StratMiniTableSkel() {
  return (
    <div className="table-wrap">
      <table className="strat">
        <thead><tr>
          <th>Strategy</th><th>Symbol</th><th className="num">PF</th><th className="num">WR</th>
          <th className="num">Day P&L</th><th className="num">30d P&L</th><th>Trend</th>
        </tr></thead>
        <tbody className="skel-stack">
          {Array.from({length: 6}).map((_, i) => (
            <tr key={i}>
              <td><window.Skel kind="line" w={160} /></td>
              <td><window.Skel kind="line" w={48} /></td>
              <td className="num"><window.Skel kind="line" w={42} style={{marginLeft:"auto"}} /></td>
              <td className="num"><window.Skel kind="line" w={42} style={{marginLeft:"auto"}} /></td>
              <td className="num"><window.Skel kind="line" w={70} style={{marginLeft:"auto"}} /></td>
              <td className="num"><window.Skel kind="line" w={72} style={{marginLeft:"auto"}} /></td>
              <td><window.Skel w={80} h={18} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SpcPanelSkel() {
  return (
    <div className="right-block">
      <h3>Process control · z-score</h3>
      <div className="spc-grid">
        {Array.from({length: 60}).map((_, i) => <window.Skel kind="square" key={i} />)}
      </div>
      <div className="spc-axis">
        <window.Skel kind="line-sm" w={36} />
        <window.Skel kind="line-sm" w={36} />
      </div>
    </div>
  );
}

function HealthPanelSkel() {
  return (
    <div className="right-block skel-stack">
      <h3>System health</h3>
      {Array.from({length: 6}).map((_, i) => (
        <div className="health-row" key={i}>
          <window.Skel kind="line" w={140} />
          <window.Skel kind="line" w={70} />
        </div>
      ))}
    </div>
  );
}

function FeedPanelSkel() {
  return (
    <div className="right-block skel-stack">
      <h3>Live feed</h3>
      {Array.from({length: 7}).map((_, i) => (
        <div className="feed-row" key={i}>
          <window.Skel kind="line-sm" w={48} />
          <window.Skel kind="line" w="86%" />
          <window.Skel kind="line-sm" w={40} />
        </div>
      ))}
    </div>
  );
}


function PassEstRow({ a }) {
  // Combine P(pass)/T(pass) row — agent pass_estimate emit (hourly MC from the
  // account's CURRENT state under full TopstepX rules: trailing-EOD MLL,
  // DLL session-lock, consistency 2x best-day; strategy/pass_prob.py).
  const pe = a.passEst;
  if (!pe) return null;
  const kind = (a.kind || "").toLowerCase();
  if (!kind.includes("combine")) return null;
  const fmtP = (x) => (typeof x === "number" ? (x >= 10 ? x.toFixed(0) : x.toFixed(1)) + "%" : "—");
  const fmtT = (x) => (typeof x === "number" ? Math.round(x) + "d" : "—");
  const ageMin = a.passEstLastUs ? Math.floor((Date.now() * 1000 - a.passEstLastUs) / 60_000_000) : null;
  const stale = ageMin != null && ageMin > 180;   // > 3 emits missed
  if (pe.locked || !pe.raw) {
    return (
      <div className="mll-row" style={{ marginTop: 6, opacity: stale ? 0.5 : 1 }}
        title={pe.note || "no modeled legs on roster"}>
        <span className="muted">P(pass)</span>
        <span className="tnum neg">{pe.locked ? "0% · LOCKED" : "n/a · " + (pe.note || "unmodeled")}</span>
      </div>
    );
  }
  const r = pe.raw, h = pe.haircut || {};
  const p30 = r.horizons && r.horizons["30d"];
  const h30 = h.horizons && h.horizons["30d"];
  const eta = (x) => {
    if (typeof x !== "number") return "—";
    const d = new Date(Date.now() + x * 86_400_000);
    return `${d.getMonth() + 1}/${d.getDate()}`;
  };
  const unmodeled = (pe.unmodeled_strategies || []).length
    ? ` · partial (unmodeled: ${pe.unmodeled_strategies.join(", ")})` : "";
  const tip =
    `P(pass) from current state (bal ${window.fmt$((pe.state || {}).balance || 0, 0)}, ` +
    `best-day ${window.fmt$((pe.state || {}).best_day || 0, 0)}, ${pe.dll ? "DLL" : "no DLL"})\n` +
    `raw:     30d ${fmtP(p30)} · eventual ${fmtP(r.p_pass_eventual)} · MLL ${fmtP(r.p_mll_eventual)}\n` +
    `haircut: 30d ${fmtP(h30)} · eventual ${fmtP(h.p_pass_eventual)}  ` +
    `${(pe.method || "").startsWith("racer") ? "(p-2pp fill-stress)" : "(x0.65 research->live)"}\n` +
    `${typeof pe.v_exact === "number" ? "exact-DP V " + pe.v_exact.toFixed(1) + "% (MC diff " + (pe.mc_vs_exact_diff ?? "—") + "pp)\n" : ""}` +
    `T(pass) p50 ${fmtT(r.t_pass_p50)} (~${eta(r.t_pass_p50)}) · p90 ${fmtT(r.t_pass_p90)} (~${eta(r.t_pass_p90)})\n` +
    `method ${pe.method} legs ${pe.legs_version}${unmodeled}`;
  return (
    <div className="mll-row" style={{ marginTop: 6, opacity: stale ? 0.5 : 1 }} title={tip}>
      <span>
        <span className="muted">P(pass)</span>{" "}
        <span className={"tnum " + ((p30 || 0) >= 30 ? "pos" : "")}>{fmtP(p30)}</span>
        <span className="muted"> 30d · </span>
        <span className="tnum">{fmtP(r.p_pass_eventual)}</span>
        <span className="muted"> evt{unmodeled ? " · partial" : ""}{stale ? " · stale" : ""}</span>
        {pe.race_open && <span className="tag warn" style={{ marginLeft: 6 }}
          title="a race is open — estimate reflects the pre-entry state; updates ≤60s after the exit">racing…</span>}
      </span>
      <span>
        {/* "T ~11d · ≤21d (90%)" — median without jargon; formal p50/p90 + ETA
            dates live in the tooltip (operator-picked format 2026-07-16). */}
        <span className="muted">T</span>{" "}
        <span className="tnum">{typeof r.t_pass_p50 === "number" ? "~" + fmtT(r.t_pass_p50) : "—"}</span>
        <span className="muted"> · </span>
        <span className="tnum">{typeof r.t_pass_p90 === "number" ? "≤" + fmtT(r.t_pass_p90) : "—"}</span>
        <span className="muted"> (90%)</span>
      </span>
    </div>
  );
}

function AccountCard({ a }) {
  // Defensive: real accounts may not set every field the design expects.
  // Compute mll bar with safe fallbacks; degrade to a flat bar when
  // sessionStart/target/mllFloor are missing rather than NaN-poisoning
  // the geometry.
  const equity   = Number(a.equity) || 0;
  const mllFloor = typeof window.resolvedMllFloor === "function"
    ? window.resolvedMllFloor(a) : (Number(a.mllFloor) || 0);
  const sessStart = Number(a.sessionStart) || equity || (a.initial_capital || 0);
  // Profit target resolution (priority order):
  //   1. a.target            — pushed by the agent's goal_state emit
  //                            (config.profit_target).  Authoritative.
  //   2. ACCOUNT_KIND_RULES  — static tier rule (combine ≥$150K → $9,000)
  //                            for accounts whose agent emit hasn't landed
  //                            yet.  Without this, a fresh-bootstrap combine
  //                            renders as "no target — survival" even though
  //                            the broker definitively has a target.
  let target = Number(a.target) || 0;
  if (!target && typeof window.ACCOUNT_KIND_RULES === "function") {
    const r = window.ACCOUNT_KIND_RULES(a.kind, a.initial_capital);
    if (r && r.target_dollars > 0) target = r.target_dollars;
  }
  // Consistency-adjusted effective target.  TopstepX combine rule:
  // best_day ≤ consistency_pct × cumulative_at_pass — rearranged:
  //   effective = max(profit_target, best_day / consistency_pct)
  // Once best_day breaks past 50% × target, the broker's real finish-line
  // moves out and the static $9,000 chip lies to the operator.  Source of
  // truth is the agent-computed value (a.effectiveTarget); compute locally
  // as a fallback so the bump shows up even on older agents that don't
  // ship effective_profit_target yet.
  const consistency = Number(a.consistencyPct) || 0;
  const bestDay = Math.max(
    Number(a.bestDayPnl) || 0,
    Number(a.todayGoalPnl) || 0,
  );
  let effectiveTarget = Number(a.effectiveTarget) || 0;
  if (!effectiveTarget && target > 0 && consistency > 0 && bestDay > 0) {
    effectiveTarget = Math.max(target, bestDay / consistency);
  }
  if (!effectiveTarget) effectiveTarget = target;
  const targetIsBumped = effectiveTarget > target + 0.5;
  // Bar geometry: use the EFFECTIVE target as the right edge so the fill
  // reflects actual closeness-to-pass — not the static config target which
  // becomes a phantom finish line once the consistency rule kicks in.
  const rangeLo = mllFloor || sessStart * 0.95;
  const rangeHi = effectiveTarget > 0 ? sessStart + effectiveTarget : sessStart * 1.05;
  const denom = Math.max(1, rangeHi - rangeLo);
  const pos = (equity - rangeLo) / denom;
  const targetPos = target > 0 ? (sessStart + target - rangeLo) / denom : 1;
  const effectivePos = effectiveTarget > 0 ? (sessStart + effectiveTarget - rangeLo) / denom : 1;
  const startPos = (sessStart - rangeLo) / denom;
  const kind = a.kind || "unknown";
  // Status panel = LIVE strategies only.  Stopped variants stay in
  // STRATS for the dedicated strategy pages / history view, but
  // they're noise here: they have no live status text so they render
  // as duplicate-looking "—" rows alongside the live ones (e.g. an
  // old mr_v4 with no agent status next to the live mr_v2).
  const allStrats = (window.STRATS || []).filter((s) => s.acct === a.id && s.instance === a.instance);
  const strats = allStrats.filter((s) => s.state !== "stopped");
  const stoppedCount = allStrats.length - strats.length;
  // Liveness of the owning agent.  When this account's instance is
  // disconnected from the hub, every number below (balance, day P&L, MLL,
  // target, headroom, the stopped count) is the LAST value the agent pushed
  // before it dropped — not live.  Surface that instead of letting the
  // operator read a stale balance as current (the "prac shows live values
  // while its agent is offline" report).  "Quiet" = instance connected but
  // no equity emit for ≥5 min (agent wedged / feed silent).
  const _inst = (window.INSTANCES || []).find((i) => i.id === a.instance);
  const _ageUs = Number(a.strategiesLastUs) ? (Date.now() * 1000 - Number(a.strategiesLastUs)) : null;
  const ageMin = _ageUs != null ? Math.floor(_ageUs / 60_000_000) : null;
  const ageTxt = ageMin == null ? null : (ageMin >= 60 ? Math.round(ageMin / 60) + "h" : ageMin + "m");
  // Liveness is the AGENT HEARTBEAT, not the strategy-status cadence.
  // inst.lastSeenMs bumps on every frame from this instance — including the
  // 60s equity heartbeat — so a live agent whose strategy-status emit is slow
  // (or whose only fresh signal on first load is the bootstrap's last-persisted
  // status row, minutes old) still reads as alive.  Gating staleness on the
  // heartbeat kills the false "NO UPDATE · agent quiet" flash on load: while
  // the heartbeat is fresh we trust the numbers (equity still shimmers via
  // equityVerified until the first tick lands), and warn only once the
  // heartbeat itself has gone silent AND the strategy data is stale.  The
  // roster state flag lags reconnects, so it only picks the wording.
  const _seenAgeMs = _inst && _inst.lastSeenMs ? (Date.now() - _inst.lastSeenMs) : null;
  const agentAlive = _seenAgeMs != null && _seenAgeMs < 300_000;   // heartbeat < 5m
  const isStale = !agentAlive && ageMin != null && ageMin >= 5;
  const instOffline = isStale && (_inst ? _inst.state !== "running" : true);
  // Remote-pause: agent halts new entries but keeps strategies attached, so
  // the periodic emit flags them paused (a.strategyPaused). Surface a
  // persistent banner — pausing otherwise looked like nothing happened.
  const pausedCount = a.strategyPaused ? Object.keys(a.strategyPaused).length : 0;
  const preStaged = window.acctPreStaged && window.acctPreStaged(a);
  return (
    <div className="acct">
      {preStaged && (
        <div
          title="This account was added to the hub but not yet activated on the agent. The agent attaches an account only when its first strategy is added — until then there's no live data. Add a strategy from this page (or a strategies page) to activate it."
          style={{
            display: "flex", alignItems: "center", gap: 6,
            fontSize: 11, letterSpacing: "0.04em", fontWeight: 600,
            padding: "3px 7px", marginBottom: 8, borderRadius: 3,
            border: "1px solid var(--warn)", color: "var(--warn)",
            background: "rgba(255,165,0,0.07)",
          }}>
          ◷ PRE-STAGED · not on the agent yet — add a strategy to activate
        </div>
      )}
      {a.handsOff && (
        <div
          title="HANDS-OFF: you have manual control. The system will NOT flatten positions you open, and the protective drawdown lock is suppressed. TopStep's real MLL still applies."
          style={{
            display: "flex", alignItems: "center", gap: 6,
            fontSize: 11, letterSpacing: "0.04em", fontWeight: 700,
            padding: "3px 7px", marginBottom: 8, borderRadius: 3,
            border: "1px solid var(--accent)", color: "var(--accent)",
            background: "var(--accent-soft)",
          }}>
          ✋ HANDS-OFF · manual control · auto-flatten suppressed
        </div>
      )}
      {a.dllDayLock && (
        <div
          title="DLL day-lock: this account hit the broker's Daily Loss Limit (−$3,000 on DLL-variant tickets) and TopStep locked the SESSION. This is NOT an account failure and NOT a permanent lock — the MLL floor was not breached. Entries resume automatically at the next trading day."
          style={{
            display: "flex", alignItems: "center", gap: 6,
            fontSize: 11, letterSpacing: "0.04em", fontWeight: 700,
            padding: "3px 7px", marginBottom: 8, borderRadius: 3,
            border: "1px solid var(--warn)", color: "var(--warn)",
            background: "rgba(255,165,0,0.10)",
          }}>
          ◉ DLL LOCK (day) · broker daily-loss session lock · resumes next session
        </div>
      )}
      {pausedCount > 0 && !a.handsOff && (
        <div
          title="Trading is paused on this account — new entries are halted for the paused strategies. Open positions are left as-is. Resume from the account page."
          style={{
            display: "flex", alignItems: "center", gap: 6,
            fontSize: 11, letterSpacing: "0.04em", fontWeight: 600,
            padding: "2px 7px", marginBottom: 8, borderRadius: 3,
            border: "1px solid var(--warn)", color: "var(--warn)",
            background: "rgba(255,165,0,0.07)",
          }}>
          ⏸ PAUSED · new entries halted{pausedCount > 1 ? ` (${pausedCount} strategies)` : ""}
        </div>
      )}
      {isStale && (
        <div
          title={instOffline
            ? "This account's agent is disconnected from the hub — the values below are the last the agent sent before it dropped, not live."
            : "Agent connected but no equity update for a while — values below may be stale."}
          style={{
            display: "flex", alignItems: "center", gap: 6,
            fontSize: 11, letterSpacing: "0.04em", fontWeight: 600,
            padding: "2px 7px", marginBottom: 8, borderRadius: 3,
            border: `1px solid ${instOffline ? "var(--neg)" : "var(--warn)"}`,
            color: instOffline ? "var(--neg)" : "var(--warn)",
            background: instOffline ? "rgba(229,72,77,0.08)" : "rgba(255,165,0,0.07)",
          }}>
          {instOffline
            ? `AGENT OFFLINE${ageTxt ? ` · values as of ${ageTxt} ago` : " · values not live"}`
            : `NO UPDATE ${ageTxt} · agent quiet`}
        </div>
      )}
      <div className="acct-head">
        <div>
          <div className="acct-id">
            <span className={"badge " + kind}>{kind.toUpperCase()}</span>
            <a className="acct-name" href={`#/accounts/${a.id}`} style={{ color: "inherit", textDecoration: "none" }}>{a.label}</a>
          </div>
          <div className="acct-sub" style={{ marginTop: 4 }}>{a.broker} · {a.code} · acct/{a.id}</div>
        </div>
        <div className="equity" style={isStale ? { opacity: 0.5 } : undefined}>
          {a.equityVerified ? (
            <>
              <div className="num tnum">{window.fmt$(equity, 2)}</div>
              <div className={"pnl tnum " + ((a.dayPnl || 0) >= 0 ? "pos" : "neg")}>
                {window.fmt$(a.dayPnl || 0, 2)} day · {window.fmt$(a.totalPnl || 0, 0)} ses
              </div>
            </>
          ) : preStaged ? (
            <>
              {/* Pre-staged: no agent, no equity ever. Show a static dash
                * instead of an infinite shimmer (the reported "keeps loading"). */}
              <div className="num tnum muted">—</div>
              <div className="pnl tnum muted" style={{ fontSize: 11 }}>no data · inactive</div>
            </>
          ) : (
            <>
              {/* Fresh-registered account: agent hasn't emitted equity yet.
                * Shimmer rather than render the $0 anchor fallback, which
                * read as "this account has zero balance" instead of "we're
                * still waiting for the first agent push". */}
              <window.Skel kind="line-lg" w={120} />
              <window.Skel kind="line-sm" w={150} style={{marginTop:4}} />
            </>
          )}
        </div>
      </div>
      <div className="mll" style={isStale ? { opacity: 0.5 } : undefined}>
        {a.equityVerified ? (
          <>
            <div className="mll-row">
              <span>MLL {window.fmt$(mllFloor, 0)}</span>
              <span title={targetIsBumped
                ? `static target ${window.fmt$(target, 0)}; best day ${window.fmt$(bestDay, 0)} × (1/consistency_pct ${consistency.toFixed(2)}) = effective ${window.fmt$(effectiveTarget, 0)} required to pass`
                : undefined}>
                {effectiveTarget > 0
                  ? (targetIsBumped
                      ? `target ${window.fmt$(sessStart + effectiveTarget, 0)} (consistency: ${window.fmt$(target, 0)} → ${window.fmt$(effectiveTarget, 0)})`
                      : `target ${window.fmt$(sessStart + effectiveTarget, 0)}`)
                  : a.kind === "xfa" ? <span style={{color:"var(--pos)"}}>payout</span>
                  : "no target — survival"}
              </span>
            </div>
            <div className="mll-bar">
              <div className="fill" style={{ width: (Math.max(0, Math.min(1, pos)) * 100).toFixed(2) + "%" }} />
              <div className="floor" style={{ left: "0%" }} />
              <div className="target" style={{ left: (Math.max(0, Math.min(1, startPos)) * 100).toFixed(2) + "%", background: "var(--muted)" }} />
              {/* Static config target — dimmed when the consistency rule has
                 bumped the actual finish line out, so the operator sees BOTH
                 the original goal and where it's been pushed to. */}
              {target > 0 && targetIsBumped && (
                <div className="target" style={{
                  left: (Math.max(0, Math.min(1, targetPos)) * 100).toFixed(2) + "%",
                  background: "var(--muted)", opacity: 0.5,
                }} title={`static target ${window.fmt$(target, 0)}`} />
              )}
              {effectiveTarget > 0 && (
                <div className="target" style={{
                  left: (Math.max(0, Math.min(1, effectivePos)) * 100).toFixed(2) + "%",
                }} title={targetIsBumped
                  ? `effective target (consistency-adjusted) ${window.fmt$(effectiveTarget, 0)}`
                  : `target ${window.fmt$(effectiveTarget, 0)}`} />
              )}
            </div>
            <div className="mll-row">
              <span>headroom {window.fmt$(equity - mllFloor, 0)}</span>
              <span>{effectiveTarget > 0
                ? `to target ${window.fmt$(sessStart + effectiveTarget - equity, 0)}`
                : a.kind === "xfa" && equity > 0 && typeof window.xfaPayoutMath === "function"
                  ? (() => { const pm = window.xfaPayoutMath(a); return pm ? `payout ${window.fmt$(pm.withdrawable, 0)} (50% bal, 90/10 split)` : ""; })()
                  : ""}</span>
            </div>
          </>
        ) : preStaged ? null : (
          /* Until the first agent equity tick lands we don't have mllFloor
           * or a meaningful equity to compute the bar geometry — render the
           * AccountCardSkel's MLL skeleton so the row reads "loading"
           * instead of "MLL $0, headroom $0", which looked like a breach. */
          <>
            <div className="mll-row">
              <window.Skel kind="line-sm" w={80} />
              <window.Skel kind="line-sm" w={120} />
            </div>
            <window.Skel h={6} style={{display:"block",width:"100%"}} />
            <div className="mll-row">
              <window.Skel kind="line-sm" w={100} />
              <window.Skel kind="line-sm" w={100} />
            </div>
          </>
        )}
      <PassEstRow a={a} />
      </div>
      <div className="instances">
        {/* Cold-start skeleton (2026-07-17): the strategy ROWS derive from the
            agent's ~60s strategy-status emits, so for up to a minute after
            login the list is empty even though the bootstrap already knows
            the roster (a.strategies from the accounts table). Render named
            shimmer rows from the roster until the live rows arrive — the
            ticket is never blank and the loading state is explicit. */}
        {strats.length === 0 && Array.isArray(a.strategies) && a.strategies.length > 0 &&
          a.strategies.map((name) => (
            <div className="inst" data-state="stopped" key={"skel-" + name}
              style={{ opacity: 0.8 }}>
              <div className="ind" />
              <div className="name">
                {(typeof window.prettyStrategy === "function") ? window.prettyStrategy(name) : name}
              </div>
              <div className="meta">
                <window.Skel kind="line-sm" w={120} />
              </div>
            </div>
          ))}
        {strats.map((i) => {
          // Match the design's multi-span meta layout (the CSS styles
          // spans with separators) — but hide placeholder fields
          // ("—" symbol, undefined since) that would render as the
          // "— · 1c · since —" garbage when rebuildStratsFromAccounts
          // can't derive real per-strategy metadata.
          // lastSig (per-strategy short status) is sourced first from
          // a.strategyStatus[defId] (the agent's "flat tokyo OR=91 …"
          // text), falling back to i.lastSig.
          const lastSig = (a.strategyStatus && a.strategyStatus[i.defId]) || i.lastSig;
          // Staleness: the lastSig text is computed once per agent equity
          // emit (every ~60s normally).  If a.strategiesLastUs is older
          // than 5 min, the agent has stopped heartbeating — the rendered
          // "next: london 2h5m" / "held=170b" values are frozen from when
          // the emit was generated, NOT what's true right now.  Surface
          // this so the operator doesn't trust stale phantom-shaped data.
          const _stratsLastUs = a.strategyStatus ? a.strategiesLastUs : null;
          const stratStaleMin = _stratsLastUs
            ? Math.floor((Date.now() * 1000 - _stratsLastUs) / 60_000_000)
            : null;
          const stratIsStale = stratStaleMin != null && stratStaleMin >= 5;
          const showSymbol = i.symbol && i.symbol !== "—";
          const showSince = i.since && i.since !== "—";
          // LED state:
          //   green pulse  → strategy has an OPEN POSITION right now
          //   steady green → strategy is running and flat
          //   grey         → strategy is disabled / stopped (agent
          //                  isn't running it)
          //   red          → agent-reported error or account breached
          //
          // hasOpenPos / state / unrealized are derived once in
          // data-live.jsx (rebuildStratsFromAccounts) from the agent's
          // strategy_id-tagged positions[].  Single source of truth —
          // no regex-on-status fallback here.  Older STRATS rows that
          // lacked these fields are gone after the 2026-05-20 rebuild.
          const stratStatus = lastSig || "";
          const stratHasErr = /\b(error|err|fault|failed)\b/i.test(stratStatus);
          // Grace / cooldown / entry-lockout detection — agent emits
          // tokens like "grace 240s/300s" or "blocked" or "cooldown
          // 5m" when the strategy is alive but temporarily forbidden
          // from entering (post-SL pause, consecutive-loss freeze,
          // FOMC blackout window, broker reconcile, etc.).  Without
          // surfacing this, the operator sees a normal "flat" status
          // and assumes the strategy is just waiting on signal — when
          // it actually can't act on one even if it fires.
          //
          // Capture group 1 = the wait duration token if present
          // (rendered next to the BLOCK chip so the operator knows
          // how long is left).
          const graceMatch = stratStatus.match(/\b(?:grace|blocked|cooldown|lockout|entry[_\-]?block)\s*([0-9]+\s*[smhd])?(?:\/[0-9]+\s*[smhd])?/i);
          const isGrace = !!graceMatch;
          const stratHasOpenPos = !!i.hasOpenPos;
          const ledState =
            i.state === "stopped"   ? "stopped"
            : i.state === "breached" ? "breached"
            : stratHasErr            ? "error"
            : stratHasOpenPos        ? "running"
            : isGrace                ? "blocked"
            :                          "waiting";
          // Display P&L = realized-today + per-strategy unrealized.
          // Read the unrealized STRAIGHT from this account's positions
          // (tagged by strategy_id) rather than the rebuilt STRATS field:
          // STRATS.unrealized can lag a.positions (observed frozen at 0 while
          // a.positions ticked 225→230 live), freezing the per-strategy P&L.
          // a.positions updates on every mark-to-market emit, so this ticks.
          // Falls back to the STRATS value when no tagged position is present.
          const stratUnreal = (() => {
            const live = (a.positions || []).filter(
              (p) => p && p.strategy_id === i.defId);
            return live.length
              ? live.reduce((s, p) => s + (Number(p.unrealized) || 0), 0)
              : (Number(i.unrealized) || 0);
          })();
          const stratLivePnl = (Number(i.today) || 0) + stratUnreal;
          return (
            <div className="inst" data-state={ledState} key={i.id}>
              <div className="ind" />
              <div className="name" title={i.name}>
                {(typeof window.prettyStrategy === "function") ? window.prettyStrategy(i.name) : i.name}
              </div>
              <div className="meta">
                {showSymbol && <span>{i.symbol}</span>}
                {i.contracts ? <span>{i.contracts}c</span> : null}
                {showSince && <span>since {i.since}</span>}
                {isGrace && (
                  <span
                    title={`agent reports entry-block (${graceMatch[0]}). Strategy will not enter even on a valid signal until the block clears.`}
                    style={{
                      fontFamily: "var(--mono)",
                      fontSize: 10,
                      letterSpacing: "0.06em",
                      padding: "1px 5px",
                      border: "1px solid var(--warn)",
                      color: "var(--warn)",
                      background: "rgba(255, 165, 0, 0.06)",
                    }}>
                    [BLOCK]{graceMatch[1] ? " " + graceMatch[1] : ""}
                  </span>
                )}
                {lastSig && (() => {
                  // Strip the agent-embedded "P&L=±$N" token (mr emits
                  // "short entry=… SL=… TP=… P&L=-$175" etc.).  The same
                  // number is already rendered as pnl-mini on the right —
                  // showing it twice was noisy and went stale faster than
                  // the live mark-to-market in pnl-mini.
                  //
                  // Also inject the LIVE contract count after the
                  // side@price ("short@30000 1c") — agent's status text
                  // doesn't include size; openContracts is derived
                  // (data-live.jsx) from positions tagged with this
                  // strategy_id.  Falls back to bare text when flat.
                  const liveCt = Number(i.openContracts) || 0;
                  // Hide the contract chip entirely when the position
                  // data the count came from is stale (>5 min).  Without
                  // this we hallucinate "25c MNQ short" minutes after
                  // the broker flattened — the hub's positions_latest
                  // table is whatever the agent last emitted, not what
                  // the broker holds right now.
                  const posAgeS = i.posLastUs
                    ? Math.floor((Date.now() * 1000 - i.posLastUs) / 1_000_000)
                    : null;
                  const posStale = posAgeS != null && posAgeS > 300;
                  const posSym = (i.posSymbol || "").trim();
                  // Round any price-like number ≥ 100 to integer for
                  // compactness ("30205.00" → "30205").  Threshold ≥ 100
                  // avoids stomping tick-sized fields ("0.25", "1.5")
                  // that would lose meaning when rounded.  Negative
                  // numbers and signed offsets are preserved.
                  const compactPrices = (s) =>
                    s.replace(/-?\d+\.\d+/g, (m) => {
                      const n = Number(m);
                      return Math.abs(n) >= 100 ? String(Math.round(n)) : m;
                    });
                  let sigTxt = compactPrices(
                    lastSig
                      .replace(/\s*\bP&L=[+\-−]?\$?-?\d[\d.,]*/g, "")
                      .replace(/\b(short|long)\s+entry=/gi, "$1@")
                      // Strip the agent's own size token ("long x1@…"): the
                      // dashboard injects the authoritative LIVE count below
                      // ("Nc SYM"), so leaving the agent's "xN" showed the
                      // contract count twice (operator saw "1c MNQ … x1").
                      .replace(/\b(short|long)\s+x\d+(?=@)/gi, "$1")
                  ).trim();
                  if (liveCt > 0 && !posStale) {
                    // Insert "Nc SYM" right after "side@price" so a
                    // symbol-mismatched phantom (e.g. mr text says
                    // short@4500 but hub has "25c MNQ") is obvious.
                    // When the side@price pattern isn't present, prepend.
                    const ctTxt = posSym ? `${liveCt}c ${posSym}` : `${liveCt}c`;
                    const m = sigTxt.match(/^(short|long)@\S+/i);
                    if (m) {
                      sigTxt = m[0] + ` ${ctTxt}` + sigTxt.slice(m[0].length);
                    } else {
                      sigTxt = `${ctTxt} · ${sigTxt}`;
                    }
                  } else if (liveCt > 0 && posStale) {
                    sigTxt = sigTxt + ` · ${posAgeS > 3600 ? Math.round(posAgeS/3600)+"h" : Math.round(posAgeS/60)+"m"} old`;
                  }
                  if (!sigTxt) return null;
                  return (
                    <span
                      className="muted"
                      title={stratIsStale
                        ? `agent last reported ${stratStaleMin}m ago — values likely stale`
                        : undefined}
                      style={stratIsStale ? { opacity: 0.55, textDecoration: "line-through" } : undefined}
                    >
                      {sigTxt}
                      {stratIsStale ? ` · stale ${stratStaleMin}m` : ""}
                    </span>
                  );
                })()}
                {/* No status emit at all — render a placeholder so the
                    operator can tell "agent hasn't shipped per-strategy
                    status yet" from "strategy is silently stuck".  Common
                    for newly-added variants (e.g. spread_mr_gc_cl) whose
                    agent module doesn't have a status-emit hook yet. */}
                {!lastSig && i.state !== "stopped" && (
                  <span
                    className="muted"
                    title="agent hasn't emitted strategyStatus for this variant yet — add a status-emit hook in the agent's strategy module to populate this line"
                    style={{ opacity: 0.6, fontStyle: "italic" }}>
                    no status emit
                  </span>
                )}
              </div>
              <div className={"pnl-mini tnum " + (stratLivePnl > 0 ? "pos" : stratLivePnl < 0 ? "neg" : "muted")}
                   title={stratHasOpenPos && stratUnreal !== 0
                     ? `realized today ${window.fmt$(i.today || 0, 2)} + open ${window.fmt$(stratUnreal, 2)}`
                     : undefined}>
                {stratLivePnl === 0 ? "—" : window.fmt$(stratLivePnl, 2)}
              </div>
            </div>);
        })}
        {stoppedCount > 0 && (
          <a href={`#/accounts/${a.id}`} style={{
            display: "block",
            padding: "6px var(--pad)",
            fontFamily: "var(--mono)",
            fontSize: 11,
            color: "var(--muted)",
            textDecoration: "none",
            borderTop: "1px dashed var(--rule)",
          }} title="Open account detail to inspect stopped/historical variants">
            +{stoppedCount} stopped · history →
          </a>
        )}
      </div>
    </div>);

}

function StratMiniTable() {
  const dash = "—";
  const rows = [...(window.STRATS || [])].sort((a, b) => (b.pnl30 || 0) - (a.pnl30 || 0));
  if (rows.length === 0) {
    return (
      <div style={{padding:"24px var(--pad)",color:"var(--muted)",fontFamily:"var(--mono)",fontSize:12}}>
        No strategy attribution yet — accounts need a <code>strategy</code> in their HELLO payload.
      </div>);
  }
  return (
    <div className="table-wrap">
      <table className="strat">
        <thead><tr>
          <th>Strategy</th><th>Sym</th><th>Acct</th><th>State</th>
          <th className="num">WR</th><th className="num">PF</th>
          <th className="num">Today</th><th className="num">30d P&L</th><th>30d</th>
        </tr></thead>
        <tbody>
          {rows.map((r, i) => {
            const wrTxt = (r.wr != null) ? (r.wr * 100).toFixed(1) + "%" : dash;
            const pfTxt = (r.pf != null && Number.isFinite(r.pf)) ? r.pf.toFixed(2) : dash;
            // Daily P&L = realized-today (closed exits, CT-day bucketed) + the
            // live open-position unrealized, so it ticks with the mark while a
            // position is open (matching the account-card pnl-mini) instead of
            // freezing between closed trades.
            const todayUnreal = Number(r.unrealized) || 0;
            const today = (r.today || 0) + todayUnreal;
            const pnl30 = r.pnl30 || 0;
            return (
              <tr key={r.id || i} className="row-link" onClick={() => {if (r.defId) location.hash = `#/strategies/${r.defId}`;}}>
                <td>{r.name || dash}</td>
                <td className="muted">{r.symbol || dash}</td>
                <td className="muted">{r.acct || dash}</td>
                <td><window.StateBadge state={r.state || "stopped"} /></td>
                <td className="num tnum">{wrTxt}</td>
                <td className="num tnum">{pfTxt}</td>
                <td className={"num tnum " + (today > 0 ? "pos" : today < 0 ? "neg" : "muted")}
                    title={todayUnreal !== 0 ? `realized today ${window.fmt$(r.today || 0, 2)} + open ${window.fmt$(todayUnreal, 2)}` : undefined}>{today === 0 ? dash : window.fmt$(today, 2)}</td>
                <td className={"num tnum " + (pnl30 > 0 ? "pos" : pnl30 < 0 ? "neg" : "muted")}>{pnl30 === 0 ? dash : window.fmt$(pnl30, 0)}</td>
                <td className="micro muted">{/* per-strategy 30d sparkline lands when the hub serves the series */}—</td>
              </tr>);
          })}
        </tbody>
      </table>
    </div>);

}

/* Pretty-print one SPC row: resolves account/strategy labels through
 * window.ACCOUNTS when the agent's alert message left them null.
 *
 * Inference order (frontend-only — long-term fix is the agent tagging
 * spc_monitor alerts with account_label + strategy, but this covers
 * older bots and partial fills):
 *   1. account_label set         → use it
 *   2. instance_id has one acct  → use that account
 *   3. strategy + roster lookup  → if exactly one account has this
 *      strategy in its roster, infer that account.  Critical for
 *      multi-account agents (aviaf-paper hosts prac + combine4): step
 *      2 returns >1 candidate, but step 3 disambiguates by which
 *      account actually runs the strategy named in the alert.
 *   4. strategy missing         → if account known and has exactly
 *      one strategy attached, use it.
 *   5. Both still missing       → null (caller treats as "fleet-wide").
 */
function resolveSpcRow(r) {
  const accts = window.ACCOUNTS || [];
  let account = r.account_label || null;
  let strategy = r.strategy || null;
  let inferredAccount = false;
  let inferredStrategy = false;
  if (!account && r.instance_id) {
    const onInst = accts.filter((a) => a.instance === r.instance_id || a.instance_id === r.instance_id);
    if (onInst.length === 1) {
      account = onInst[0].label || onInst[0].id || null;
      inferredAccount = !!account;
    } else if (onInst.length > 1 && strategy) {
      const owns = onInst.filter((a) =>
        Array.isArray(a.strategies) && a.strategies.includes(strategy));
      if (owns.length === 1) {
        account = owns[0].label || owns[0].id || null;
        inferredAccount = !!account;
      }
    }
  }
  if (!account && strategy) {
    const owns = accts.filter((a) =>
      Array.isArray(a.strategies) && a.strategies.includes(strategy));
    if (owns.length === 1) {
      account = owns[0].label || owns[0].id || null;
      inferredAccount = !!account;
    }
  }
  if (!strategy && account) {
    const acctRow = accts.find((a) => (a.label || a.id) === account);
    const list = (acctRow && Array.isArray(acctRow.strategies)) ? acctRow.strategies : [];
    if (list.length === 1) { strategy = list[0]; inferredStrategy = true; }
  }
  return { account, strategy, inferredAccount, inferredStrategy };
}

/* SPC Overview panel — red-blue squared grid (one mini 4-track block
 * per (account, strategy) currently warning), matching the design of
 * AccountSpcPanel on the per-account page.
 *
 * Source of truth: /api/spc rows (current open warnings) for filtering
 * + window.SPC_BASELINES (expected_wr/pnl/tpd/dd + window) + window.TRADES
 * for the rolling z-series.  By iterating baselines (which always carry
 * explicit account_label + strategy), we sidestep the "(unknown account)"
 * problem entirely — no inference needed.  We then filter to baselines
 * whose corresponding (acct, strat) appears in SPC_ROWS as currently
 * warning, so the panel still says "open warnings" — just rendered as
 * a heatmap instead of a text list.
 */
function SpcPanel() {
  const rows = window.SPC_ROWS || [];
  const baselines = window.SPC_BASELINES || {};
  const trades = window.TRADES || [];

  // Index open warnings by (acct, strat) so a single block surfaces
  // every warning track on that pair without duplicating the row.
  const warnByKey = new Map();    // "acct|strat" -> [rows]
  for (const r of rows) {
    const { account, strategy } = resolveSpcRow(r);
    if (!account || !strategy) continue;
    const k = `${account}|${strategy}`;
    (warnByKey.get(k) || warnByKey.set(k, []).get(k)).push(r);
  }

  if (warnByKey.size === 0) {
    return (
      <div className="right-block">
        <h3>SPC · open warnings</h3>
        <div style={{padding:"10px var(--pad) 12px",color:"var(--muted)",fontFamily:"var(--mono)",fontSize:11}}>
          No open SPC warnings.
        </div>
      </div>);
  }

  // Render compact grid per (acct, strat).  Window kept small (15) so
  // four tracks fit in the Overview side panel without horizontal
  // scroll on a narrow column.
  const W_COMPACT = 15;
  const blocks = [];
  for (const [key, alertList] of warnByKey.entries()) {
    const [acct, strat] = key.split("|");
    const base = baselines[`${acct}|${strat}`];
    // Fallback when no baseline: emit a tiny 1-cell strip per warning
    // track so the row is visible even if the agent hasn't shipped
    // expected_* yet.
    if (!base) {
      blocks.push(
        <div key={key} style={{marginBottom:10}}>
          <div className="mono" style={{fontSize:11,color:"var(--ink-2)",letterSpacing:"0.04em",marginBottom:4,display:"flex",justifyContent:"space-between"}}>
            <span><span style={{color:"var(--ink)"}}>{window.strategyLabelFor ? window.strategyLabelFor(acct, strat) : strat}</span> <span style={{color:"var(--muted-2)"}}>· {acct}</span></span>
            <span className="muted" style={{fontSize:10}}>no baseline</span>
          </div>
          {alertList.map((r, i) => {
            const isAlarm = (r.level || "").includes("ALARM");
            const z = Number.isFinite(r.z) ? r.z : 0;
            const v = _spcCellDataV(z, r.track);
            return (
              <div key={i} style={{display:"flex",alignItems:"center",gap:8,marginBottom:2,fontFamily:"var(--mono)",fontSize:10.5}}>
                <div className="spc-cell" data-v={v} style={{width:14,height:14,borderRadius:2}} title={r.detail || ""} />
                <span style={{textTransform:"uppercase",letterSpacing:"0.04em",color:"var(--ink-2)",minWidth:32}}>{r.track || "—"}</span>
                <span className={"tnum " + (isAlarm ? "neg" : "warn")}>z={z >= 0 ? "+" : ""}{z.toFixed(2)}</span>
                <span className="muted" style={{flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}} title={r.detail||""}>{r.detail || ""}</span>
              </div>
            );
          })}
        </div>
      );
      continue;
    }
    // Baseline present — render the full red-blue 4-track heatmap, same
    // shape as AccountSpcPanel just narrower window so the Overview side
    // panel stays uncluttered.
    const myTrades = trades.filter((t) =>
      t.account === acct && t.strategy === strat && t.isExit
    );
    myTrades.sort((a, b) => (a.exitTime || "").localeCompare(b.exitTime || ""));
    const W = Math.max(8, Math.min(W_COMPACT, base.window || W_COMPACT));
    const wrZ  = _rollingWrZ (myTrades, base.expected_wr,  W);
    const pnlZ = _rollingPnlZ(myTrades, base.expected_pnl, W);
    const tpdZ = _rollingTpdZ(myTrades, base.expected_tpd, W);
    const ddZ  = _rollingDdZ (myTrades, base.expected_dd,  base.dd_std);
    const label = window.strategyLabelFor ? window.strategyLabelFor(acct, strat) : strat;
    blocks.push(
      <div key={key} style={{marginBottom:12}}>
        <div className="mono" style={{fontSize:11,color:"var(--ink-2)",letterSpacing:"0.04em",marginBottom:4,display:"flex",justifyContent:"space-between"}}>
          <span><span style={{color:"var(--ink)"}}>{label}</span> <span style={{color:"var(--muted-2)"}}>· {acct}</span></span>
          <span className="muted" style={{fontSize:10}}>{myTrades.length} t · w={W}</span>
        </div>
        {_spcTrackRow({label:"WR",   track:"wr",  zSeries:wrZ,  window:W})}
        {_spcTrackRow({label:"$/t",  track:"pnl", zSeries:pnlZ, window:W})}
        {_spcTrackRow({label:"TPD",  track:"tpd", zSeries:tpdZ, window:W})}
        {_spcTrackRow({label:"DD",   track:"dd",  zSeries:ddZ,  window:W})}
      </div>
    );
  }

  return (
    <div className="right-block">
      <h3>SPC · open warnings</h3>
      <div style={{ padding: "4px var(--pad) 10px" }}>
        {blocks}
        <div className="spc-axis" style={{ marginTop: 4 }}>
          <span>oldest</span>
          <span>latest</span>
        </div>
      </div>
    </div>);
}

function HealthPanel() {
  // 1-second tick to keep time-since values ("Ns ago", staleness
  // thresholds) current.  Without this, the row says e.g. "318s ago"
  // and stays there until the next WS frame triggers a bump() — so a
  // quiet agent looks like a frozen agent.  setInterval(..., 1000) is
  // cheaper than re-rendering on every WS frame and keeps the clock
  // honest even when nothing is happening.
  const [, _tickHealth] = usePg(0);
  usePgE(() => {
    const h = setInterval(() => _tickHealth(t => (t + 1) | 0), 1000);
    return () => clearInterval(h);
  }, []);

  // Single source of truth in data-live.jsx so desktop + mobile read
  // the same derivation.  Any new health signal added there lights up
  // both surfaces at once.
  const items = (window.computeHealthItems && window.computeHealthItems()) || [];

  return (
    <div className="right-block">
      <h3>Health</h3>
      {items.map((it, i) =>
      <div className="health-row" key={i}>
          <div className="lbl">{it.k}</div>
          <div className="v">
            <span className="muted" style={{ fontSize: 11 }}>{it.x}</span>
            {/* Pulse the dot ONLY when something is wrong (warn/neg) so
                the operator's eye is drawn to the row that actually
                needs attention.  ok rows render a static dot — no
                animation noise for routine green state. */}
            <span
              className={"dot " + (it.v === "warn" ? "warn" : it.v === "neg" ? "neg" : "")}
              style={ it.v === "ok" ? { animation: "none" } : undefined }
            />
          </div>
        </div>
      )}
    </div>);

}

function FeedPanel() {
  const feed = window.FEED || [];
  return (
    <div className="right-block" style={{ borderBottom: 0 }}>
      <h3>Event feed · live</h3>
      <div className="feed">
        {feed.length === 0 ? (
          <div style={{padding:"10px 0",color:"var(--muted)",fontFamily:"var(--mono)",fontSize:11}}>
            No events yet.
          </div>
        ) : feed.map((f, i) => {
          const tag = (f.tag || "info").toString();
          return (
            <div className="feed-row" key={i}>
              <span className="t">{f.t || ""}</span>
              <span className="ev"><span className={"tag " + tag}>{tag.toUpperCase()}</span>{f.txt || ""}</span>
            </div>);
        })}
      </div>
    </div>);

}

/* ── Instances · process monitor (terminal-dense) ─────────── */
window.PageInstances = function PageInstances() {
  const grouped = {};
  window.INSTANCES.forEach((i) => {(grouped[i.host] = grouped[i.host] || []).push(i);});
  const hosts = Object.keys(grouped);

  return (
    <div>
      <window.SectionHeader title="Instances" count={window.INSTANCES.length} sub="aviaf processes · grouped by host" right={
      <>
          <button data-active="true">All</button><button>Running</button><button>Stopped</button>
          <window.Btn kind="primary">+ Launch</window.Btn>
        </>
      } />
      {hosts.map((host) =>
      <div key={host}>
          <div style={{ padding: "10px var(--pad) 6px", borderBottom: "1px dashed var(--rule)", fontFamily: "var(--mono)", fontSize: 11, color: "var(--muted)", letterSpacing: "0.08em", textTransform: "uppercase", display: "flex", justifyContent: "space-between" }}>
            <span><b style={{ color: "var(--ink)", fontWeight: 500 }}>{host}</b> · {grouped[host].length} process{grouped[host].length === 1 ? "" : "es"}</span>
            <span className="tnum">{grouped[host].filter((i) => i.state === "running").length} running</span>
          </div>
          {grouped[host].map((i) => {
          const cfg = window.CONFIGS.find((c) => c.id === i.config);
          const acct = window.ACCOUNTS.find((a) => a.id === i.account);
          const dayPnl = window.STRATS.filter((s) => s.instance === i.id).reduce((s, x) => s + x.today, 0);
          const hbMax = Math.max(...i.heartbeat, 0.01);
          return (
            <div key={i.id} className="inst-row" data-state={i.state} onClick={() => {location.hash = `#/instances/${i.id}`;}}>
                <div className="led" />
                <div>
                  <div className="inst-id">{i.id}</div>
                  <div className="inst-host">pid {i.pid} · py {i.py} · {i.branch.split("@")[0]}</div>
                </div>
                <div>
                  <div style={{ fontSize: 12 }}>{cfg ? cfg.label : "—"}</div>
                  <div className="inst-host">{acct ? `${acct.label}` : "no account"}</div>
                </div>
                <div className="inst-meta">
                  <span>up <b style={{ color: "var(--ink)", fontWeight: 500 }} className="tnum">{i.uptime}</b></span>
                  <span>cpu <span className="tnum">{i.cpu.toFixed(1)}%</span></span>
                  <span>mem <span className="tnum">{i.mem}M</span></span>
                  <span>tps <span className="tnum">{i.ticksPerSec}</span></span>
                  <span>lag <span className={"tnum " + (i.lagMs > 80 ? "warn" : "")}>{i.lagMs}ms</span></span>
                  <span title="Hub↔agent WebSocket round-trip (RFC 6455 ping/pong)">ping <span className={"tnum " + (i.rttMs == null ? "muted" : i.rttMs > 200 ? "warn" : "")}>{i.rttMs == null ? "—" : i.rttMs + "ms"}</span></span>
                  <span>· <span className="tnum">{i.strategies.length}</span> strat</span>
                  <span className={dayPnl > 0 ? "pos" : dayPnl < 0 ? "neg" : ""}>day {dayPnl === 0 ? "—" : window.fmt$(dayPnl, 0)}</span>
                </div>
                <div className="inst-hb">
                  {i.heartbeat.slice(-30).map((v, j) =>
                <div key={j} className="b" style={{ height: Math.max(2, v / hbMax * 22) + "px" }} />
                )}
                </div>
              </div>);

        })}
        </div>
      )}
    </div>);

};

window.PageInstanceDetail = function PageInstanceDetail({ id }) {
  const inst = window.INSTANCES.find((x) => x.id === id);
  if (!inst) return <div style={{ padding: 32 }}>Instance not found.</div>;
  // Memo the STRATS filter — fresh array identity every render meant
  // every child saw a "new" prop and re-rendered even when the data
  // hadn't moved.  inst.strategies identity is stable across renders
  // since INSTANCES is replaced wholesale by data-live.jsx.
  const STRATS = window.STRATS;
  const strats = usePgM(
    () => (STRATS || []).filter((s) => inst.strategies.includes(s.id)),
    [STRATS, inst.strategies],
  );
  const positions = window.OPEN_POSITIONS[inst.id] || [];
  const cfg = window.CONFIGS.find((c) => c.id === inst.config);

  return (
    <>
      <window.PageHeader
        crumbs={[{ label: "Instances", href: "#/instances" }, { label: inst.id }]}
        actions={<>
          <window.Btn onClick={() => document.getElementById("inst-logfile-section")?.scrollIntoView({ behavior: "smooth", block: "start" })}>Logs ↓</window.Btn>
          <ConfigReloadBtn instId={inst.id} />
          <RestartInstanceBtn instId={inst.id} />
          <CmdBtn label="Killswitch" kind="danger" busyLabel="Killing…" okMsg="killswitch sent"
            title="Emergency stop-all trading on this agent (irreversible)"
            confirm={`EMERGENCY KILLSWITCH on ${inst.id}?\n\nThis halts ALL trading on the agent immediately and is irreversible — you must re-enable trading manually afterward.`}
            run={() => window.killswitchInstance(inst.id)} />
        </>} />
      
      <div style={{ padding: "16px var(--pad) 0", display: "grid", gridTemplateColumns: "repeat(4, 1fr)", borderBottom: "1px solid var(--rule)" }}>
        {[
        { l: "State", v: <window.StateBadge state={inst.state} size="md" /> },
        { l: "Host · PID", v: <span className="mono tnum">{inst.host}<span className="muted"> · {inst.pid}</span></span> },
        { l: "Uptime", v: <span className="mono tnum">{inst.uptime}</span> },
        { l: "Started", v: <span className="mono tnum">{inst.started}</span> },
        { l: "Account", v: <span className="mono">{inst.account ? <a href={`#/accounts/${inst.account}`} style={{ color: "var(--ink)" }}>{inst.account}</a> : "—"}</span> },
        { l: "Config", v: <span className="mono"><a href={`#/configs/${inst.config}`} style={{ color: "var(--ink)", fontSize: 11, wordBreak: "break-all" }}>{cfg ? cfg.file : inst.config}</a></span> },
        { l: "Branch", v: <span className="mono" style={{ fontSize: 11 }}>{inst.branch}</span> },
        { l: "CPU · Mem · TPS", v: <span className="mono tnum">{inst.cpu.toFixed(1)}% · {inst.mem}M · {inst.ticksPerSec}/s</span> },
        { l: "Ping · Clock lag", v: <span className="mono tnum" title="Hub↔agent WebSocket round-trip (RFC 6455 ping/pong) · reported clock offset">{inst.rttMs == null ? "—" : inst.rttMs + "ms"}<span className="muted"> · {inst.lagMs == null ? "—" : inst.lagMs + "ms"}</span></span> }].
        map((c, i) =>
        <div key={i} style={{ padding: "6px 20px 18px", borderRight: i % 4 !== 3 ? "1px solid var(--rule)" : "none", minWidth: 0 }}>
            <div style={{ fontFamily: "var(--mono)", fontSize: 10.5, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--muted)", marginBottom: 6 }}>{c.l}</div>
            <div style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>{c.v}</div>
          </div>
        )}
      </div>

      <window.SectionHeader title="Strategies · attached" count={strats.length} right={<window.Btn kind="accent">+ Attach</window.Btn>} />
      <div className="table-wrap">
        <table className="strat">
          <thead><tr>
            <th>Strategy</th><th>Symbol</th><th>State</th>
            <th className="num">Contracts</th><th className="num">Risk/trade</th>
            <th className="num">Today</th><th className="num">30d P&L</th><th>Controls</th>
          </tr></thead>
          <tbody>
            {strats.map((s) =>
            <tr key={s.id}>
                <td><a href={`#/strategies/${s.defId}`} style={{ color: "var(--ink)", textDecoration: "none" }}>{s.name}</a></td>
                <td className="muted">{s.symbol}</td>
                <td><window.StateBadge state={s.state} /></td>
                <td className="num tnum"><ContractStepper value={s.contracts} /></td>
                <td className="num tnum">{window.fmt$(s.riskPerTrade, 0)}</td>
                <td className={"num tnum " + (s.today > 0 ? "pos" : s.today < 0 ? "neg" : "muted")}>{s.today === 0 ? "—" : window.fmt$(s.today, 2)}</td>
                <td className={"num tnum " + (s.pnl30 > 0 ? "pos" : "neg")}>{window.fmt$(s.pnl30, 0)}</td>
                <td>
                  <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                    {s.state === "running" || s.state === "armed"
                      ? <CmdBtn label="Pause" busyLabel="Pausing…" okMsg="paused"
                          confirm={`Pause trading on account ${s.acct}? This affects ALL strategies on that account, not just ${s.name}.`}
                          run={() => window.pauseInstance(s.instance, s.acct)} />
                      : <CmdBtn label="Start" kind="primary" busyLabel="Starting…" okMsg="resumed"
                          confirm={`Resume trading on account ${s.acct}?`}
                          run={() => window.resumeInstance(s.instance, s.acct)} />}
                    <CmdBtn label="Flatten" kind="danger" busyLabel="Flattening…" okMsg="flatten sent"
                      confirm={`Flatten ALL open positions on account ${s.acct}? Sends market-close orders for every open position on the account.`}
                      run={() => window.flattenAccount(s.instance, s.acct)} />
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <window.SectionHeader title="Open positions" count={positions.length} />
      {positions.length === 0 ?
      <div style={{ padding: "24px var(--pad)", color: "var(--muted)", fontFamily: "var(--mono)", fontSize: 12 }}>No open positions.</div> :
      <PositionsTable positions={positions} />}

      <div id="inst-logfile-section"><window.ErrorBoundary label="process log"><InstanceLogFile instId={inst.id} /></window.ErrorBoundary></div>
    </>);

};

/* Shared command button: optional confirm → POST (via a window.* command
 * helper) → busy state → inline ✓/✗ result toast.  `run` returns the
 * helper's promise (CommandResponse).  This is what turns the previously
 * dead Pause / Start / Flatten / Killswitch controls into real, audited
 * actions with operator-visible feedback. */
function CmdBtn({ label, busyLabel, kind, confirm, run, okMsg, disabled, title }) {
  const [busy, setBusy] = usePg(false);
  const [msg, setMsg] = usePg(null);
  const danger = kind === "danger" || kind === "warn";
  const onClick = async () => {
    if (busy || disabled) return;
    if (confirm) {
      const ok = window.confirmModal
        ? await window.confirmModal({
            title: (danger ? "⚠ " : "") + label, body: confirm,
            confirmLabel: label, danger,
          })
        : window.confirm(confirm);
      if (!ok) return;
    }
    setBusy(true);
    setMsg(null);
    try {
      const body = await run();
      const err = body && (body.error
        || (body.result && body.result.ack && body.result.ack.error));
      if (err) {
        setMsg("✗ " + err);
        window.pushToast && window.pushToast({ kind: "error", title: label + " failed", msg: err });
      } else {
        setMsg("✓ " + (okMsg || "sent"));
        window.pushToast && window.pushToast({ kind: "success", title: label, msg: okMsg || "sent" });
      }
    } catch (e) {
      const m = String(e && e.message ? e.message : e);
      setMsg("✗ " + m);
      window.pushToast && window.pushToast({ kind: "error", title: label + " failed", msg: m });
    } finally {
      setBusy(false);
      setTimeout(() => setMsg(null), 6000);
    }
  };
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <window.Btn kind={kind} onClick={onClick} disabled={disabled || busy} title={title}>
        {busy ? (busyLabel || "…") : label}
      </window.Btn>
      {msg && (
        <span style={{
          fontSize: 11, fontFamily: "var(--mono)",
          color: msg[0] === "✓" ? "var(--pos)" : "var(--neg)",
        }}>{msg}</span>
      )}
    </span>
  );
}
window.CmdBtn = CmdBtn;

// Hot config reload: preview the diff (dry-run) in the confirm modal, then
// apply. Adds/removes only the changed strategies; accounts with open
// positions are never disturbed (their removes are skipped agent-side).
function ConfigReloadBtn({ instId }) {
  const [busy, setBusy] = usePg(false);
  const onClick = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const p = await window.configReload(instId, true);   // dry-run preview
      const add = p.strategies_added || [], rem = p.strategies_removed || [];
      const skip = p.skipped || [], newAcc = p.accounts_new_in_config || [], goneAcc = p.accounts_missing_from_config || [];
      if (!add.length && !rem.length) {
        window.pushToast && window.pushToast({ kind: "info", title: "Config reload",
          msg: "Running roster already matches config.toml — no strategy changes." +
            (newAcc.length || goneAcc.length ? ` (account diffs reported, not auto-applied)` : "") });
        return;
      }
      const body =
        (add.length ? `＋ Add: ${add.join(", ")}\n` : "") +
        (rem.length ? `－ Remove: ${rem.join(", ")}\n` : "") +
        (newAcc.length ? `\nNew accounts in config (NOT auto-added): ${newAcc.join(", ")}` : "") +
        (goneAcc.length ? `\nAccounts missing from config (left running): ${goneAcc.join(", ")}` : "") +
        `\n\nApplied live from ${p.path}. Accounts with open positions are never disturbed — their removes are skipped.`;
      const ok = await window.confirmModal({ title: "↻ Reload config on " + instId, body, confirmLabel: "Apply changes" });
      if (!ok) return;
      const res = await window.configReload(instId, false);   // apply
      const a = res.strategies_added || [], rr = res.strategies_removed || [], sk = res.skipped || [], er = res.errors || [];
      window.pushToast && window.pushToast({
        kind: er.length ? "warn" : "success", title: "Config reloaded",
        msg: `+${a.length} added · −${rr.length} removed` + (sk.length ? ` · ${sk.length} skipped (open positions)` : "") + (er.length ? ` · ${er.length} errors` : ""),
      });
    } catch (e) {
      const m = String(e && e.message ? e.message : e);
      window.pushToast && window.pushToast({ kind: "error", title: "Config reload failed",
        msg: /501/.test(m) ? "agent predates config_reload — redeploy the agent" : /503/.test(m) ? "agent offline" : m });
    } finally { setBusy(false); }
  };
  return <window.Btn onClick={onClick} disabled={busy} title="Re-read config.toml and apply only the strategy delta — no restart">{busy ? "reloading…" : "↻ Reload config"}</window.Btn>;
}

function RestartInstanceBtn({ instId }) {
  const [busy, setBusy] = usePg(false);
  const [msg, setMsg] = usePg(null);
  const onClick = async () => {
    if (busy) return;
    const body =
      "The agent will stop and re-exec. Any in-flight orders die with the " +
      "process; existing positions remain at the broker (the agent re-attaches " +
      "on startup).\n\nOlder agents may not implement RESTART — you'll see " +
      "'unknown command kind' if so.";
    const ok = window.confirmModal
      ? await window.confirmModal({ title: "⚠ Restart " + instId, body, confirmLabel: "Restart", danger: true })
      : window.confirm(body);
    if (!ok) return;
    setBusy(true);
    setMsg(null);
    try {
      const r = await fetch(
        "/api/commands/" + encodeURIComponent(instId) + "/restart" +
        "?confirm_label=" + encodeURIComponent(instId),
        { method: "POST", credentials: "same-origin" }
      );
      const body = await r.json().catch(() => ({}));
      if (!r.ok || body.ok === false) {
        setMsg("✗ " + (body.error || body.detail || ("HTTP " + r.status)));
      } else {
        setMsg("✓ restart sent");
      }
    } catch (e) {
      setMsg("✗ " + String(e));
    } finally {
      setBusy(false);
      setTimeout(() => setMsg(null), 6000);
    }
  };
  return (
    <span style={{display:"inline-flex", alignItems:"center", gap:8}}>
      <window.Btn kind="warn" onClick={onClick}>
        {busy ? "Restarting…" : "Restart"}
      </window.Btn>
      {msg && (
        <span style={{
          fontSize: 11,
          fontFamily: "var(--mono)",
          color: msg.startsWith("✓") ? "var(--pos)" : "var(--neg)",
        }}>{msg}</span>
      )}
    </span>
  );
}

// Live tail of the agent's REAL process log file (paper_<stamp>.log), paged
// by byte offset via /api/instances/<id>/logfile so a multi-hundred-MB file is
// never shipped whole.  Tails the end on open, follows new bytes, and loads
// older CHUNK-sized pages when the operator scrolls to the top.
function InstanceLogFile({ instId }) {
  const CHUNK = 65536;
  const [st, setSt] = usePg({ text: "", start: 0, end: 0, size: 0, path: "", loading: true, err: null });
  const [follow, setFollow] = usePg(true);
  const [older, setOlder] = usePg(false);
  const paneRef = usePgR(null);
  const stRef = usePgR(st); stRef.current = st;
  const prependRef = usePgR(null);   // scrollHeight snapshot for scroll preservation

  const url = (qs) => `/api/instances/${encodeURIComponent(instId)}/logfile?${qs}`;
  const errText = (code) =>
    code === 501 ? "this agent predates log tailing — redeploy the agent"
    : code === 503 ? "agent offline — no live log to tail"
    : code === 504 ? "agent timed out serving the log"
    : `error ${code}`;

  // Initial tail (last CHUNK bytes).
  usePgE(() => {
    let alive = true;
    setSt({ text: "", start: 0, end: 0, size: 0, path: "", loading: true, err: null });
    (async () => {
      try {
        const r = await fetch(url(`length=${CHUNK}`), { credentials: "same-origin" });
        if (!alive) return;
        if (!r.ok) { setSt(s => ({ ...s, loading: false, err: errText(r.status) })); return; }
        const d = await r.json();
        setSt({ text: d.data || "", start: d.start, end: d.end, size: d.size, path: d.path, loading: false, err: null });
      } catch (e) { if (alive) setSt(s => ({ ...s, loading: false, err: String(e) })); }
    })();
    return () => { alive = false; };
  }, [instId]);

  // Follow: poll for bytes appended past `end`.
  usePgE(() => {
    if (!follow) return;
    const t = setInterval(async () => {
      const s = stRef.current;
      if (s.loading || s.err) return;
      try {
        const r = await fetch(url(`offset=${s.end}&length=${CHUNK}`), { credentials: "same-origin" });
        if (!r.ok) return;
        const d = await r.json();
        if (d.end > s.end && d.data) setSt(p => ({ ...p, text: p.text + d.data, end: d.end, size: d.size }));
        else if (d.size !== s.size) setSt(p => ({ ...p, size: d.size }));
      } catch (_) { /* transient */ }
    }, 2500);
    return () => clearInterval(t);
  }, [follow, instId]);

  // Stick to the bottom while following.
  usePgE(() => {
    if (!follow) return;
    const el = paneRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [st.text, follow]);

  // Preserve scroll position after an older page is prepended.
  React.useLayoutEffect(() => {
    if (prependRef.current != null) {
      const el = paneRef.current;
      if (el) el.scrollTop += el.scrollHeight - prependRef.current;
      prependRef.current = null;
    }
  });

  async function loadOlder() {
    const s = stRef.current;
    if (s.start <= 0 || older) return;
    setOlder(true); setFollow(false);
    const newOff = Math.max(0, s.start - CHUNK);
    const len = s.start - newOff;
    try {
      const r = await fetch(url(`offset=${newOff}&length=${len}`), { credentials: "same-origin" });
      if (r.ok) {
        const d = await r.json();
        const el = paneRef.current;
        prependRef.current = el ? el.scrollHeight : 0;
        setSt(p => ({ ...p, text: d.data + p.text, start: d.start }));
      }
    } catch (_) { /* transient */ } finally { setOlder(false); }
  }

  function onScroll(e) {
    if (e.target.scrollTop < 60 && stRef.current.start > 0 && !older) loadOlder();
  }

  // Render lines; the first line is partial when start>0 (mid-file byte cut).
  let lines = st.text ? st.text.split("\n") : [];
  if (st.start > 0 && lines.length) lines = lines.slice(1);
  if (lines.length && lines[lines.length - 1] === "") lines = lines.slice(0, -1);
  const lvlOf = (l) => {
    const m = /\[(INFO|WARN|WARNING|ERROR|DEBUG|CRITICAL)\]/.exec(l);
    if (!m) return "INFO";
    return m[1] === "WARNING" ? "WARN" : m[1] === "CRITICAL" ? "ERROR" : m[1];
  };
  const fmtKB = (n) => n >= 1048576 ? (n / 1048576).toFixed(1) + " MB" : (n / 1024).toFixed(0) + " KB";
  const pct = st.size > 0 ? Math.round((st.size - st.start) / st.size * 100) : 100;

  return (
    <>
      <window.SectionHeader
        title="Process log · live tail"
        sub={st.err ? instId : `${st.path || instId} · ${fmtKB(st.size)} · showing last ${pct}%`}
        right={
          <>
            {st.start > 0 && (
              <window.Btn onClick={loadOlder} disabled={older}>
                {older ? "loading…" : "↑ older"}
              </window.Btn>
            )}
            <window.Btn onClick={() => setFollow(f => !f)} kind={follow ? "primary" : undefined}>
              {follow ? "Follow ●" : "Follow"}
            </window.Btn>
          </>
        }
      />
      <div className="log-pane" ref={paneRef} onScroll={onScroll}>
        {st.err ? (
          <div style={{ padding: "16px var(--pad)", color: "var(--warn)", fontFamily: "var(--mono)", fontSize: 12 }}>{st.err}</div>
        ) : st.loading ? (
          <div style={{ padding: "16px var(--pad)", color: "var(--muted)", fontFamily: "var(--mono)", fontSize: 12 }}>loading tail…</div>
        ) : (
          <>
            {st.start > 0 && (
              <div onClick={loadOlder} style={{ padding: "6px var(--pad)", color: "var(--muted)", fontFamily: "var(--mono)", fontSize: 11, cursor: "pointer", textAlign: "center", borderBottom: "1px solid var(--rule)" }}>
                {older ? "loading older…" : `↑ scroll up or click to load older (${fmtKB(st.start)} above)`}
              </div>
            )}
            {lines.map((l, i) => (
              <div key={i} className="logf-line" data-lvl={lvlOf(l)}>{l || " "}</div>
            ))}
          </>
        )}
      </div>
    </>);
}

function InstanceLog({ instId, instHost }) {
  // Filter + tail are local UI state.  Re-reads window.INSTANCE_LOGS on
  // every tick of __LIVE.rev so new lines from data-live.jsx's WS
  // event handler flow in without prop drilling — matches the same
  // re-render strategy the rest of the dashboard uses.
  const [filter, setFilter] = usePg("ALL");
  const [tail, setTail] = usePg(true);
  const [, tick] = usePg(0);
  const paneRef = usePgR(null);

  usePgE(() => {
    const t = setInterval(() => tick(n => (n + 1) | 0), 1000);
    return () => clearInterval(t);
  }, []);

  const all = window.INSTANCE_LOGS[instId] || [];
  const visible = filter === "ALL" ? all : all.filter(l => l.lvl === filter);
  // Most-recent at the BOTTOM (terminal convention).  Data is stored
  // newest-first, so reverse for display.
  const display = [...visible].reverse();

  usePgE(() => {
    if (!tail) return;
    const el = paneRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [tail, display.length]);

  const pillBtn = (label, active, onClick) => (
    <button data-active={active ? "true" : undefined} onClick={onClick}>
      {label}
    </button>
  );

  return (
    <>
      <window.SectionHeader
        title="Recent log · last 12h"
        sub={`${instId} · ${instHost} · ${visible.length}/${all.length} lines`}
        right={
          <>
            {pillBtn("All",   filter === "ALL",   () => setFilter("ALL"))}
            {pillBtn("INFO",  filter === "INFO",  () => setFilter("INFO"))}
            {pillBtn("WARN",  filter === "WARN",  () => setFilter("WARN"))}
            {pillBtn("ERROR", filter === "ERROR", () => setFilter("ERROR"))}
            <window.Btn onClick={() => setTail(t => !t)} kind={tail ? "primary" : undefined}>
              {tail ? "Tail ●" : "Tail"}
            </window.Btn>
          </>
        }
      />
      <div className="log-pane" ref={paneRef}>
        {display.length === 0 ? (
          <div style={{ padding: "16px var(--pad)", color: "var(--muted)", fontFamily: "var(--mono)", fontSize: 12 }}>
            {all.length === 0
              ? "no events yet from this instance · health pings appear here as they arrive"
              : `no ${filter} lines in the last 12h`}
          </div>
        ) : display.map((l, i) => (
          <div key={i} className="log-line" data-lvl={l.lvl}>
            <span className="t">{l.t}</span>
            <span className="lvl">{l.lvl}</span>
            <span className="src">{l.src}</span>
            <span className="msg">{l.txt}</span>
          </div>
        ))}
      </div>
    </>);

}

function ContractStepper({ value }) {
  const [v, setV] = usePg(value);
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, border: "1px solid var(--rule-2)" }}>
      <button onClick={() => setV((x) => Math.max(0, x - 1))} style={{ background: "transparent", border: 0, color: "var(--muted)", padding: "2px 8px", fontFamily: "var(--mono)", cursor: "pointer" }}>−</button>
      <span style={{ minWidth: 18, textAlign: "center" }}>{v}</span>
      <button onClick={() => setV((x) => x + 1)} style={{ background: "transparent", border: 0, color: "var(--muted)", padding: "2px 8px", fontFamily: "var(--mono)", cursor: "pointer" }}>+</button>
    </span>);

}

function PositionsTable({ positions }) {
  return (
    <div className="table-wrap">
      <table className="strat">
        <thead><tr>
          <th>Symbol</th><th>Side</th><th className="num">Qty</th>
          <th className="num">Entry</th><th className="num">Mark</th>
          <th className="num">Unrealized</th><th>Opened</th><th>Strategy</th><th></th>
        </tr></thead>
        <tbody>
          {positions.map((p, i) =>
          <tr key={i}>
              <td>{p.sym}</td>
              <td className={p.side === "long" ? "pos" : "neg"} style={{ textTransform: "uppercase", letterSpacing: "0.06em" }}>{p.side}</td>
              <td className="num tnum">{p.qty}</td>
              <td className="num tnum">{p.entry.toLocaleString("en-US", { minimumFractionDigits: 2 })}</td>
              <td className="num tnum">{p.mark.toLocaleString("en-US", { minimumFractionDigits: 2 })}</td>
              <td className={"num tnum " + (p.unreal >= 0 ? "pos" : "neg")}>{window.fmt$(p.unreal, 2)}</td>
              <td className="muted tnum">{p.opened}</td>
              <td className="muted">{p.strategy_id || p.strat || "—"}</td>
              <td><window.Btn kind="danger">Close</window.Btn></td>
            </tr>
          )}
        </tbody>
      </table>
    </div>);

}

/* ====== begin: new strategy / class / risk-map components (from design drop) ====== */

function StrategyRiskMap({ strats: stratsIn, classes: classesProp, selectedId, onSelect }) {
  const [hidden, setHidden] = usePg(() => new Set());
  const [xMode, setXMode] = usePg("weekly"); // weekly | monthly | alltime
  const [sized, setSized] = usePg("trades");  // trades | contracts
  const [showSurvival, setShowSurvival] = usePg(true);
  const [showStopped, setShowStopped] = usePg(true);
  // Grouping: "strategy" (default) shows PURE strategy performance — one bubble
  // per strategy, trades merged across every account it runs on. "account"
  // shows one bubble per (account, strategy). Cross-account rows are R-native
  // (STRATS_BY_DEF).
  const [group, setGroup] = usePg("strategy");
  const strats = (group === "strategy" ? (window.STRATS_BY_DEF || []) : stratsIn) || [];
  // Normalization: "R" (default) divides P&L and drawdown by each strategy's
  // intended $-risk per trade (its 1R) so strategies on different instruments
  // / sizes are comparable — a GC strategy no longer dwarfs an MGC one by the
  // 10× multiplier. "$" shows raw dollars (survival is a $ concept).
  const [unit, setUnit] = usePg("R");

  const classes = classesProp && classesProp.length ? classesProp : window.STRATEGY_CLASSES;

  // Single-pass strat metadata cache.  Without this, `window.classOfStrat`
  // (which walks STRATEGY_CLASSES, scans matchPrefixes, etc.) gets called
  // from 5+ code paths per render × N strategies — measurably hot during
  // zoom/pan.  One pass through strats here, cheap dict lookups everywhere
  // else.  Stable per (strats, classes) — does NOT re-run on viewBox /
  // hover state changes.
  const w = 940, h = 560, padL = 84, padR = 32, padT = 24, padB = 64;
  const stratMeta = usePgM(() => {
    const meta = new Map();      // strat.id -> { klass, color }
    const classCounts = {};      // class.id -> # strats in that class
    const classKlassSet = new Set(classes.map((c) => c.id));
    const variantsByClass = new Map(); // klass -> Set<defId>
    for (const s of strats) {
      const klass = window.classOfStrat(s);
      const isCanonical = !s.defId || s.defId === klass;
      const color = isCanonical
        ? window.classMeta(klass).color
        : window.derivedClassColor(s.defId);
      meta.set(s.id, { klass, color });
      if (classKlassSet.has(klass)) {
        classCounts[klass] = (classCounts[klass] || 0) + 1;
      } else {
        // unknown class — still tracked for the variant legend, but
        // doesn't get a class-row chip.
        classCounts[klass] = (classCounts[klass] || 0) + 1;
      }
      if (s.defId) {
        if (!variantsByClass.has(klass)) variantsByClass.set(klass, new Set());
        variantsByClass.get(klass).add(s.defId);
      }
    }
    return { meta, classCounts, variantsByClass };
  }, [strats, classes]);

  const klassCount = stratMeta.classCounts;
  const colorOfStrat = (s) => (stratMeta.meta.get(s.id) || {}).color || window.classMeta("other").color;

  // Filter:
  //   - class chips toggle every variant in that class
  //   - variant chips (defId) toggle a single variant within a class
  //   - the "stopped" chip drops state ∉ LIVE_STATES rows from the canvas
  const liveSet = window.LIVE_STATES || new Set(["running", "armed", "paused"]);
  const visible = usePgM(() => strats.filter((s) => {
    const klass = (stratMeta.meta.get(s.id) || {}).klass;
    if (hidden.has(klass)) return false;
    if (s.defId && hidden.has("var:" + s.defId)) return false;
    if (!showStopped && !liveSet.has(s.state)) return false;
    return true;
  }), [strats, stratMeta, hidden, showStopped, liveSet]);

  // Variant inventory — drives the per-variant legend row.  Only render
  // when a class has > 1 variant.
  const variantList = usePgM(() => {
    const out = [];
    // Count strats per defId in one pass (replaces O(V×N) strats.filter calls).
    const defCount = new Map();
    for (const s of strats) {
      if (!s.defId) continue;
      defCount.set(s.defId, (defCount.get(s.defId) || 0) + 1);
    }
    for (const [klass, defSet] of stratMeta.variantsByClass.entries()) {
      const defs = Array.from(defSet).sort();
      if (defs.length <= 1) continue;
      for (const defId of defs) {
        out.push({
          defId, klass,
          color: defId === klass
            ? window.classMeta(klass).color
            : window.derivedClassColor(defId),
          count: defCount.get(defId) || 0,
        });
      }
    }
    return out;
  }, [strats, stratMeta]);

  // Per-mode field pickers.  Before 2026-05-24 these all read `pnl30`
  // (which was actually all-time) and `dd` (all-time) — "Weekly" just
  // divided by 4.3 and showed an averaged number that disagreed wildly
  // with the broker's actual last-week P&L when a strategy's lifetime
  // curve differed from its recent week.  Now pnl7/pnl30/pnlAll +
  // dd7/dd30/ddAll are emitted from data-live so x and y track the
  // same window.
  const pnlField = xMode === "weekly" ? "pnl7" : xMode === "alltime" ? "pnlAll" : "pnl30";
  const ddField  = xMode === "weekly" ? "dd7"  : xMode === "alltime" ? "ddAll"  : "dd30";
  // In R mode, divide by the strategy's 1R (riskUnit). Fall back to raw $ for
  // any strategy the agent didn't send a risk for (riskUnit missing/0).
  const asR = unit === "R";
  const asCt = unit === "perct";   // $/contract
  const rDiv = (s) => (asR && window.safeN(s.riskUnit, 0) > 0) ? s.riskUnit : 1;
  const ctDiv = (s) => {
    const c = window.safeN(s.aggR ? s.contractsSum : s.contracts, 0);
    return c > 0 ? c : 1;
  };
  // R: divide by 1R (aggR carries precomputed R fields). $/ct: divide raw $ by
  // contract count (size-normalized, instrument still visible). $: raw dollars.
  const _norm = (s, raw, rField) => asR
    ? (s.aggR ? window.safeN(s[rField], 0) : raw / rDiv(s))
    : asCt ? raw / ctDiv(s)
    : raw;
  const pnlOf = (s) => _norm(s, window.safeN(s[pnlField], 0), pnlField + "R");
  const ddOf  = (s) => Math.abs(_norm(s, Math.abs(window.safeN(s[ddField], 0)), ddField + "R"));
  const xVal = (s) => pnlOf(s);
  const yVal = (s) => ddOf(s);
  const sVal = (s) => sized === "trades" ? Math.max(4, window.safeN(s.trades, 6)) : Math.max(1, window.safeN(s.contracts, 1)) * 24;

  // ── chart bounds ──
  // Bounds depend only on the data + axis mode + survival toggle.  Memoize
  // so panning the viewBox (which re-renders the component) doesn't
  // re-walk strats and rebuild ticks.
  const bounds = usePgM(() => {
    let xMin = 0, xMax = 0.01, yMax = 1;
    for (const s of strats) {
      const xv = pnlOf(s);
      const yv = ddOf(s);
      if (xv < xMin) xMin = xv;
      if (xv > xMax) xMax = xv;
      if (yv > yMax) yMax = yv;
    }
    // $-survival threshold (the MLL band that kills accounts) is a dollar
    // concept — only pad the axis for it in $ mode.
    if (showSurvival && unit === "$") yMax = Math.max(yMax, 5000);
    const xSpan = (xMax - xMin) || 1;
    xMin -= xSpan * 0.10; xMax += xSpan * 0.12; yMax *= 1.08;
    return { xMin, xMax, yMax };
  }, [strats, xMode, showSurvival, pnlField, ddField, unit]);
  const { xMin, xMax, yMax } = bounds;

  const X = (v) => padL + (v - xMin) / (xMax - xMin) * (w - padL - padR);
  const Y = (v) => padT + (1 - v / yMax) * (h - padT - padB);

  const xTicks = usePgM(() => window.niceTicks(xMin, xMax, 6), [xMin, xMax]);
  const yTicks = usePgM(() => window.niceTicks(0, yMax, 5), [yMax]);

  // ── reference: efficiency lines profit/risk = k (where risk>0) ──
  // pnlOf / ddOf are already mode-windowed (weekly / 30d / alltime),
  // so the ratio is the per-window return-on-risk ratio.
  const ratios = strats.filter((s) => ddOf(s) > 0).map((s) => pnlOf(s) / ddOf(s));
  const refs = [];
  if (ratios.length) {
    const med = ratios.slice().sort((a, b) => a - b)[Math.floor(ratios.length / 2)];
    [med * 0.5, med, med * 2].forEach((r, i) => refs.push({ r: Math.max(0.0001, r), label: r.toFixed(2) + "×", emphasis: i === 1 }));
  }

  const fmt$short = (v) => {
    const a = Math.abs(v);
    if (a >= 1000) return (v < 0 ? "−" : "") + "$" + (a / 1000).toFixed(1) + "k";
    return (v < 0 ? "−" : "") + "$" + a.toFixed(0);
  };
  // Axis/tick/tooltip value formatter — R-multiples in R mode, dollars in $.
  const fmtAxis = (v) => asR
    ? ((v < 0 ? "−" : "") + Math.abs(v).toFixed(Math.abs(v) < 10 ? 1 : 0) + "R")
    : asCt ? fmt$short(v) + "/ct"
    : fmt$short(v);

  const toggle = (k) => {
    const next = new Set(hidden);
    next.has(k) ? next.delete(k) : next.add(k);
    setHidden(next);
  };

  // ── viewBox state for zoom/pan ──
  const baseVB = { x: 0, y: 0, w: w, h: h };
  const [vb, setVb] = usePg(baseVB);
  const vbRef = usePgR(vb);
  vbRef.current = vb;
  const svgRef = usePgR(null);
  const dragRef = usePgR({ active: false, lastX: 0, lastY: 0, moved: 0 });
  const touchRef = usePgR({ mode: 0, lastDist: 0, lastX: 0, lastY: 0 });
  const [isDragging, setIsDragging] = usePg(false);

  const ZOOM_MIN = 0.92, ZOOM_MAX = 8;
  const zoomLevel = w / vb.w;

  const zoomAt = (clientX, clientY, factor) => {
    const el = svgRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const u = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const v = Math.max(0, Math.min(1, (clientY - rect.top) / rect.height));
    const cur = vbRef.current;
    const minW = w / ZOOM_MAX, maxW = w / ZOOM_MIN;
    // clamp factor so we never blow past limits in one step
    const targetW = cur.w * factor;
    const clampedW = Math.max(minW, Math.min(maxW, targetW));
    if (clampedW === cur.w) return;
    const effFactor = clampedW / cur.w;
    const nw = clampedW;
    const nh = cur.h * effFactor;
    const nx = cur.x + (cur.w - nw) * u;
    const ny = cur.y + (cur.h - nh) * v;
    setVb({ x: nx, y: ny, w: nw, h: nh });
  };

  const zoomCenter = (factor) => {
    const el = svgRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    zoomAt(rect.left + rect.width / 2, rect.top + rect.height / 2, factor);
  };

  const reset = () => setVb(baseVB);

  // pan in client pixels → viewBox units
  const panBy = (dxPx, dyPx) => {
    const el = svgRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const dx = (dxPx / rect.width) * vbRef.current.w;
    const dy = (dyPx / rect.height) * vbRef.current.h;
    setVb((prev) => ({ ...prev, x: prev.x - dx, y: prev.y - dy }));
  };

  // attach native wheel/touch listeners (passive: false so we can preventDefault)
  usePgE(() => {
    const el = svgRef.current;
    if (!el) return;
    const onWheel = (e) => {
      e.preventDefault();
      // smooth gentle zoom; clamp per-tick magnitude to avoid trackpad surges
      const dy = Math.max(-40, Math.min(40, e.deltaY));
      const factor = Math.exp(dy * 0.0028); // ~3% per typical mouse-wheel tick
      zoomAt(e.clientX, e.clientY, factor);
    };
    const onTouchStart = (e) => {
      if (e.touches.length === 1) {
        touchRef.current = { mode: 1, lastX: e.touches[0].clientX, lastY: e.touches[0].clientY, lastDist: 0 };
      } else if (e.touches.length === 2) {
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        touchRef.current = {
          mode: 2,
          lastDist: Math.sqrt(dx*dx + dy*dy),
          lastX: (e.touches[0].clientX + e.touches[1].clientX) / 2,
          lastY: (e.touches[0].clientY + e.touches[1].clientY) / 2,
        };
      }
    };
    const onTouchMove = (e) => {
      e.preventDefault();
      const tr = touchRef.current;
      if (e.touches.length === 1 && tr.mode === 1) {
        const t = e.touches[0];
        panBy(t.clientX - tr.lastX, t.clientY - tr.lastY);
        tr.lastX = t.clientX; tr.lastY = t.clientY;
      } else if (e.touches.length === 2) {
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        const dist = Math.sqrt(dx*dx + dy*dy);
        const cx = (e.touches[0].clientX + e.touches[1].clientX) / 2;
        const cy = (e.touches[0].clientY + e.touches[1].clientY) / 2;
        if (tr.lastDist > 0) {
          let f = tr.lastDist / dist;
          // clamp per-frame pinch step
          f = Math.max(0.7, Math.min(1.4, f));
          if (f !== 1) zoomAt(cx, cy, f);
        }
        // also pan with two-finger center movement
        if (tr.lastX) panBy(cx - tr.lastX, cy - tr.lastY);
        tr.lastDist = dist; tr.lastX = cx; tr.lastY = cy; tr.mode = 2;
      }
    };
    const onTouchEnd = (e) => {
      if (e.touches.length === 0) touchRef.current = { mode: 0, lastDist: 0, lastX: 0, lastY: 0 };
      else if (e.touches.length === 1) {
        touchRef.current = { mode: 1, lastX: e.touches[0].clientX, lastY: e.touches[0].clientY, lastDist: 0 };
      }
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    el.addEventListener("touchstart", onTouchStart, { passive: false });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    el.addEventListener("touchend", onTouchEnd, { passive: false });
    el.addEventListener("touchcancel", onTouchEnd, { passive: false });
    return () => {
      el.removeEventListener("wheel", onWheel);
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
      el.removeEventListener("touchcancel", onTouchEnd);
    };
  }, []);

  // mouse drag (synthetic — fine to attach as React handlers)
  const onMouseDown = (e) => {
    if (e.button !== 0) return;
    dragRef.current = { active: true, lastX: e.clientX, lastY: e.clientY, moved: 0 };
    setIsDragging(true);
  };
  const onMouseMove = (e) => {
    if (!dragRef.current.active) return;
    const dx = e.clientX - dragRef.current.lastX;
    const dy = e.clientY - dragRef.current.lastY;
    dragRef.current.moved += Math.abs(dx) + Math.abs(dy);
    panBy(dx, dy);
    dragRef.current.lastX = e.clientX; dragRef.current.lastY = e.clientY;
  };
  const onMouseUp = () => {
    dragRef.current.active = false;
    // schedule a click-suppression flag clear
    setTimeout(() => { dragRef.current.moved = 0; }, 50);
    setIsDragging(false);
  };

  // bubble click should NOT fire after a drag-pan
  const handleBubbleClick = (s) => {
    if (dragRef.current.moved > 4) return;
    if (onSelect) {
      onSelect(s.id === selectedId ? null : s.id);
      return;
    }
    const klass = window.classOfStrat(s);
    location.hash = "#/strategies/" + klass;
  };

  // sort points so smaller bubbles draw on top.  Stable across viewBox
  // changes — sort key depends only on visible + sized.  MUST be declared
  // before any conditional return below to keep React's rules-of-hooks
  // happy across "empty → populated" transitions.
  const orderedPts = usePgM(
    () => [...visible].sort((a, b) => sVal(b) - sVal(a)),
    [visible, sized],
  );

  // Shared stack-dodge: when multiple strategies plot at the same (x,y)
  // — common at the origin for variants with no fills yet — fan them
  // around a small ring so each bubble is independently visible.  Was
  // computed TWICE per render (once for bubbles, once for labels);
  // unified here so labels and bubbles always agree and we don't pay
  // the O(N) bucket-walk twice.
  const stackPos = usePgM(() => {
    const buckets = new Map();
    for (const s of orderedPts) {
      const key = X(xVal(s)).toFixed(1) + "|" + Y(yVal(s)).toFixed(1);
      const arr = buckets.get(key);
      if (arr) arr.push(s);
      else buckets.set(key, [s]);
    }
    const out = new Map();
    for (const stack of buckets.values()) {
      if (stack.length <= 1) continue;
      const ringR = 18;
      for (let i = 0; i < stack.length; i++) {
        const a = (2 * Math.PI * i) / stack.length;
        out.set(stack[i].id, { dx: Math.cos(a) * ringR, dy: Math.sin(a) * ringR });
      }
    }
    return out;
  }, [orderedPts, xMin, xMax, yMax, xMode]);

  // survival threshold lines — one per account-type that has live deploys here
  const acctTypesPresent = Array.from(new Set(strats.map((s) => window.acctTypeOfStrat(s))));
  const survivalLines = window.ACCT_TYPES
    .filter((t) => t.survival && acctTypesPresent.includes(t.id))
    .map((t) => ({ y: t.survival, label: t.label, color: t.color }));

  // Empty-state — placed AFTER all hooks so the hook count is identical
  // between "loading" and "populated" renders (rules of hooks).  Per the
  // operator preference (goal 2026-05-24): ALWAYS render the shimmer
  // when strats is empty — never the "No live strategies — deploy on an
  // account to populate the map." copy.  Rationale: the prompt read as a
  // configuration error during the bootstrap window and confused
  // operators who could see accounts loading elsewhere on the page.  The
  // truly-zero-strategies state is rare enough on a working hub that the
  // shimmer is a better default; if strats stay empty long-term, the
  // shimmer just keeps looping (matches Overview AccountCard behaviour
  // when an account is still waiting for its first equity push).
  if (strats.length === 0) {
    return (
      <div className="rrmap-empty" style={{ padding: "20px var(--pad)" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ display: "flex", gap: 12, alignItems: "baseline" }}>
            <window.Skel kind="line-sm" w={260} />
            <window.Skel kind="line-sm" w={120} />
          </div>
          <window.Skel kind="line-sm" w={420} />
          <window.Skel h={460} style={{ display: "block", width: "100%", borderRadius: 4 }} />
          <div style={{ display: "flex", gap: 10 }}>
            <window.Skel kind="line-sm" w={100} />
            <window.Skel kind="line-sm" w={100} />
            <window.Skel kind="line-sm" w={100} />
            <window.Skel kind="line-sm" w={100} />
          </div>
        </div>
      </div>);
  }

  return (
    <>
      <div className="rrmap-h">
        <div className="t">Live strategies · risk × return map <b>{visible.length}</b><span className="muted" style={{ marginLeft: 6 }}>/ {strats.length}</span></div>
        <div className="sub">
          x · <b style={{ color: "var(--ink)" }}>{xMode === "weekly" ? "weekly P&L" : xMode === "alltime" ? "P&L · loaded (≤5k fills)" : "30d P&L"}</b> &nbsp;
          y · <b style={{ color: "var(--ink)" }}>|max drawdown|</b> &nbsp;
          size · <b style={{ color: "var(--ink)" }}>{sized}</b>
        </div>
        <div className="rrmap-mode">
          <button data-active={xMode === "weekly"} onClick={() => setXMode("weekly")}>Weekly</button>
          <button data-active={xMode === "monthly"} onClick={() => setXMode("monthly")}>30d</button>
          <button data-active={xMode === "alltime"} onClick={() => setXMode("alltime")}>All</button>
        </div>
        <div className="rrmap-mode" title="By strategy = pure strategy performance, merged across all accounts. By account = one bubble per (account, strategy).">
          <button data-active={group === "strategy"} onClick={() => setGroup("strategy")}>By strategy</button>
          <button data-active={group === "account"} onClick={() => setGroup("account")}>By account</button>
        </div>
        <div className="rrmap-mode" title="R = fully normalized (per risk-per-trade: strips size + instrument). $/ct = dollars per contract (strips size, keeps instrument $/pt). $ = raw dollars.">
          <button data-active={unit === "R"} onClick={() => setUnit("R")}>R</button>
          <button data-active={unit === "perct"} onClick={() => setUnit("perct")}>$/ct</button>
          <button data-active={unit === "$"} onClick={() => setUnit("$")}>$</button>
        </div>
        <div className="rrmap-mode">
          <button data-active={sized === "trades"} onClick={() => setSized("trades")}>Trades</button>
          <button data-active={sized === "contracts"} onClick={() => setSized("contracts")}>Contracts</button>
        </div>
        <div className="rrmap-mode">
          <button data-active={showSurvival} onClick={() => setShowSurvival(!showSurvival)} title="Toggle survival threshold guides">
            {showSurvival ? "✓ survival" : "survival"}
          </button>
        </div>
        <div className="rrmap-mode" title="Show variants the agent has attached but not started">
          <button data-active={showStopped} onClick={() => setShowStopped(!showStopped)}>
            {showStopped ? "✓ stopped" : "stopped"}
          </button>
        </div>
      </div>

      <div className="rrmap-canvas">
        <svg
          ref={svgRef}
          className={"rrmap-svg " + (isDragging ? "dragging" : "")}
          viewBox={`${vb.x} ${vb.y} ${vb.w} ${vb.h}`}
          preserveAspectRatio="xMidYMid meet"
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          onMouseLeave={onMouseUp}
          onDoubleClick={reset}>
        {/* y gridlines */}
        {yTicks.map((v, i) => (
          <g key={"yt" + i}>
            <line x1={padL} x2={w - padR} y1={Y(v)} y2={Y(v)} stroke="var(--rule)" strokeWidth="1" vectorEffect="non-scaling-stroke" />
            <text x={padL - 8} y={Y(v) + 3} textAnchor="end" className="rrmap-axis-l">{fmtAxis(v)}</text>
          </g>
        ))}
        {/* x gridlines */}
        {xTicks.map((v, i) => (
          <g key={"xt" + i}>
            <line x1={X(v)} x2={X(v)} y1={padT} y2={h - padB} stroke={v === 0 ? "var(--ink-2)" : "var(--rule)"} strokeWidth="1"
              opacity={v === 0 ? 0.55 : 1}
              vectorEffect="non-scaling-stroke" />
            <text x={X(v)} y={h - padB + 14} textAnchor="middle" className="rrmap-axis-l">{fmtAxis(v)}</text>
          </g>
        ))}

        {/* axis labels */}
        <text x={(padL + w - padR) / 2} y={h - 8} textAnchor="middle" className="rrmap-axis-l" style={{ letterSpacing: "0.12em", textTransform: "uppercase" }}>
          {(xMode === "weekly" ? "→ weekly P&L" : xMode === "alltime" ? "→ P&L · loaded fills only" : "→ 30d P&L") + (asR ? " (R)" : asCt ? " ($/ct)" : " ($)")}
        </text>
        <text x={14} y={(padT + h - padB) / 2} textAnchor="middle" className="rrmap-axis-l"
          transform={`rotate(-90 14 ${(padT + h - padB) / 2})`}
          style={{ letterSpacing: "0.12em", textTransform: "uppercase" }}>
          {asR ? "↑ max drawdown (R)" : asCt ? "↑ max drawdown ($/ct)" : "↑ max drawdown ($)"}
        </text>

        {/* survival threshold lines */}
        {showSurvival && unit === "$" && survivalLines.map((sl, i) => (
          <g key={"surv" + i}>
            <line x1={padL} x2={w - padR} y1={Y(sl.y)} y2={Y(sl.y)}
              className="rrmap-survival-line" vectorEffect="non-scaling-stroke" />
            <text x={padL + 6} y={Y(sl.y) - 4} className="rrmap-survival-l">
              ⚠ {sl.label} · MLL ${(sl.y / 1000).toFixed(1)}k
            </text>
          </g>
        ))}

        {/* efficiency reference lines (profit / risk = k) */}
        {refs.map((rf, i) => {
          const x0 = Math.max(0, xMin);
          const x1 = xMax;
          const y0 = x0 / rf.r;
          const y1 = x1 / rf.r;
          return (
            <g key={"ref" + i}>
              <line x1={X(x0)} y1={Y(Math.min(yMax * 1.05, Math.max(0, y0)))} x2={X(x1)} y2={Y(Math.min(yMax * 1.05, Math.max(0, y1)))}
                stroke="var(--muted-2)" strokeWidth={rf.emphasis ? "1.2" : "1"}
                strokeDasharray={rf.emphasis ? "1 4" : "1 6"} opacity={rf.emphasis ? 0.6 : 0.35} vectorEffect="non-scaling-stroke" />
              <text x={X(x1) - 4} y={Y(Math.min(yMax * 0.96, Math.max(0, y1))) - 3} textAnchor="end" className="rrmap-quad-label">
                profit/risk · {rf.label}
              </text>
            </g>);
        })}

        {/* quadrant labels */}
        <text x={w - padR - 6} y={h - padB - 8} textAnchor="end" className="rrmap-quad-label">↘ sweet spot · high profit · low risk</text>
        <text x={padL + 6} y={padT + 12} textAnchor="start" className="rrmap-quad-label">⚠ low profit · high risk</text>

        {/* points — uses the SHARED stackPos memo above (no duplicate work) */}
        {orderedPts.map((s) => {
          const cColor = colorOfStrat(s);
          const dodge = stackPos.get(s.id) || { dx: 0, dy: 0 };
          const cx = X(xVal(s)) + dodge.dx;
          const cy = Y(yVal(s)) + dodge.dy;
          const r = Math.max(5, Math.min(22, 4 + Math.sqrt(sVal(s)) * 1.5));
          // ring dash encodes runtime state:
          //   solid  = running     dashed = armed
          //   dotted = paused      sparse = stopped/breached (also faded)
          const stateRing = s.state === "armed" ? "5 3"
            : s.state === "paused" ? "2 3"
            : (!liveSet.has(s.state) ? "1 3" : "0");
          const isStopped = !liveSet.has(s.state);
          const isSel = s.id === selectedId;
          // fade stopped/breached so they read as "not running" without
          // being invisible — class color still identifies the family
          const groupOpacity = isStopped ? 0.42 : 1;
          const fillOp = isSel ? 0.36 : (isStopped ? 0.07 : 0.18);
          return (
            <g key={s.id} style={{ cursor: "pointer", opacity: groupOpacity }}
              data-state={s.state}
              onClick={() => handleBubbleClick(s)}>
              {isSel && (
                <>
                  <circle cx={cx} cy={cy} r={r + 8} fill="none" stroke={cColor} strokeWidth="1" opacity="0.4"
                    strokeDasharray="2 3" vectorEffect="non-scaling-stroke" />
                  <circle cx={cx} cy={cy} r={r + 3} fill="none" stroke={cColor} strokeWidth="1.4" opacity="0.6"
                    vectorEffect="non-scaling-stroke" />
                </>
              )}
              <circle cx={cx} cy={cy} r={r}
                fill={cColor} fillOpacity={fillOp}
                stroke={cColor} strokeWidth={isSel ? "2.4" : "1.6"}
                strokeDasharray={stateRing} vectorEffect="non-scaling-stroke" />
              <circle cx={cx} cy={cy} r={isSel ? "2.6" : "2"} fill={cColor} />
              <title>{s.name}{s.defId && s.defId !== s.name ? " (agent slug: " + s.defId + ")" : ""} · {s.acct} · {s.state} · {window.safeN(s.contracts, 0)}c · {window.safeN(s.trades, 0)}t · {xMode === "weekly" ? "7d" : xMode === "alltime" ? "all" : "30d"} {fmtAxis(pnlOf(s))} · maxDD {fmtAxis(ddOf(s))}{window.safeN(s.trades, 0) === 0 ? " · (no closed trades · " + (window.safeN(s.unrealized, 0) !== 0 ? "open unr " + window.fmt$(s.unrealized, 0) : "no fills attributed") + ")" : ""}</title>
            </g>);
        })}

        {/* labels — uses the SHARED stackPos memo (was rebuilt here for
            no reason).  labelInfo itself isn't memoized because the
            collision rectangles depend on selectedId-driven re-renders;
            collision detection is now linear, not O(N²). */}
        {(() => {
          const lblLineH = 12;
          // Spatial grid for O(N) collision check: bucket placed labels
          // by 80px tiles, only test against labels in the same/adjacent
          // tile.  Replaces the prior `placed.some(...)` walk that was
          // O(P²) cumulative.
          const TILE = 80;
          const grid = new Map();
          const keyOfRect = (x, y) => `${Math.floor(x / TILE)}|${Math.floor(y / TILE)}`;
          const collides = (rect) => {
            for (let gx = Math.floor(rect.x0 / TILE) - 1; gx <= Math.floor(rect.x1 / TILE) + 1; gx++) {
              for (let gy = Math.floor(rect.y0 / TILE) - 1; gy <= Math.floor(rect.y1 / TILE) + 1; gy++) {
                const arr = grid.get(`${gx}|${gy}`);
                if (!arr) continue;
                for (const p of arr) {
                  if (!(rect.x1 < p.x0 || rect.x0 > p.x1 || rect.y1 < p.y0 || rect.y0 > p.y1)) return true;
                }
              }
            }
            return false;
          };
          const placeRect = (rect) => {
            const arr = grid.get(keyOfRect(rect.x0, rect.y0)) || [];
            arr.push(rect);
            grid.set(keyOfRect(rect.x0, rect.y0), arr);
          };
          const labelInfo = orderedPts.map((s) => {
            const dodge = stackPos.get(s.id) || { dx: 0, dy: 0 };
            const cx = X(xVal(s)) + dodge.dx;
            const cy = Y(yVal(s)) + dodge.dy;
            const r = Math.max(5, Math.min(22, 4 + Math.sqrt(sVal(s)) * 1.5));
            // Contract count only when the agent actually reported one —
            // "·0c" read as "sized at zero contracts" on every bubble
            // (the agent currently emits an empty strategyContracts map).
            const _ct = window.safeN(s.aggR ? s.contractsSum : s.contracts, 0);
            const text = (s.name || s.id || "?") + " · " + (s.acct || "—")
              + (_ct > 0 ? "·" + _ct + "c" : "");
            const tw = Math.max(64, text.length * 5.3);
            const candidates = [
              { lx: cx, ly: cy + r + 10, anchor: "middle" },
              { lx: cx, ly: cy - r - 4,  anchor: "middle" },
              { lx: cx + r + 6, ly: cy + 3, anchor: "start"  },
              { lx: cx - r - 6, ly: cy + 3, anchor: "end"    }];
            let chosen = null;
            for (const c of candidates) {
              const x0 = c.anchor === "middle" ? c.lx - tw / 2 : c.anchor === "start" ? c.lx : c.lx - tw;
              const rect = { x0: x0 - 2, y0: c.ly - lblLineH, x1: x0 + tw + 2, y1: c.ly + 2 };
              if (collides(rect)) continue;
              if (rect.x0 > padL - 30 && rect.x1 < w - padR + 30 && rect.y0 > padT - 6 && rect.y1 < h - padB + 4) {
                chosen = c;
                placeRect(rect);
                break;
              }
            }
            return { s, chosen, cx, cy, r, text };
          });
          return labelInfo.map(({ s, chosen, cx, cy, r, text }) => {
            if (!chosen) {
              return <circle key={"hide" + s.id} cx={cx + r + 3} cy={cy} r="1" fill="var(--muted-2)" />;
            }
            return (
              <text key={"lbl" + s.id} x={chosen.lx} y={chosen.ly} textAnchor={chosen.anchor}
                className="rrmap-pt-label" pointerEvents="none">{text}</text>);
          });
        })()}
        </svg>

        {/* zoom controls */}
        <div className="rrmap-zoom">
          <button onClick={() => zoomCenter(0.82)} title="Zoom in">＋</button>
          <div className="lvl tnum">{zoomLevel.toFixed(2)}×</div>
          <button onClick={() => zoomCenter(1.22)} title="Zoom out">−</button>
          <button onClick={reset} title="Reset view">⌖</button>
        </div>
      </div>

      <div className="rrmap-legend">
        {classes.filter((c) => (klassCount[c.id] || 0) > 0 || (window.STRATEGY_CLASSES || []).some(x => x.id === c.id)).map((c) => {
          const on = !hidden.has(c.id);
          const cnt = klassCount[c.id] || 0;
          return (
            <span key={c.id} className="item" style={{ color: c.color }} data-on={on}
              onClick={() => toggle(c.id)} title={c.desc || c.id}>
              <span className="swatch" />
              <span style={{ color: on ? "var(--ink)" : "var(--muted)" }}>{c.label}</span>
              <span className="cnt">· {cnt}</span>
            </span>);

        })}
        <span style={{ flex: 1 }} />
        <span style={{ color: "var(--muted-2)", fontSize: 10.5, letterSpacing: "0.06em" }}>
          ring · solid running · dashed armed · dotted paused · faded stopped
        </span>
      </div>
      {variantList.length > 0 && (
        <div className="rrmap-legend" style={{ marginTop: 4 }}>
          <span style={{ color: "var(--muted-2)", fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase", marginRight: 6 }}>variants</span>
          {variantList.map((v) => {
            const vkey = "var:" + v.defId;
            const on = !hidden.has(vkey);
            return (
              <span key={vkey} className="item" style={{ color: v.color }} data-on={on}
                onClick={() => toggle(vkey)} title={`${v.defId} (class: ${v.klass}) — ${v.count} deploy${v.count === 1 ? "" : "s"}`}>
                <span className="swatch" />
                <span style={{ color: on ? "var(--ink)" : "var(--muted)" }}>{v.defId}</span>
                <span className="cnt">· {v.count}</span>
              </span>);
          })}
        </div>
      )}
    </>);

}

/* ── Per-class aggregated equity curves (sidebar of map) ──── */

function StrategyClassCurves({ strats, layout }) {
  const classes = window.STRATEGY_CLASSES;
  const groups = classes.map((c) => {
    const members = strats.filter((s) => window.classOfStrat(s) === c.id);
    return { ...c, members };
  }).filter((g) => g.members.length > 0);

  // Empty-state — same operator preference as StrategyRiskMap: render a
  // shimmer skeleton rather than the "no deployments" copy, so a fresh
  // page load doesn't briefly flash that prompt during bootstrap.  Three
  // skeleton curve cards approximate the layout of the real strip.
  if (groups.length === 0) {
    const horizontalEmpty = layout === "horizontal";
    return (
      <div className={"rrmap-curves" + (horizontalEmpty ? " horizontal" : "")}
           style={{ padding: "12px var(--pad)" }}>
        {!horizontalEmpty && (
          <div className="rrmap-h" style={{ marginBottom: 8 }}>
            <window.Skel kind="line-sm" w={220} />
            <div style={{ height: 4 }} />
            <window.Skel kind="line-sm" w={300} />
          </div>
        )}
        <div style={{ display: horizontalEmpty ? "grid" : "block",
                      gridTemplateColumns: horizontalEmpty ? "repeat(3, 1fr)" : undefined,
                      gap: 12 }}>
          {Array.from({ length: 3 }).map((_, i) => (
            <div className="rrmap-curve" key={i} style={{ padding: "8px 0" }}>
              <div className="rrmap-curve-h" style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                <window.Skel kind="line-sm" w={140} />
                <window.Skel kind="line-sm" w={60} />
              </div>
              <window.Skel h={56} style={{ display: "block", width: "100%", borderRadius: 4 }} />
            </div>
          ))}
        </div>
      </div>);
  }

  const N = 60;
  const horizontal = layout === "horizontal";

  return (
    <div className={"rrmap-curves" + (horizontal ? " horizontal" : "")}>
      {!horizontal && (
        <div className="rrmap-h" style={{ marginBottom: 0 }}>
          <div className="t">Running classes · cumulative <b>{groups.length}</b></div>
          <div className="sub">aggregated equity per class · last {N}d</div>
        </div>
      )}
      {groups.map((g) => {
        // aggregate live strats in this class, anchored to total 30d
        const pair = window.aggregateCurves(g.members, N);
        const curve = pair.live;
        const last = curve.at(-1) || 0;
        const peak = curve.reduce((m, v) => Math.max(m, v), -Infinity);
        let maxDD = 0;
        let runMax = -Infinity;
        for (const v of curve) {
          if (v > runMax) runMax = v;
          if (runMax - v > maxDD) maxDD = runMax - v;
        }
        const lo = Math.min(...curve, 0);
        const hi = Math.max(...curve, last, 1);
        const span = (hi - lo) || 1;
        const sw = 360, sh = 56, padTo = 6, padBo = 6, padLe = 4, padRi = 28;
        const x = (i) => padLe + i / (curve.length - 1) * (sw - padLe - padRi);
        const y = (v) => padTo + (1 - (v - lo) / span) * (sh - padTo - padBo);
        const dPath = curve.map((v, i) => (i ? "L" : "M") + x(i).toFixed(1) + " " + y(v).toFixed(1)).join(" ");
        const dArea = dPath + ` L ${x(curve.length - 1).toFixed(1)} ${(sh - padBo).toFixed(1)} L ${x(0).toFixed(1)} ${(sh - padBo).toFixed(1)} Z`;

        // benchmark sum (research promise) for divergence number
        const benchSum = g.members.reduce((s, x) => {
          const b = window.STRATEGY_DEFS.find((d) => d.id === x.defId)?.bench;
          return s + (b ? b.eR * x.riskPerTrade * Math.max(x.trades, 20) : 0);
        }, 0);
        const div = benchSum ? (last - benchSum) / Math.abs(benchSum) * 100 : 0;

        return (
          <div className="rrmap-curve" key={g.id}>
            <div className="rrmap-curve-h">
              <span className="name" style={{ color: g.color }}>
                <span className="swatch" />
                <span style={{ color: "var(--ink)" }}>{g.label}</span>
                <span className="desc">· {g.desc} · {g.members.length} live</span>
              </span>
              <span className="nums">
                <span>30d <b className={"tnum " + (last >= 0 ? "pos" : "neg")}>{window.fmt$(last, 0)}</b></span>
                <span>maxDD <b className="tnum neg">−{window.fmt$(maxDD, 0).replace("−", "").replace("$", "$")}</b></span>
                <span className={"tnum " + (div >= 0 ? "pos" : "neg")}>{div >= 0 ? "↑" : "↓"} {Math.abs(div).toFixed(0)}% vs R</span>
              </span>
            </div>
            <svg viewBox={`0 0 ${sw} ${sh}`} preserveAspectRatio="none">
              <line x1={padLe} x2={sw - padRi} y1={y(0)} y2={y(0)} stroke="var(--rule-2)" strokeDasharray="1 4" strokeWidth="1" vectorEffect="non-scaling-stroke" />
              <path d={dArea} fill={g.color} fillOpacity="0.10" />
              <path d={dPath} fill="none" stroke={g.color} strokeWidth="1.9" vectorEffect="non-scaling-stroke" />
              <circle cx={x(curve.length - 1)} cy={y(last)} r="2.6" fill={g.color} />
              <text x={sw - padRi + 4} y={y(last) + 3} className="rrmap-axis-l" style={{ fill: g.color, fontSize: 10 }}>{window.fmt$(last, 0)}</text>
            </svg>
            <div style={{ fontFamily: "var(--mono)", fontSize: 10.5, color: "var(--muted)", marginTop: 4, display: "flex", justifyContent: "space-between" }}>
              <span>{g.members.map((m) => m.name).join(" · ")}</span>
              <span className="tnum">{g.members.reduce((s, x) => s + x.trades, 0)} trades · {(g.members.reduce((s, x) => s + x.wr * x.trades, 0) / Math.max(1, g.members.reduce((s, x) => s + x.trades, 0)) * 100).toFixed(1)}% WR</span>
            </div>
          </div>);

      })}
    </div>);

}

/* ── Strategy-class siblings comparison (class page) ─────── */

function ClassSiblingsComparison({ klass, currentDefId }) {
  // every def in this class
  const defs = window.STRATEGY_DEFS.filter((d) => d.klass === klass);
  // for each def, aggregate live deployments
  const series = defs.map((d) => {
    const live = window.STRATS.filter((s) => s.defId === d.id && window.LIVE_STATES.has(s.state));
    return { def: d, live };
  }).filter((row) => row.live.length > 0);

  const [hidden, setHidden] = usePg(() => new Set());

  if (series.length === 0) {
    return (
      <div className="cls-cmp-empty">
        No live variations in this class. Deploy a recipe to start the comparison.
      </div>);
  }

  const N = 60;

  // build an aggregated equity for each variation
  const enriched = series.map((row, idx) => {
    const pair = window.aggregateCurves(row.live, N);
    // metrics aggregated
    const totalTrades = row.live.reduce((s, x) => s + x.trades, 0) || 1;
    const wr = row.live.reduce((s, x) => s + x.wr * x.trades, 0) / totalTrades;
    const pf = row.live.reduce((s, x) => s + x.pf * x.trades, 0) / totalTrades;
    const eR = row.live.reduce((s, x) => s + x.eR * x.trades, 0) / totalTrades;
    const pnl30 = row.live.reduce((s, x) => s + x.pnl30, 0);
    const pnlW = row.live.reduce((s, x) => s + (x.pnl7 || 0), 0);
    // max DD across aggregated curve
    let mdd = 0, runMax = -Infinity;
    for (const v of pair.live) {
      if (v > runMax) runMax = v;
      if (runMax - v > mdd) mdd = runMax - v;
    }
    // color: index into a small palette per variation, but keep class hue
    const baseHue = { orb: 250, alpha: 155, mr: 75, other: 215 }[row.def.klass] || 215;
    const variants = [
      `oklch(0.62 0.14 ${baseHue})`,
      `oklch(0.48 0.16 ${baseHue + 16})`,
      `oklch(0.74 0.12 ${baseHue - 16})`,
      `oklch(0.55 0.18 ${baseHue + 32})`,
      `oklch(0.68 0.10 ${baseHue - 32})`];
    const color = row.def.id === currentDefId ? `oklch(0.46 0.18 ${baseHue})` : variants[idx % variants.length];
    return { ...row, curve: pair.live, wr, pf, eR, pnl30, pnlW, mdd, color, isSelf: row.def.id === currentDefId, totalTrades };
  });

  const visible = enriched.filter((r) => !hidden.has(r.def.id));

  const w = 760, h = 240, padL = 60, padR = 22, padT = 14, padB = 30;
  const all = visible.flatMap((r) => r.curve).concat([0]);
  let lo = Math.min(...all), hi = Math.max(...all);
  if (lo === hi) hi = lo + 1;
  const span = (hi - lo) || 1;
  lo -= span * 0.06; hi += span * 0.12;
  const len = enriched[0]?.curve.length || N;
  const X = (i) => padL + i / Math.max(1, len - 1) * (w - padL - padR);
  const Y = (v) => padT + (1 - (v - lo) / (hi - lo)) * (h - padT - padB);

  const yTicks = window.niceTicks(lo, hi, 4);

  const fmt$short = (v) => {
    const a = Math.abs(v);
    if (a >= 1000) return (v < 0 ? "−" : "") + "$" + (a / 1000).toFixed(1) + "k";
    return (v < 0 ? "−" : "") + "$" + a.toFixed(0);
  };

  // ranking helpers
  const bestPnl = Math.max(...enriched.map((r) => r.pnl30));
  const bestPf = Math.max(...enriched.map((r) => r.pf));
  const bestWr = Math.max(...enriched.map((r) => r.wr));
  const bestER = Math.max(...enriched.map((r) => r.eR));
  const bestDD = Math.min(...enriched.map((r) => r.mdd));

  const toggle = (id) => {
    const next = new Set(hidden);
    next.has(id) ? next.delete(id) : next.add(id);
    setHidden(next);
  };

  return (
    <div className="cls-cmp">
      <div className="cls-cmp-main">
        <div className="cls-cmp-legend">
          {enriched.map((r) => (
            <span key={r.def.id} className="item"
              data-self={r.isSelf} data-on={!hidden.has(r.def.id)}
              style={{ color: r.color }}
              onClick={() => toggle(r.def.id)}>
              <span className="sw" />
              <span style={{ color: r.isSelf ? "var(--ink)" : "var(--ink-2)" }}>{r.def.name}</span>
              <span className="meta">· {r.live.length} deploy{r.live.length === 1 ? "" : "s"}</span>
              <span className={"delta tnum " + (r.pnl30 >= 0 ? "pos" : "neg")}>{window.fmt$(r.pnl30, 0)}</span>
            </span>
          ))}
        </div>
        <svg className="cls-cmp-svg" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" style={{ height: h }}>
          {yTicks.map((v, i) => (
            <g key={i}>
              <line x1={padL} x2={w - padR} y1={Y(v)} y2={Y(v)} stroke="var(--rule)" strokeWidth="1" vectorEffect="non-scaling-stroke" />
              <text x={padL - 8} y={Y(v) + 3} textAnchor="end" className="rrmap-axis-l">{fmtAxis(v)}</text>
            </g>
          ))}
          {lo < 0 && hi > 0 && (
            <line x1={padL} x2={w - padR} y1={Y(0)} y2={Y(0)} stroke="var(--ink)" strokeDasharray="1 4" opacity="0.5" vectorEffect="non-scaling-stroke" />
          )}
          {/* x axis date labels */}
          {Array.from({ length: 6 }).map((_, i) => {
            const ix = Math.floor((len - 1) * (i / 5));
            const d = new Date(); d.setDate(d.getDate() - (len - 1 - ix));
            return (
              <text key={"xl" + i} x={X(ix)} y={h - 8} textAnchor="middle" className="rrmap-axis-l">
                {d.toLocaleDateString("en-US", { month: "short", day: "numeric" })}
              </text>);
          })}
          {/* curves (drawn back-to-front, self last for emphasis) */}
          {visible.slice().sort((a, b) => Number(a.isSelf) - Number(b.isSelf)).map((r) => {
            const path = r.curve.map((v, i) => (i ? "L" : "M") + X(i).toFixed(1) + " " + Y(v).toFixed(1)).join(" ");
            const last = r.curve.at(-1) || 0;
            return (
              <g key={r.def.id}>
                <path d={path} fill="none" stroke={r.color}
                  strokeWidth={r.isSelf ? "2.4" : "1.4"}
                  strokeDasharray={r.isSelf ? "0" : "0"}
                  opacity={r.isSelf ? 1 : 0.78}
                  vectorEffect="non-scaling-stroke" />
                <circle cx={X(r.curve.length - 1)} cy={Y(last)} r={r.isSelf ? "3.4" : "2.4"} fill={r.color} />
                <text x={X(r.curve.length - 1) + 5} y={Y(last) + 3} className="rrmap-axis-l"
                  style={{ fill: r.color, fontSize: 10, fontWeight: r.isSelf ? 600 : 400 }}>
                  {window.fmt$(last, 0)}
                </text>
              </g>);
          })}
        </svg>
      </div>
      <div className="cls-cmp-side">
        <div className="rrmap-h" style={{ marginBottom: 6 }}>
          <div className="t">Variations · ranked</div>
          <div className="sub">{window.classMeta(klass).label} class · {enriched.length} live</div>
        </div>
        <table className="cls-cmp-table">
          <thead>
            <tr>
              <th>Variation</th>
              <th>WR</th>
              <th>PF</th>
              <th>E[R]</th>
              <th>30d</th>
              <th>maxDD</th>
            </tr>
          </thead>
          <tbody>
            {[...enriched].sort((a, b) => b.pnl30 - a.pnl30).map((r) => (
              <tr key={r.def.id} data-self={r.isSelf}
                className="row-link"
                onClick={() => { location.hash = "#/strategies/" + r.def.id; }}>
                <td>
                  <span className="nm" style={{ color: r.color }}>
                    <span className="sw" />
                    <span style={{ color: r.isSelf ? "var(--ink)" : "var(--ink-2)" }}>{r.def.name}</span>
                  </span>
                  <div className="deploys-meta" style={{ color: "var(--muted)", fontSize: 10, marginTop: 2 }}>
                    {r.live.map((s) => s.acct + "·" + s.contracts + "c").join(" · ")}
                  </div>
                </td>
                <td className={r.wr === bestWr ? "best" : ""}>{(r.wr * 100).toFixed(1)}%</td>
                <td className={r.pf === bestPf ? "best" : ""}>{r.pf.toFixed(2)}</td>
                <td className={r.eR === bestER ? "best" : ""}>{window.fmtP(r.eR, 2)}</td>
                <td className={(r.pnl30 === bestPnl ? "best " : "") + (r.pnl30 >= 0 ? "pos" : "neg")}>{window.fmt$(r.pnl30, 0)}</td>
                <td className={(r.mdd === bestDD ? "best " : "") + "neg"}>−{window.fmt$(r.mdd, 0).replace("−", "").replace("$", "$")}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ fontFamily: "var(--mono)", fontSize: 10.5, color: "var(--muted)", marginTop: 10, lineHeight: 1.55 }}>
          <b style={{ color: "var(--ink)" }}>Read:</b> aggregated cumulative P&L of all live deployments per variation.
          Bold row = this strategy. Live only — research-only recipes are excluded.
        </div>
      </div>
    </div>);

}

/* ── Overview ─────────────────────────────────────────────── */

function CriticalAlertPanel() {
  // window.ALERTS holds raw DAL rows; normalize so we can rely on the same
  // sev/ack/head/msg fields the alerts page uses.
  const raw = window.ALERTS || [];
  const all = (window.normalizeAlertRow ? raw.map(window.normalizeAlertRow) : raw);
  const sevRank = { crit: 3, warn: 2, info: 1 };
  const counts = {
    crit: all.filter((a) => a.sev === "crit" && !a.ack).length,
    warn: all.filter((a) => a.sev === "warn" && !a.ack).length,
    info: all.filter((a) => a.sev === "info" && !a.ack).length,
  };
  // "Active alert" is only meant to surface genuinely unattended,
  // untouched crits — not warns, not anything the operator has already
  // acked.  Warns still feed the counts row below.
  const openCrits = all.filter((a) => !a.ack && a.sev === "crit");
  // Newest first — alert_pk is monotonic in the DAL so it doubles as a
  // recency proxy when ts_us isn't normalized onto the row.
  const top = openCrits.sort((a, b) => (b.alert_pk || 0) - (a.alert_pk || 0))[0];
  const showFull = !!top;

  // Minimal mode: when no crit/warn — just a single-line counts banner
  if (!showFull) {
    return (
      <div className="right-block alert-mini compact" data-sev="ok">
        <a href="#/alerts" className="al-compact mono" onClick={() => { location.hash = "#/alerts"; }}>
          <span className="al-compact-t">ALERTS</span>
          <span className="al-cnt crit"><span className="d" /> {counts.crit} crit</span>
          <span className="al-cnt warn"><span className="d" /> {counts.warn} warn</span>
          <span className="al-cnt info"><span className="d" /> {counts.info} info</span>
          <span className="al-compact-arrow">→</span>
        </a>
      </div>);
  }

  return (
    <div className="right-block alert-mini" data-sev={top.sev}>
      <div className="al-mini-h">
        <h3 style={{ margin: 0 }}>Active alert</h3>
        <div className="al-mini-counts mono">
          {counts.crit > 0 && <span className="al-cnt crit"><span className="d" /> {counts.crit} crit</span>}
          {counts.warn > 0 && <span className="al-cnt warn"><span className="d" /> {counts.warn} warn</span>}
        </div>
      </div>
      <a className="al-mini-body" href={"#/alerts/" + encodeURIComponent(top.id)}
        onClick={(e) => { e.preventDefault(); location.hash = "#/alerts/" + encodeURIComponent(top.id); }}>
        <div className="al-mini-row">
          <span className={"al-sev " + top.sev}>{top.sev.toUpperCase()}</span>
          <span className="al-source mono muted">{top.sourceRaw || top.source || top.bucket || ""}{top.account ? " · " + top.account : ""} · #{top.id}</span>
          <span className="al-age mono muted">{top.age}</span>
        </div>
        <div className="al-mini-head" title={top.msg}>{top.head}</div>
        <div className="al-mini-msg mono">{top.msg}</div>
      </a>
      <a href="#/alerts" className="al-mini-foot mono"
        onClick={(e) => { e.preventDefault(); location.hash = "#/alerts"; }}>
        View all alerts <span style={{ marginLeft: 4 }}>→</span>
      </a>
    </div>);
}


function SelectedStrategyPanel({ strat, onClose }) {
  if (!strat) return null;
  const def = window.STRATEGY_DEFS.find((d) => d.id === strat.defId);
  const klass = def ? def.klass : "other";
  const kmeta = window.classMeta(klass);
  const acct = (window.ACCOUNTS || []).find((a) => a.id === strat.acct);
  const atype = window.acctTypeMeta(acct ? acct.kind : "other");
  const inst = (window.INSTANCES || []).find((i) => i.id === strat.instance);
  const cfg = inst ? (window.CONFIGS || []).find((c) => c.id === inst.config) : null;
  const positions = inst ? ((window.OPEN_POSITIONS || {})[inst.id] || []).filter((p) => p.strat === strat.id) : [];
  const linkedResearch = def ? (window.RESEARCH || []).filter((r) => Array.isArray(r.produces) && r.produces.includes(def.id)) : [];

  const [tab, setTab] = usePg("params");

  // Safe numeric reads — STRATS rows can land with null pf (no
  // losing trades yet) / undefined sharpe / dd; toFixed on null
  // crashes the whole page.  Centralize the coercion so each
  // render slot is independent of the data path.
  const wrN     = window.safeN(strat.wr, NaN);
  const pfN     = window.safeN(strat.pf, NaN);
  const eRN     = window.safeN(strat.eR, NaN);
  const sharpeN = window.safeN(strat.sharpe, NaN);
  const ddN     = window.safeN(strat.dd, 0);
  const pnl30N  = window.safeN(strat.pnl30, 0);
  const pnl7N   = window.safeN(strat.pnl7, 0);
  const todayN  = window.safeN(strat.today, 0);
  const tradesN = window.safeN(strat.trades, 0);
  const riskN   = window.safeN(strat.riskPerTrade, 0);
  const contractsN = window.safeN(strat.contracts, 0);

  // sparkline (re-anchored to strat.pnl30)
  const seedName = (strat.name || strat.id || "x");
  const seedAcct = (strat.acct || "x");
  const seed = seedName.length * 7 + seedAcct.charCodeAt(0) * 3;
  const rawEq = window.genEquity(seed).slice(-30);
  const last = rawEq.at(-1) || 1;
  const target = pnl30N;
  const eq = rawEq.map((v) => last !== 0 ? v / last * target : 0);

  const benchDelta = def && def.bench ? {
    wr: Number.isFinite(wrN) ? wrN - window.safeN(def.bench.wr, 0) : NaN,
    pf: Number.isFinite(pfN) ? pfN - window.safeN(def.bench.pf, 0) : NaN,
    eR: Number.isFinite(eRN) ? eRN - window.safeN(def.bench.eR, 0) : NaN,
  } : null;

  // DD vs survival headroom
  const survival = atype.survival;
  const ddPct = survival ? Math.min(1.05, Math.abs(ddN) / survival) : 0;

  // sparkline path
  const sw = 320, sh = 44, padTo = 4, padBo = 4, padLe = 2, padRi = 2;
  const lo = Math.min(...eq, 0), hi = Math.max(...eq, 0.01);
  const span = (hi - lo) || 1;
  const x = (i) => padLe + i / (eq.length - 1) * (sw - padLe - padRi);
  const y = (v) => padTo + (1 - (v - lo) / span) * (sh - padTo - padBo);
  const pathD = eq.map((v, i) => (i ? "L" : "M") + x(i).toFixed(1) + " " + y(v).toFixed(1)).join(" ");
  const areaD = pathD + ` L ${x(eq.length - 1).toFixed(1)} ${(sh - padBo).toFixed(1)} L ${x(0).toFixed(1)} ${(sh - padBo).toFixed(1)} Z`;
  const eqColor = target >= 0 ? "var(--pos)" : "var(--neg)";

  return (
    <div className="sel-panel" data-state={strat.state}>
      <div className="sel-head">
        <div className="sel-kicker">
          <span className="sel-led" data-state={strat.state} />
          <span style={{ color: kmeta.color }}>● {kmeta.label}</span>
          <window.StateBadge state={strat.state} size="sm" />
        </div>
        <button className="sel-close" onClick={onClose} title="Clear selection">×</button>
      </div>

      <div className="sel-title">
        <a href={"#/strategies/" + (def ? def.id : klass)} className="sel-name">{strat.name}</a>
        <div className="sel-sub mono">
          {strat.aggR ? (
            <>
              <span className="sel-acct-badge" style={{ color: "var(--muted)" }}>MERGED</span>
              <span className="sel-acct-name">{(strat.accounts || []).join(" · ")}</span>
              <span>· {strat.trades}t · pure strategy (all accounts)</span>
            </>
          ) : (
            <>
              <span className="sel-acct-badge" style={{ color: atype.color }}>{atype.label.toUpperCase()}</span>
              <a href={"#/accounts/" + strat.acct} className="sel-acct-name">{acct ? acct.label : strat.acct}</a>
              <span>· {strat.symbol || (def && def.symbol) || "—"}{strat.venue ? "." + strat.venue : ""} · {contractsN}c · risk {window.fmt$(riskN, 0)}</span>
            </>
          )}
        </div>
      </div>

      {/* hero stats */}
      <div className="sel-hero">
        <div className="sel-hero-col">
          <div className="lbl">30d P&L</div>
          <div className={"v mono tnum " + (pnl30N >= 0 ? "pos" : "neg")}>{window.fmt$(pnl30N, 0)}</div>
          <div className="sub mono">wk {window.fmt$(pnl7N, 0)}</div>
        </div>
        <div className="sel-hero-col">
          <div className="lbl">Today</div>
          <div className={"v mono tnum " + (todayN === 0 ? "muted" : todayN > 0 ? "pos" : "neg")}>
            {todayN === 0 ? "—" : window.fmt$(todayN, 0)}
          </div>
          <div className="sub mono">{window.cleanSig(strat.lastSig) || "—"}</div>
        </div>
        <div className="sel-hero-col">
          <div className="lbl">Max DD</div>
          <div className="v mono tnum neg">−{window.fmt$(Math.abs(ddN), 0).replace("−", "").replace("$", "$")}</div>
          {survival ? (
            <div className="sub mono">
              <span className={ddPct > 0.8 ? "neg" : ddPct > 0.5 ? "warn" : "muted"}>{(ddPct * 100).toFixed(0)}%</span>
              <span className="muted"> of MLL</span>
            </div>
          ) : (
            <div className="sub mono muted">no cap</div>
          )}
        </div>
      </div>

      {/* sparkline */}
      <div className="sel-spark">
        <svg viewBox={`0 0 ${sw} ${sh}`} preserveAspectRatio="none">
          <line x1={padLe} x2={sw - padRi} y1={y(0)} y2={y(0)} stroke="var(--rule-2)" strokeWidth="1" strokeDasharray="1 4" vectorEffect="non-scaling-stroke" />
          <path d={areaD} fill={eqColor} opacity="0.10" />
          <path d={pathD} fill="none" stroke={eqColor} strokeWidth="1.6" vectorEffect="non-scaling-stroke" />
          <circle cx={x(eq.length - 1)} cy={y(eq.at(-1) || 0)} r="2" fill={eqColor} />
        </svg>
      </div>

      {/* live vs bench — single compact row */}
      <div className="sel-perf">
        <div className="sel-perf-cell">
          <span className="lbl">WR</span>
          <span className="v mono tnum">{Number.isFinite(wrN) ? (wrN * 100).toFixed(1) + "%" : "—"}</span>
          {benchDelta && Number.isFinite(benchDelta.wr) && <span className={"d mono tnum " + (benchDelta.wr >= 0 ? "pos" : "neg")}>
            {benchDelta.wr >= 0 ? "+" : ""}{(benchDelta.wr * 100).toFixed(1)}pp
          </span>}
        </div>
        <div className="sel-perf-cell">
          <span className="lbl">PF</span>
          <span className="v mono tnum">{!Number.isFinite(pfN) ? (pfN === Infinity ? "∞" : "—") : pfN.toFixed(2)}</span>
          {benchDelta && Number.isFinite(benchDelta.pf) && <span className={"d mono tnum " + (benchDelta.pf >= 0 ? "pos" : "neg")}>
            {benchDelta.pf >= 0 ? "+" : ""}{benchDelta.pf.toFixed(2)}
          </span>}
        </div>
        <div className="sel-perf-cell">
          <span className="lbl">E[R]</span>
          <span className={"v mono tnum " + (eRN >= 0 ? "pos" : "neg")}>{window.fmtP(eRN, 2)}</span>
          {benchDelta && Number.isFinite(benchDelta.eR) && <span className={"d mono tnum " + (benchDelta.eR >= 0 ? "pos" : "neg")}>
            {benchDelta.eR >= 0 ? "+" : ""}{benchDelta.eR.toFixed(2)}
          </span>}
        </div>
        <div className="sel-perf-cell">
          <span className="lbl">Sharpe</span>
          <span className="v mono tnum">{window.safeFix(sharpeN, 2)}</span>
          <span className="d mono muted">{tradesN} trades</span>
        </div>
      </div>

      {/* tabs */}
      <div className="sel-tabs">
        <button data-active={tab === "params"}    onClick={() => setTab("params")}>Params</button>
        <button data-active={tab === "positions"} onClick={() => setTab("positions")}>Positions{positions.length > 0 ? " · " + positions.length : ""}</button>
        <button data-active={tab === "config"}    onClick={() => setTab("config")}>Config</button>
        <button data-active={tab === "lineage"}   onClick={() => setTab("lineage")}>Lineage</button>
      </div>

      <div className="sel-tab-body">
        {tab === "params" && (
          <div className="sel-params-tab">
            {strat.params ? (
              Object.entries(strat.params).map(([group, ps]) => (
                <div key={group} className="sel-params-group">
                  <div className="sel-params-group-h mono">{group}</div>
                  <div className="sel-params-list">
                    {Object.entries(ps).map(([k, v]) => (
                      <div key={k} className="sel-param-row mono">
                        <span className="muted">{k}</span>
                        <span className="tnum">{typeof v === "boolean" ? (v ? "true" : "false") : String(v)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))
            ) : <div className="sel-empty-tab mono">No params loaded.</div>}
          </div>
        )}

        {tab === "positions" && (
          positions.length > 0 ? (
            <div>
              {positions.map((p, i) => {
                const side = (p.side || "").toString();
                return (
                  <div key={i} className="sel-pos-row mono">
                    <div className="sel-pos-l">
                      <span className={"sel-pos-side " + (side === "long" ? "pos" : "neg")}>{side ? side.toUpperCase() : "—"}</span>
                      <span className="sel-pos-sym">{p.sym || "—"}</span>
                      <span className="sel-pos-qty muted">{window.safeN(p.qty, 0)}c</span>
                    </div>
                    <div className="sel-pos-px mono tnum">
                      <span>@ {window.safeFix(p.entry, 2)} → {window.safeFix(p.mark, 2)}</span>
                    </div>
                    <div className={"sel-pos-pnl mono tnum " + (window.safeN(p.unreal, 0) >= 0 ? "pos" : "neg")}>{window.fmt$(window.safeN(p.unreal, 0), 2)}</div>
                    <div className="sel-pos-time mono muted">{p.opened || "—"}</div>
                  </div>
                );
              })}
            </div>
          ) : <div className="sel-empty-tab mono">No open positions on this strategy.</div>
        )}

        {tab === "config" && (() => {
          // Per-(account, slug) live config from STRATEGY_CONFIG_SEED in
          // data-live.jsx — surfaces ml_model_dir / threshold / sub_budget
          // / notes for the variant actually running, derived from the
          // agent's TOML on aviaf-paper.  Falls back to the legacy mock
          // instance-cfg block when neither is available.
          const liveCfg = window.strategyConfigFor
            ? window.strategyConfigFor(strat.acct, strat.defId)
            : null;
          if (liveCfg) {
            const rows = [
              ["variant",         liveCfg.label],
              ["family",          liveCfg.family],
              ["symbol",          liveCfg.symbol],
              ["ml model dir",    liveCfg.ml_model_dir],
              ["ml threshold",    liveCfg.ml_threshold != null ? liveCfg.ml_threshold : null],
              ["ml display only", liveCfg.ml_display_only != null ? (liveCfg.ml_display_only ? "yes (shadow)" : "no (gates entries)") : null],
              ["sub-budget",      liveCfg.sub_budget != null ? window.fmt$(liveCfg.sub_budget, 0) : null],
              ["max SL $",        liveCfg.max_sl_dollars != null ? window.fmt$(liveCfg.max_sl_dollars, 0) : null],
              ["config file",     liveCfg.config_file],
              ["config section",  liveCfg.config_section],
              ["basis",           liveCfg.basis],
            ].filter(([, v]) => v != null && v !== "");
            return (
              <div className="sel-cfg-tab mono">
                {rows.map(([k, v]) => (
                  <div key={k} className="sel-cfg-row">
                    <span className="muted">{k}</span>
                    <span className="tnum" style={{ overflowWrap: "anywhere", textAlign: "right" }}>{String(v)}</span>
                  </div>
                ))}
                {liveCfg.notes && <div className="sel-cfg-note">{liveCfg.notes}</div>}
                <div className="sel-cfg-row" style={{ marginTop: 8 }}>
                  <span className="muted">agent slug</span>
                  <span className="tnum muted">{strat.defId}</span>
                </div>
              </div>
            );
          }
          if (cfg) {
            return (
              <div className="sel-cfg-tab mono">
                <div className="sel-cfg-row">
                  <span className="muted">file</span>
                  <a href={"#/configs/" + cfg.id} style={{ color: "var(--ink)" }}>{cfg.file}</a>
                </div>
                <div className="sel-cfg-row"><span className="muted">label</span><span>{cfg.label}</span></div>
                <div className="sel-cfg-row"><span className="muted">daily loss cap</span><span className="tnum">{window.fmt$(cfg.dailyLossCap, 0)}</span></div>
                <div className="sel-cfg-row"><span className="muted">max concurrent</span><span className="tnum">{cfg.maxConcurrent}</span></div>
                <div className="sel-cfg-row"><span className="muted">edited</span><span className="tnum">{cfg.lastEdit}</span></div>
                <div className="sel-cfg-row"><span className="muted">used by</span><span className="tnum">{cfg.usedBy.length} instance{cfg.usedBy.length === 1 ? "" : "s"}</span></div>
                <div className="sel-cfg-note">{cfg.note}</div>
              </div>
            );
          }
          return (
            <div className="sel-empty-tab mono">
              No config snapshot for ({strat.acct} · {strat.defId}). Add a
              STRATEGY_CONFIG_SEED entry in data-live.jsx, or override via
              <span className="tnum"> localStorage["aviaf.strategy.labels"]</span>.
            </div>
          );
        })()}

        {tab === "lineage" && (
          <div className="sel-lineage-tab">
            {def && (
              <div className="sel-cfg-row mono"><span className="muted">recipe</span>
                <a href={"#/strategies/" + def.id} style={{ color: "var(--ink)" }}>{def.name}</a>
              </div>
            )}
            {def && <div className="sel-cfg-row mono"><span className="muted">author</span><span>{def.author}</span></div>}
            {def && <div className="sel-cfg-row mono"><span className="muted">validated</span><span className="tnum">{def.validated}</span></div>}
            {def && <div className="sel-cfg-row mono"><span className="muted">basis</span><span>{def.basis}</span></div>}
            {linkedResearch.length > 0 && (
              <div className="sel-research">
                <div className="sel-block-h">Source research</div>
                {linkedResearch.map((r) => (
                  <a key={r.id} href={"#/research/" + r.id} className="sel-research-row">
                    <div className="sel-research-t">{r.title}</div>
                    <div className="mono muted" style={{ fontSize: 11 }}>{r.tag} · {r.date} · {r.status}</div>
                  </a>
                ))}
              </div>
            )}
            {inst && (
              <div className="sel-cfg-row mono" style={{ marginTop: 8 }}>
                <span className="muted">runs on</span>
                <a href={"#/instances/" + inst.id} style={{ color: "var(--ink)" }}>{inst.id}</a>
              </div>
            )}
            {inst && <div className="sel-cfg-row mono"><span className="muted">host · uptime</span><span>{inst.host} · <span className="tnum">{inst.uptime}</span></span></div>}
          </div>
        )}
      </div>

      {/* footer actions */}
      <div className="sel-actions-foot">
        <window.Btn kind="primary" onClick={() => { location.hash = "#/strategies/" + klass; }}>Open class →</window.Btn>
        {strat.state === "running" || strat.state === "armed" ? (
          <CmdBtn label="Pause" busyLabel="Pausing…" okMsg="paused"
            confirm={`Pause trading on account ${strat.acct}? This affects ALL strategies on that account.`}
            run={() => window.pauseInstance(strat.instance, strat.acct)} />
        ) : (
          <CmdBtn label="Start" kind="primary" busyLabel="Starting…" okMsg="resumed"
            confirm={`Resume trading on account ${strat.acct}?`}
            run={() => window.resumeInstance(strat.instance, strat.acct)} />
        )}
        <CmdBtn label="Flatten" kind="danger" busyLabel="Flattening…" okMsg="flatten sent"
          confirm={`Flatten ALL open positions on account ${strat.acct}? Sends market-close orders.`}
          run={() => window.flattenAccount(strat.instance, strat.acct)} />
      </div>
    </div>);
}

/* ── Empty state when nothing is selected ─────────────────── */
function SelectedStrategyEmpty({ strats }) {
  // "deployments", not "live deployments" — the map deliberately shows
  // stopped rows and (since 2026-07-26) rows from retired accounts whose
  // trades still fall inside the 30d window.
  const liveCount = strats.length;
  return (
    <div className="sel-empty">
      <div className="sel-empty-icon">⊙</div>
      <div className="sel-empty-title">Tap a bubble to inspect</div>
      <div className="sel-empty-sub mono">
        {liveCount} deployment{liveCount === 1 ? "" : "s"} across {window.STRATEGY_CLASSES.length} classes ·
        click any point in the map to see its full configuration, perf vs bench, open positions and config snapshot.
      </div>
      <div className="sel-empty-tips mono">
        <div><b style={{color:"var(--ink-2)"}}>Pinch / scroll</b> · zoom into a region</div>
        <div><b style={{color:"var(--ink-2)"}}>Drag</b> · pan the canvas</div>
        <div><b style={{color:"var(--ink-2)"}}>Double-click</b> · reset view</div>
        <div><b style={{color:"var(--ink-2)"}}>Legend</b> · toggle class visibility</div>
      </div>
    </div>);
}

/* ── Strategies · catalog ─────────────────────────────────── */

window.PageStrategies = function PageStrategies() {
  const [sortBy, setSortBy] = usePg("pf");
  const [selectedId, setSelectedId] = usePg(null);

  // Live-rev subscription: every WS push (fill, equity tick, alert, …)
  // bumps window.__LIVE.rev via data-live.jsx#bump().  Poll it on a
  // 500ms cadence and force re-render only when it changes — same
  // pattern PageAlerts uses, cheap and ensures the map / panels reflect
  // brand-new fills without the operator having to navigate away.
  const [liveRev, setLiveRev] = usePg(() => (window.__LIVE && window.__LIVE.rev) || 0);
  const [lastUpdateMs, setLastUpdateMs] = usePg(() => Date.now());
  usePgE(() => {
    const id = setInterval(() => {
      const cur = (window.__LIVE && window.__LIVE.rev) || 0;
      if (cur !== liveRev) {
        setLiveRev(cur);
        setLastUpdateMs(Date.now());
      }
    }, 500);
    return () => clearInterval(id);
  }, [liveRev]);

  // Map shows EVERY deployed strategy (running, armed, paused, stopped,
  // breached) so operators can see variants the agent has attached but
  // not activated.  StrategyRiskMap fades stopped/breached bubbles via
  // data-state attributes; the legend counts include stopped.
  //
  // Re-derive on every liveRev change — the snapshots get rebuilt by
  // rebuildStratsFromAccounts when fills/equity land, so re-reading
  // window.STRATS here picks up the new numbers without rebuilding
  // them ourselves.
  const rawStrats = usePgM(
    () => (window.getAllDeployedStrats ? window.getAllDeployedStrats() : (window.STRATS || [])),
    [liveRev],
  );
  const liveStrats = usePgM(() => window.getLiveStrats(), [liveRev]);

  // Suppress bubbles whose defId is exactly a STRATEGY_CLASSES id
  // (bare "orb", "mr", "ma_cross", "pair_divergence", "orb_test").
  // Those slugs come from the agent's [strategy.<name>] config blocks
  // and read as class names on the map — operators were confusing them
  // for class aggregates instead of individual deployments.  Their fills
  // are usually folded into a real variant via FILL_REMAP anyway (e.g.
  // combine4.orb → mr → labelled "mr_v2"); the remaining bare-class
  // rows that aren't remapped are hidden here per operator request
  // (2026-05-25).  Toggle wiring + a status note keep the data
  // discoverable if needed.
  const [showBareClass, setShowBareClass] = usePg(false);
  const classIdSet = new Set((window.STRATEGY_CLASSES || []).map((c) => c.id));
  // Bare-class slug ("mr", "orb") OR `_legacy` suffix (produced by the
  // `since`-cutoff split in rebuildStratsFromAccounts for prac:mr →
  // mr_v3 + prac:mr_legacy etc).  Both kinds are visual noise on the
  // map — they read as duplicates of the versioned bubble that the
  // operator actually thinks of as the deployment.  Hidden by default;
  // the "show bare-class" toggle surfaces both so the data stays
  // discoverable for forensics.
  const isBareOrLegacy = (s) =>
    classIdSet.has(s.defId) || (s.defId && s.defId.endsWith("_legacy"));
  const bareClassStrats = rawStrats.filter(isBareOrLegacy);
  const allStrats = showBareClass
    ? rawStrats
    : rawStrats.filter((s) => !isBareOrLegacy(s));
  // Resolve the clicked bubble's id in BOTH sets: per-account rows
  // (`acct:defId`) and cross-account "By strategy" rows (`defId`, from
  // STRATS_BY_DEF). Without the second lookup, inspecting a By-strategy
  // bubble found nothing and the panel stayed blank.
  const selected = rawStrats.find((s) => s.id === selectedId)
    || (window.STRATS_BY_DEF || []).find((s) => s.id === selectedId)
    || null;

  // Discover any classes present in data that aren't in STRATEGY_CLASSES
  // — variants whose prefix didn't match any registered class fall back
  // to "other" with a derived color, but a never-seen class slug emitted
  // straight from the agent (e.g. agent ships a fresh klass field via
  // strategy_def_inventory) should still get a swatch.
  const classesInData = new Set(allStrats.map((s) => window.classOfStrat(s)));
  const knownIds = new Set(window.STRATEGY_CLASSES.map((c) => c.id));
  const extraClasses = [...classesInData]
    .filter((k) => k && !knownIds.has(k))
    .map((k) => window.classMeta(k));
  const allClasses = [...window.STRATEGY_CLASSES, ...extraClasses];
  const liveCount = liveStrats.filter((s) => !isBareOrLegacy(s) || showBareClass).length;
  const totalDeploys = allStrats.length;
  const retiredCount = allStrats.filter((s) => s.retired && !s.external).length;
  // Externally-managed rows (read-only mirror instances) trade for real but
  // aren't aviaf deployments — counted on their own line so "N live" keeps
  // meaning "N strategies this system is running".
  const externalCount = allStrats.filter((s) => s.external).length;
  // Per-strategy state comes from the agent's equity emit (a.strategyStatus).
  // On a COLD load that hasn't arrived yet, every row resolves to "stopped"
  // and the sub-line read "0 live · 18 stopped" for ~30-60s — a flat lie on a
  // page whose whole purpose is telling the operator what's running.  Detect
  // "no status seen yet" and say so instead of reporting a false zero.
  const statusPending = (window.ACCOUNTS || []).length > 0
    && !(window.ACCOUNTS || []).some(
      (a) => a && a.strategyStatus && Object.keys(a.strategyStatus).length > 0,
    );
  const liveSub = statusPending
    ? `${totalDeploys} deployed · awaiting agent status`
    : `${liveCount} live · ${totalDeploys - liveCount - retiredCount - externalCount} stopped`
      + (retiredCount ? ` · ${retiredCount} on retired acct${retiredCount === 1 ? "" : "s"}` : "")
      + (externalCount ? ` · ${externalCount} mirrored (external)` : "");

  return (
    <div>
      <window.SectionHeader
        title={<>Strategy map · all deployed <LiveDot lastUpdateMs={lastUpdateMs} /></>}
        sub={`${liveSub} · across ${classesInData.size} class${classesInData.size === 1 ? "" : "es"} · tap any bubble${bareClassStrats.length > 0 && !showBareClass ? ` · ${bareClassStrats.length} bare-class slug${bareClassStrats.length === 1 ? "" : "s"} hidden` : ""}`}
        right={bareClassStrats.length > 0 ? (
          <button
            data-active={showBareClass}
            onClick={() => setShowBareClass((v) => !v)}
            title={`Bubbles whose defId is a class name (orb, mr, …) — usually folded into a variant by FILL_REMAP.  ${bareClassStrats.length} such row${bareClassStrats.length === 1 ? "" : "s"}.`}>
            {showBareClass ? "✓ show bare-class" : "show bare-class"}
          </button>
        ) : null}
      />
      <div className="rrmap">
        <div className="rrmap-main">
          <StrategyRiskMap strats={allStrats} classes={allClasses} selectedId={selectedId} onSelect={setSelectedId} />
        </div>
        <div className="rrmap-side">
          {selected ? (
            <SelectedStrategyPanel strat={selected} onClose={() => setSelectedId(null)} />
          ) : (
            <SelectedStrategyEmpty strats={allStrats} />
          )}
        </div>
      </div>

      <window.SectionHeader title="Running classes · cumulative" sub="aggregated equity per class · last 60d" />
      <div className="cls-curves-strip">
        <StrategyClassCurves strats={liveStrats} layout="horizontal" />
      </div>

      <StrategyCatalog sortBy={sortBy} setSortBy={setSortBy} />
    </div>);

};

/* ── Strategy catalogue ───────────────────────────────────────
 *
 * Used to render window.STRATEGY_DEFS only — a static 5-entry seed of
 * May-2026 research recipes (orb / mr / orb_test / pair_divergence /
 * ma_cross).  None of the slugs the agent actually runs (orb_rule,
 * ma_cross_gc/_ng/_hg/_gcv/_gcvm, combine_racer*, zmom_nq, cl6e_spread,
 * payout_bracket, tltme) match those ids, so on 2026-07-26 the section
 * showed five recipes ALL labelled "— not deployed —" while 13 live slugs
 * traded underneath it.  Now the catalogue is the UNION of:
 *   • live/recent deployments (from STRATS_BY_DEF) — real numbers, ranked
 *     first because they're what's actually at risk;
 *   • research recipes that carry a bench (kept for the research-vs-live
 *     comparison), flagged when nothing is deployed against them.
 * Live rows show broker-verified live stats (WR / PF / 7d / 30d / trades)
 * instead of a research bench they don't have. */
function StrategyCatalog({ sortBy, setSortBy }) {
  const defs = window.STRATEGY_DEFS || [];
  const byDef = window.STRATS_BY_DEF || [];
  const defIds = new Set(defs.map((d) => d.id));
  // Live entries: every aggregated deployment, minus bare-class/legacy noise
  // and the "_unattributed" bucket (not a strategy).
  const classIds = new Set((window.STRATEGY_CLASSES || []).map((c) => c.id));
  const liveEntries = byDef
    .filter((s) => s.id && s.id !== "_unattributed" && !s.id.endsWith("_legacy"))
    .filter((s) => !(classIds.has(s.id) && !defIds.has(s.id)))
    .map((s) => ({ agg: s, def: defs.find((d) => d.id === s.id) || null }));
  const liveIds = new Set(liveEntries.map((e) => e.agg.id));
  const researchOnly = defs
    .filter((d) => !liveIds.has(d.id))
    .map((d) => ({ agg: null, def: d }));

  const _benchVal = (d) => (d && d.bench && Number.isFinite(d.bench[sortBy]))
    ? d.bench[sortBy] : null;
  const _liveVal = (agg) => {
    if (!agg) return null;
    if (sortBy === "wr") return agg.wr;
    if (sortBy === "pf") {
      return agg.grossLoss > 0 ? agg.grossWin / agg.grossLoss : (agg.grossWin > 0 ? Infinity : null);
    }
    if (sortBy === "eR") return agg.eR;
    return agg.pnl30;   // ppass has no live analogue → rank by 30d $
  };
  // Deployed first (live numbers), research-only recipes after.
  const rows = [
    ...liveEntries.sort((a, b) => (_liveVal(b.agg) ?? -Infinity) - (_liveVal(a.agg) ?? -Infinity)),
    ...researchOnly.sort((a, b) => (_benchVal(b.def) ?? -Infinity) - (_benchVal(a.def) ?? -Infinity)),
  ];

  const fmtPct = (v) => (Number.isFinite(v) ? `${(v * 100).toFixed(1)}%` : "—");
  const fmtNum = (v, d = 2) => (Number.isFinite(v) ? v.toFixed(d) : (v === Infinity ? "∞" : "—"));

  return (
    <>
      <window.SectionHeader
        title="Strategies"
        count={rows.length}
        sub={`${liveEntries.length} deployed · ${researchOnly.length} research recipe${researchOnly.length === 1 ? "" : "s"} not deployed · live rows show broker-verified numbers`}
        right={
          <>
            <button data-active={sortBy === "pf"} onClick={() => setSortBy("pf")}>Profit factor</button>
            <button data-active={sortBy === "eR"} onClick={() => setSortBy("eR")}>E[R]</button>
            <button data-active={sortBy === "wr"} onClick={() => setSortBy("wr")}>Win rate</button>
            <button data-active={sortBy === "ppass"} onClick={() => setSortBy("ppass")}>P(pass)</button>
            <window.Btn kind="primary">+ New from research</window.Btn>
          </>
        } />
      {/* Per-strategy performance ledger (overall + weekly) — 2026-08-17.
          Mounted ABOVE the catalog: the catalog is long, and a ledger below
          it is a ledger nobody scrolls to. */}
      <window.StrategyLedger />
      <div className="strat-cat">
        {rows.map(({ agg, def }) => {
          const id = agg ? agg.id : def.id;
          const deploys = (window.STRATS || []).filter((s) => s.defId === id);
          const name = (def && def.name) || (agg && agg.name) || id;
          const pf = agg && agg.grossLoss > 0
            ? agg.grossWin / agg.grossLoss
            : (agg && agg.grossWin > 0 ? Infinity : null);
          const retiredN = agg ? (agg.retiredAccounts || []).length : 0;
          const externalN = deploys.filter((s) => s.external).length;
          const allExternal = deploys.length > 0 && externalN === deploys.length;
          const fam = def
            ? `${def.family} · ${def.symbol}.${def.venue} · validated ${def.validated}`
            : `${window.classMeta ? (window.classMeta(window.inferStrategyClass ? window.inferStrategyClass(id) : "other").label || "live") : "live"} · ${agg.accounts.length} acct${agg.accounts.length === 1 ? "" : "s"}${retiredN ? ` (${retiredN} retired)` : ""} · ${allExternal ? "mirrored · read-only (not an aviaf deployment)" : "live only"}`;
          return (
            <div className="strat-card" key={id} onClick={() => {location.hash = `#/strategies/${id}`;}}>
              <div className="top">
                <div>
                  <h3>{name}</h3>
                  <div className="fam">{fam}</div>
                </div>
                {agg ? (
                  <div className="tnum" style={{ textAlign: "right", fontSize: 12 }}>
                    <div className={agg.pnl30 >= 0 ? "pos" : "neg"}>{window.fmt$(agg.pnl30)}</div>
                    <div style={{ color: "var(--muted)" }}>30d</div>
                  </div>
                ) : (
                  <window.Sparkline data={window.genEquity(name.length * 7).slice(-40)} width={140} height={36} />
                )}
              </div>
              <div className="summary">
                {(def && def.summary)
                  || `Live deployment — no research bench recorded.  ${agg.trades} closed trade${agg.trades === 1 ? "" : "s"} on ${agg.accounts.join(", ")}.`}
              </div>
              {agg ? (
                <div className="stat-row">
                  <div className="c"><div className="lbl">WR</div><div className="v tnum">{fmtPct(agg.wr)}</div></div>
                  <div className="c"><div className="lbl">PF</div><div className="v tnum">{fmtNum(pf)}</div></div>
                  <div className="c"><div className="lbl">7d</div><div className={`v tnum ${agg.pnl7 >= 0 ? "pos" : "neg"}`}>{window.fmt$(agg.pnl7)}</div></div>
                  <div className="c"><div className="lbl">30d</div><div className={`v tnum ${agg.pnl30 >= 0 ? "pos" : "neg"}`}>{window.fmt$(agg.pnl30)}</div></div>
                  <div className="c"><div className="lbl">trades</div><div className="v tnum">{agg.trades}</div></div>
                </div>
              ) : (
                <div className="stat-row">
                  <div className="c"><div className="lbl">WR</div><div className="v tnum">{fmtPct(def.bench && def.bench.wr)}</div></div>
                  <div className="c"><div className="lbl">PF</div><div className="v tnum">{fmtNum(def.bench && def.bench.pf)}</div></div>
                  <div className="c"><div className="lbl">E[R]</div><div className="v tnum pos">{def.bench ? window.fmtP(def.bench.eR, 3) : "—"}</div></div>
                  <div className="c"><div className="lbl">P(pass)</div><div className="v tnum">{fmtPct(def.bench && def.bench.ppass)}</div></div>
                  <div className="c"><div className="lbl">P(ruin)</div><div className="v tnum neg">{fmtPct(def.bench && def.bench.pruin)}</div></div>
                </div>
              )}
              <div className="deploys">
                <span style={{ color: "var(--muted)" }}>{agg ? "accounts:" : "deployed:"}</span>
                {deploys.length === 0 && <span className="pill">— not deployed —</span>}
                {deploys.map((s) =>
                <span key={s.id} className="pill" data-state={s.state}
                  title={s.external
                    ? "externally-managed account mirrored read-only — aviaf places no orders here"
                    : (s.retired ? "retired account — in-window trades still counted" : undefined)}>
                  {s.acct}{s.contracts ? ` · ${s.contracts}c` : ""} · {s.external ? "external" : (s.retired ? "retired" : s.state)}
                </span>
                )}
              </div>
            </div>);
        })}
      </div>
    </>);
}

/* ── Account-type aware breakdown for a class ─────────────── */

function AcctTypeBreakdown({ klass, classDefs }) {
  // collect every runtime strat in this class
  const strats = window.STRATS.filter((s) => classDefs.some((d) => d.id === s.defId));
  // bucket by acct kind
  const buckets = {};
  strats.forEach((s) => {
    const t = window.acctTypeOfStrat(s);
    (buckets[t] = buckets[t] || []).push(s);
  });

  // bench averages (research promise) — averaged across this class
  const benchAvg = (() => {
    const b = classDefs.map((d) => d.bench);
    if (b.length === 0) return null;
    return {
      wr:    b.reduce((s, x) => s + x.wr, 0) / b.length,
      pf:    b.reduce((s, x) => s + x.pf, 0) / b.length,
      eR:    b.reduce((s, x) => s + x.eR, 0) / b.length,
      ppass: b.reduce((s, x) => s + x.ppass, 0) / b.length,
      pruin: b.reduce((s, x) => s + x.pruin, 0) / b.length,
    };
  })();

  // build rows ordered by ACCT_TYPES, only include those with deploys
  const rows = window.ACCT_TYPES.map((t) => {
    const list = buckets[t.id] || [];
    if (list.length === 0) return null;
    const live = list.filter((s) => window.LIVE_STATES.has(s.state));
    const totalTrades = list.reduce((s, x) => s + window.safeN(x.trades, 0), 0) || 1;
    const _wMean = (key) => list.reduce(
      (s, x) => s + (Number.isFinite(x[key]) ? x[key] : 0) * window.safeN(x.trades, 0), 0,
    ) / totalTrades;
    const wr = _wMean("wr");
    const pf = _wMean("pf");
    const eR = _wMean("eR");
    const pnl30 = list.reduce((s, x) => s + window.safeN(x.pnl30, 0), 0);
    const mdd = Math.max(...list.map((x) => Math.abs(window.safeN(x.dd, 0))), 0);
    // P(pass) only meaningful for combine; P(ruin) for combine + xfa
    const showPass = t.id === "combine";
    const showRuin = t.id === "combine" || t.id === "xfa";
    return { t, list, live, totalTrades, wr, pf, eR, pnl30, mdd, showPass, showRuin };
  }).filter(Boolean);

  if (rows.length === 0) {
    return (
      <div className="cls-cmp-empty">
        No deployments yet. Deploy on an account to populate this breakdown.
      </div>);
  }

  return (
    <div className="at-tab">
      <div className="at-thead">
        <div className="at-th c-type">Account type</div>
        <div className="at-th c-dep">Deploys</div>
        <div className="at-th c-pnl">30d P&L</div>
        <div className="at-th c-dd">Max DD</div>
        <div className="at-th c-wr">Win rate</div>
        <div className="at-th c-pf">PF</div>
        <div className="at-th c-er">E[R]</div>
        <div className="at-th c-pass">P(pass · 60d)</div>
        <div className="at-th c-ruin">P(ruin · 60d)</div>
      </div>
      {rows.map((r) => {
        const survival = r.t.survival;
        const ddPct = survival ? Math.min(1, r.mdd / survival) : 0;
        return (
          <div key={r.t.id} className="at-row">
            <div className="at-cell c-type">
              <span className="at-badge" style={{ color: r.t.color }}>{r.t.label.toUpperCase()}</span>
              <span className="at-desc">{r.t.desc}</span>
              {survival && (
                <div className="at-mll">
                  MLL ${(survival / 1000).toFixed(1)}k ·
                  <span className={"tnum " + (ddPct > 0.7 ? "neg" : ddPct > 0.4 ? "warn" : "")}>
                    &nbsp;DD {(ddPct * 100).toFixed(0)}% of cap
                  </span>
                </div>
              )}
            </div>
            <div className="at-cell c-dep mono tnum">
              <b style={{ color: "var(--ink)" }}>{r.list.length}</b>
              <span className="muted"> · {r.live.length} live</span>
            </div>
            <div className={"at-cell c-pnl mono tnum " + (r.pnl30 >= 0 ? "pos" : "neg")}>{window.fmt$(r.pnl30, 0)}</div>
            <div className="at-cell c-dd mono tnum neg">−{window.fmt$(r.mdd, 0).replace("−", "").replace("$", "$")}</div>
            <div className="at-cell c-wr mono tnum">{(r.wr * 100).toFixed(1)}%</div>
            <div className="at-cell c-pf mono tnum">{r.pf.toFixed(2)}</div>
            <div className={"at-cell c-er mono tnum " + (r.eR >= 0 ? "pos" : "neg")}>{window.fmtP(r.eR, 2)}</div>
            <div className="at-cell c-pass mono tnum">
              {r.showPass && benchAvg ? (
                <span>{(benchAvg.ppass * 100).toFixed(1)}%<span className="muted"> bench</span></span>
              ) : (
                <span className="at-na">{r.t.id === "live" ? "funded · n/a" : r.t.id === "paper" ? "sim only" : "n/a"}</span>
              )}
            </div>
            <div className="at-cell c-ruin mono tnum">
              {r.showRuin && benchAvg ? (
                <span className={"tnum " + (r.t.id === "xfa" ? "warn" : "neg")}>
                  {(benchAvg.pruin * 100).toFixed(1)}%
                  <span className="muted"> bench</span>
                </span>
              ) : r.t.id === "live" ? (
                <span className="at-na">use Sharpe / maxDD</span>
              ) : (
                <span className="at-na">—</span>
              )}
            </div>
          </div>);
      })}
      <div className="at-foot">
        <span className="mono">Bench probabilities are model estimates from 60d Monte Carlo on the recipe; account-type column gates which apply. Live accounts have no formal "pass" target — use realized Sharpe / max DD.</span>
      </div>
    </div>);
}


window.PageStrategyClass = function PageStrategyClass({ klass, highlightDefId, tweaks }) {
  const meta = window.classMeta(klass);
  const classDefs = window.STRATEGY_DEFS.filter((d) => d.klass === klass);
  if (classDefs.length === 0) return <div style={{ padding: 32 }}>Class not found.</div>;

  const allDeploys = window.STRATS.filter((s) => classDefs.some((d) => d.id === s.defId));
  const liveDeploys = allDeploys.filter((s) => window.LIVE_STATES.has(s.state));
  // linked research / proposals
  const linkedResearch = window.RESEARCH.filter((r) => r.produces.some((id) => classDefs.some((d) => d.id === id)));
  const linkedProposals = window.PROPOSALS.filter((p) => classDefs.some((d) => d.id === p.target));

  // aggregate KPIs across class (weighted by trades)
  const totalTrades = liveDeploys.reduce((s, x) => s + window.safeN(x.trades, 0), 0) || 1;
  const wAvg = (k) => liveDeploys.reduce(
    (s, x) => s + (Number.isFinite(x[k]) ? x[k] : 0) * window.safeN(x.trades, 0), 0,
  ) / totalTrades;
  const pnl30Total = liveDeploys.reduce((s, x) => s + window.safeN(x.pnl30, 0), 0);
  const dayPnlTotal = liveDeploys.reduce((s, x) => s + window.safeN(x.today, 0), 0);
  const maxDD = Math.max(...liveDeploys.map((x) => Math.abs(window.safeN(x.dd, 0))), 0);
  const benchAvg = classDefs.length ? {
    wr: classDefs.reduce((s, d) => s + d.bench.wr, 0) / classDefs.length,
    pf: classDefs.reduce((s, d) => s + d.bench.pf, 0) / classDefs.length,
    eR: classDefs.reduce((s, d) => s + d.bench.eR, 0) / classDefs.length,
  } : null;

  // editor state (re-used)
  const [editing, setEditing] = usePg(null);
  const [, bump] = usePg(0);

  // unique account types in this class
  const acctTypes = Array.from(new Set(allDeploys.map((s) => window.acctTypeOfStrat(s))));

  return (
    <>
      <window.PageHeader
        crumbs={[{ label: "Strategies", href: "#/strategies" }, { label: meta.label }]}
        actions={<>
          <window.Btn>Backtest class</window.Btn>
          <window.Btn>Clone</window.Btn>
          <window.Btn kind="primary">+ Deploy variation</window.Btn>
        </>} />

      <div className="class-hero">
        <div className="ch-left">
          <div className="ch-kicker">
            <span className="ch-swatch" style={{ color: meta.color }} />
            <span>Strategy class</span>
          </div>
          <h1 className="ch-title">{meta.label}
            <span className="ch-sub">· {meta.desc}</span>
          </h1>
          <div className="ch-tagline">
            {classDefs.length} recipe{classDefs.length === 1 ? "" : "s"} ·
            &nbsp;{liveDeploys.length} live deployment{liveDeploys.length === 1 ? "" : "s"} ·
            &nbsp;across <b style={{ color: "var(--ink)" }}>{acctTypes.join(" · ")}</b> accounts
          </div>
          <div className="ch-recipes">
            {classDefs.map((d) => (
              <a key={d.id} className="ch-pill" data-self={d.id === highlightDefId}
                href={"#/strategies/" + d.id}
                onClick={(e) => { e.preventDefault(); location.hash = "#/strategies/" + d.id; }}>
                <span className="ch-pill-name">{d.name}</span>
                <span className="muted"> · {d.symbol}.{d.venue}</span>
                <span className="ch-pill-bench tnum">PF {d.bench.pf.toFixed(2)}</span>
              </a>
            ))}
          </div>
        </div>
        <div className="ch-right">
          <div className="ch-stat">
            <div className="lbl">30d P&L · class</div>
            <div className={"v mono tnum " + (pnl30Total >= 0 ? "pos" : "neg")}>{window.fmt$(pnl30Total, 0)}</div>
            <div className="sub mono">today {dayPnlTotal === 0 ? "—" : window.fmt$(dayPnlTotal, 0)}</div>
          </div>
          <div className="ch-stat">
            <div className="lbl">Max DD · live</div>
            <div className="v mono tnum neg">−{window.fmt$(maxDD, 0).replace("−", "").replace("$", "$")}</div>
            <div className="sub mono">{liveDeploys.length} deploy{liveDeploys.length === 1 ? "" : "s"}</div>
          </div>
        </div>
      </div>

      <div className="kpis">
        <div className="kpi"><div className="lbl">Recipes</div><div className="val mono tnum">{classDefs.length}</div><div className="delta mono">{classDefs.map((d) => d.symbol).filter((s, i, a) => a.indexOf(s) === i).join(" · ")}</div></div>
        <div className="kpi"><div className="lbl">Live deployments</div><div className="val mono tnum">{liveDeploys.length}</div><div className="delta mono">of {allDeploys.length} total</div></div>
        <div className="kpi"><div className="lbl">Win rate · live</div><div className="val mono tnum">{(wAvg("wr") * 100).toFixed(1)}%</div><div className="delta mono">bench {benchAvg ? (benchAvg.wr * 100).toFixed(1) + "%" : "—"}</div></div>
        <div className="kpi"><div className="lbl">Profit factor · live</div><div className="val mono tnum pos">{wAvg("pf").toFixed(2)}</div><div className="delta mono">bench {benchAvg ? benchAvg.pf.toFixed(2) : "—"}</div></div>
        <div className="kpi"><div className="lbl">E[R] · live</div><div className={"val mono tnum " + (wAvg("eR") >= 0 ? "pos" : "neg")}>{window.fmtP(wAvg("eR"), 3)}</div><div className="delta mono">bench {benchAvg ? window.fmtP(benchAvg.eR, 3) : "—"}</div></div>
        <div className="kpi"><div className="lbl">Trades · 30d</div><div className="val mono tnum">{liveDeploys.reduce((s, x) => s + x.trades, 0)}</div><div className="delta mono">across {liveDeploys.length} deploy{liveDeploys.length === 1 ? "" : "s"}</div></div>
      </div>

      <window.SectionHeader title="By account type" sub="combine · xfa · paper · live — P(pass) / P(ruin) scope to applicable types only" />
      <AcctTypeBreakdown klass={klass} classDefs={classDefs} />

      <window.SectionHeader title="Live variations · comparison" sub={`compare all live recipes in the ${meta.label} class`} />
      <ClassSiblingsComparison klass={klass} currentDefId={highlightDefId || null} />

      <window.SectionHeader title="Recipes" count={classDefs.length} sub="research artifacts in this class · click to reveal config & lineage" right={
        <window.Btn kind="primary">+ New from research</window.Btn>
      } />
      <div className="cls-recipes">
        {classDefs.map((d) => {
          const deps = window.STRATS.filter((s) => s.defId === d.id);
          const live = deps.filter((s) => window.LIVE_STATES.has(s.state));
          const totalT = deps.reduce((s, x) => s + x.trades, 0) || 1;
          const liveWR = deps.reduce((s, x) => s + x.wr * x.trades, 0) / totalT;
          const livePF = deps.reduce((s, x) => s + x.pf * x.trades, 0) / totalT;
          const livePnl = deps.reduce((s, x) => s + x.pnl30, 0);
          const isSelf = d.id === highlightDefId;
          return (
            <div className="cls-recipe" key={d.id} data-self={isSelf}>
              <div className="cr-h">
                <div>
                  <div className="cr-name">{d.name}
                    {isSelf && <span className="cr-self-tag">YOU CAME FROM</span>}
                  </div>
                  <div className="cr-meta mono">{d.symbol}.{d.venue} · validated {d.validated} · {d.author} · basis {d.basis}</div>
                </div>
                <div className="cr-bench mono">
                  <span><span className="muted">bench WR</span> {(d.bench.wr * 100).toFixed(1)}%</span>
                  <span><span className="muted">PF</span> {d.bench.pf.toFixed(2)}</span>
                  <span><span className="muted">E[R]</span> {window.fmtP(d.bench.eR, 2)}</span>
                </div>
              </div>
              <div className="cr-body">{d.summary}</div>
              <div className="cr-params">
                {Object.entries(d.keyParams).map(([k, v]) => (
                  <span key={k} className="cr-pair">
                    <span className="muted">{k}</span>
                    <span className="tnum">{String(v)}</span>
                  </span>
                ))}
              </div>
              <div className="cr-foot">
                <div className="cr-deploys">
                  <span className="muted">deploys</span>
                  {deps.length === 0 && <span className="cr-pill">— not deployed —</span>}
                  {deps.map((s) => (
                    <span key={s.id} className="cr-pill" data-state={s.state}>
                      {s.acct} · {s.contracts}c
                    </span>
                  ))}
                </div>
                <div className="cr-live mono">
                  {live.length > 0 ? (
                    <>
                      <span><span className="muted">live WR</span> <span className="tnum">{(liveWR * 100).toFixed(1)}%</span></span>
                      <span><span className="muted">PF</span> <span className="tnum">{livePF.toFixed(2)}</span></span>
                      <span className={"tnum " + (livePnl >= 0 ? "pos" : "neg")}>{window.fmt$(livePnl, 0)}</span>
                    </>
                  ) : (
                    <span className="muted">— no live data —</span>
                  )}
                </div>
              </div>
            </div>);
        })}
      </div>

      <window.SectionHeader title="Configurations" count={allDeploys.length} sub="every deployment of every recipe in this class · click to reconfigure" />
      {allDeploys.length === 0 ?
        <div style={{ padding: "24px var(--pad)", color: "var(--muted)", fontFamily: "var(--mono)", fontSize: 12 }}>No configurations yet.</div> :
        <div className="cfg-list">
          {allDeploys.map((s) => (
            <ConfigurationRow key={s.id} s={s} onOpen={() => {
              const acct = window.ACCOUNTS.find((a) => a.id === s.acct);
              if (acct) setEditing({ mode: "edit", strat: s, account: acct });
            }} />
          ))}
        </div>
      }

      {(linkedResearch.length > 0 || linkedProposals.length > 0) && (
        <window.SectionHeader title="Research & proposals" sub="lineage · experiments that produced or refine recipes in this class" />
      )}
      {linkedResearch.length > 0 && (
        <div className="res-grid">
          {linkedResearch.map((r) => (
            <div className="res-card" key={r.id}>
              <div className="h">
                <div className="title">{r.title}</div>
                <div className="meta">{r.date}</div>
              </div>
              <div className="body">{r.summary}</div>
              <div className="foot">
                <span style={{ padding: "1px 6px", border: "1px solid currentColor", letterSpacing: "0.06em", color: r.status === "shipped" ? "var(--pos)" : "var(--warn)" }}>{r.status.toUpperCase()}</span>
                <span>{r.tag}</span>
                <span>· {r.author}</span>
              </div>
            </div>
          ))}
        </div>
      )}
      {linkedProposals.length > 0 && (
        <div>
          {linkedProposals.map((p) => (
            <div className="prop-card" data-kind={p.kind} key={p.id}>
              <div className="ph">
                <span className="kind-tag">{p.kind.toUpperCase()}</span>
                <span className="ptitle">{p.title}</span>
                <span className="strength">strength {p.strength.toFixed(2)} · {p.date}</span>
              </div>
              <div className="body"><b>Why:</b> {p.rationale}</div>
              <div className="body"><b>Backtest:</b> {p.backtest}</div>
            </div>
          ))}
        </div>
      )}

      {editing &&
        <StrategyEditor
          account={editing.account}
          editing={editing}
          onClose={() => setEditing(null)}
          onApply={() => bump((x) => x + 1)}
          onDelete={() => setEditing(null)} />
      }
    </>);
};


/* ====== end: new strategy / class / risk-map components ====== */


window.PageStrategyDetail = function PageStrategyDetail({ id, tweaks }) {
  const def = window.STRATEGY_DEFS.find((x) => x.id === id);
  // backwards-compat: also accept runtime id
  const runtime = !def ? window.STRATS.find((s) => s.id === id) : null;
  const d = def || runtime && window.STRATEGY_DEFS.find((x) => x.id === runtime.defId);
  const [editing, setEditing] = usePg(null);
  const [, bump] = usePg(0);
  if (!d) return <div style={{ padding: 32 }}>Strategy not found.</div>;

  const deploys = window.STRATS.filter((s) => s.defId === d.id);
  // research producing this strategy
  const linkedResearch = window.RESEARCH.filter((r) => r.produces.includes(d.id));
  // proposals targeting this strategy
  const linkedProposals = window.PROPOSALS.filter((p) => p.target === d.id);

  return (
    <>
      <window.PageHeader
        crumbs={[{ label: "Strategies", href: "#/strategies" }, { label: d.name }]}
        actions={<><window.Btn>Backtest</window.Btn><window.Btn>Clone</window.Btn><window.Btn kind="primary">Deploy</window.Btn></>} />
      

      <div className="kpis">
        <div className="kpi"><div className="lbl">Family</div><div className="val mono" style={{ fontSize: 16 }}>{d.family}</div><div className="delta mono">{d.symbol}.{d.venue}</div></div>
        <div className="kpi"><div className="lbl">Win rate (bench)</div><div className="val mono tnum">{(d.bench.wr * 100).toFixed(1)}%</div></div>
        <div className="kpi"><div className="lbl">Profit factor</div><div className="val mono tnum pos">{d.bench.pf.toFixed(2)}</div></div>
        <div className="kpi"><div className="lbl">E[R]</div><div className={"val mono tnum " + (d.bench.eR > 0 ? "pos" : "neg")}>{window.fmtP(d.bench.eR, 3)}</div></div>
        <div className="kpi"><div className="lbl">P(pass) · 60d</div><div className="val mono tnum">{(d.bench.ppass * 100).toFixed(1)}%</div></div>
        <div className="kpi"><div className="lbl">P(ruin) · 60d</div><div className="val mono tnum neg">{(d.bench.pruin * 100).toFixed(1)}%</div></div>
      </div>

      <div className="main">
        <div className="col-left">
          <div style={{ padding: "16px var(--pad)", borderBottom: "1px solid var(--rule)", color: "var(--ink-2)", fontSize: 13, lineHeight: 1.6, maxWidth: "72ch" }}>
            {d.summary}
          </div>

          <window.SectionHeader title="Research vs live · all configurations" sub="cumulative · divergence vs research promise" />
          {deploys.length === 0 ?
          <div style={{ padding: "24px var(--pad)", color: "var(--muted)", fontFamily: "var(--mono)", fontSize: 12 }}>Not currently deployed on any instance.</div> :

          <>
                <ResearchVsLivePlot strats={deploys} />
                <DivergenceMetricsRow strats={deploys} />
              </>
          }

          <window.SectionHeader title="Configurations" count={deploys.length} sub="each deployment carries its own params · click to reconfigure" right={
          <window.Btn kind="primary">+ Deploy on account</window.Btn>
          } />
          {deploys.length === 0 ?
          <div style={{ padding: "24px var(--pad)", color: "var(--muted)", fontFamily: "var(--mono)", fontSize: 12 }}>No configurations yet.</div> :

          <div className="cfg-list">
                {deploys.map((s) =>
            <ConfigurationRow key={s.id} s={s} onOpen={() => {
              const acct = window.ACCOUNTS.find((a) => a.id === s.acct);
              if (acct) setEditing({ mode: "edit", strat: s, account: acct });
            }} />
            )}
              </div>
          }

          {(() => {
            // TOML configs that bind this strategy def.  Forward link
            // from StrategyDetail → Configs hub so the operator can
            // jump straight from a strategy to the file that wires it
            // into a live agent.  Empty when no live config references
            // the strategy yet (e.g. research-only strategies).
            const bindingConfigs = (window.CONFIGS || []).filter(
              c => (c.strategyDefs || []).includes(d.id)
            );
            if (!bindingConfigs.length) return null;
            return (
              <>
                <window.SectionHeader
                  title="TOML configs binding this strategy"
                  count={bindingConfigs.length}
                  sub="files in the configs hub that load this strategy def · click to open"
                />
                <div style={{ padding: "4px 0 12px" }}>
                  {bindingConfigs.map(c => (
                    <a key={c.id}
                       href={`#/configs/${encodeURIComponent(c.id)}`}
                       style={{
                         display: "grid",
                         gridTemplateColumns: "1fr auto auto",
                         alignItems: "center",
                         gap: 12,
                         padding: "10px var(--pad)",
                         borderBottom: "1px dashed var(--rule)",
                         color: "var(--ink)",
                         textDecoration: "none",
                         fontSize: 12,
                       }}>
                      <span className="mono">
                        {c.file}
                        {c.label && <span style={{color:"var(--muted)", marginLeft:8}}>· {c.label}</span>}
                      </span>
                      <span className="mono" style={{color:"var(--muted)", fontSize:11}}>
                        DLC ${Number(c.dailyLossCap || 0).toLocaleString()}
                      </span>
                      <span style={{color:"var(--muted)", fontSize:11}}>open →</span>
                    </a>
                  ))}
                </div>
              </>
            );
          })()}

          <window.SectionHeader title="Parameters" sub="recipe defaults · cloned per deployment" right={
          <><window.Btn>Reset</window.Btn><window.Btn kind="primary">Save as new version</window.Btn></>
          } />
          <div style={{ padding: "4px 0 16px" }}>
            {Object.entries(d.keyParams).map(([k, v]) =>
            <div key={k} style={{ padding: "8px var(--pad)", display: "grid", gridTemplateColumns: "1fr auto", alignItems: "center", borderBottom: "1px dashed var(--rule)" }}>
                <span className="mono" style={{ color: "var(--muted)", fontSize: 12 }}>{k}</span>
                <input defaultValue={String(v)} style={{
                fontFamily: "var(--mono)", fontSize: 12, color: "var(--ink)", background: "transparent",
                border: "1px solid var(--rule-2)", padding: "3px 8px", textAlign: "right", minWidth: 100
              }} />
              </div>
            )}
          </div>
        </div>
        <div className="col-right">
          <div className="right-block">
            <h3>Provenance</h3>
            <div className="kv"><div className="k">Author</div><div className="v">{d.author}</div></div>
            <div className="kv"><div className="k">Validated</div><div className="v tnum">{d.validated}</div></div>
            <div className="kv"><div className="k">Basis</div><div className="v" style={{ fontSize: 11 }}>{d.basis}</div></div>
          </div>
          <div className="right-block">
            <h3>Source research</h3>
            {linkedResearch.length === 0 && <div style={{ color: "var(--muted)", fontSize: 12, fontFamily: "var(--mono)" }}>—</div>}
            {linkedResearch.map((r) =>
            <a key={r.id} href={`#/research/${r.id}`} style={{ display: "block", padding: "8px 0", borderBottom: "1px dashed var(--rule)", textDecoration: "none", color: "var(--ink)" }}>
                <div style={{ fontSize: 12.5, fontWeight: 500 }}>{r.title}</div>
                <div className="mono" style={{ fontSize: 11, color: "var(--muted)" }}>{r.tag} · {r.date}</div>
              </a>
            )}
          </div>
          {linkedProposals.length > 0 &&
          <div className="right-block" style={{ borderBottom: 0 }}>
              <h3>Open proposals</h3>
              {linkedProposals.map((p) =>
            <a key={p.id} href="#/research" style={{ display: "block", padding: "8px 0", borderBottom: "1px dashed var(--rule)", textDecoration: "none", color: "var(--ink)" }}>
                  <div style={{ fontSize: 12.5, fontWeight: 500 }}>{p.title}</div>
                  <div className="mono" style={{ fontSize: 11, color: "var(--muted)" }}>{p.kind.toUpperCase()} · strength {p.strength.toFixed(2)}</div>
                </a>
            )}
            </div>
          }
        </div>
      </div>

      {editing &&
      <StrategyEditor
        account={editing.account}
        editing={editing}
        onClose={() => setEditing(null)}
        onApply={() => bump((x) => x + 1)}
        onDelete={() => setEditing(null)} />

      }
    </>);

};

/* ── Accounts ─────────────────────────────────────────────── */
window.PageAccounts = function PageAccounts() {
  window.useDataLoad?.(1600);
  const [kind, setKind] = usePg("all");
  const [showAdd, setShowAdd] = usePg(false);
  const [confirmDel, setConfirmDel] = usePg(null);  // active account row
  const [confirmPurge, setConfirmPurge] = usePg(null); // inactive account row
  const all = window.ACCOUNTS || [];
  const inactive = window.ACCOUNTS_INACTIVE || [];
  const filtered = filterAccts(all, kind);
  const acctsLoading = window.__LIVE?.loaded?.accounts !== true;
  // Strategies attached to an account = the canonical guard for delete.
  // accounts.strategy is the comma-list seeded from the agent's HELLO.
  const hasStrategies = (a) => Array.isArray(a.strategies) ? a.strategies.length > 0 : false;
  return (
    <div>
      <window.SectionHeader title="Accounts" count={acctsLoading ? "—" : filtered.length}
        right={
          <div style={{display:"flex", gap:8, alignItems:"center"}}>
            <AccountKindFilter accts={all} value={kind} onChange={setKind} />
            <window.Btn kind="primary" onClick={() => setShowAdd(true)}>+ Add account</window.Btn>
          </div>
        } />
      {acctsLoading ? (
        <div style={{padding:"24px var(--pad)"}}>
          <window.SkelText lines={5} widths={["94%","88%","91%","86%","90%"]} />
        </div>
      ) : filtered.length === 0 ? (
        <div style={{padding:"24px var(--pad)",color:"var(--muted)",fontFamily:"var(--mono)",fontSize:12}}>
          {all.length === 0 ? "No accounts registered." : `No accounts in the "${kind}" filter.`}
        </div>
      ) : (
      <div className="table-wrap">
        <table className="strat">
          <thead><tr>
            <th>Account</th><th>Kind</th><th>Broker</th>
            <th className="num">Equity</th><th className="num">Day P&L</th>
            <th className="num">Session P&L</th><th className="num">MLL floor</th>
            <th className="num">Headroom</th><th>Instance</th>
            <th>Opened</th><th></th>
          </tr></thead>
          <tbody>
            {filtered.map((a) => { const preStaged = window.acctPreStaged && window.acctPreStaged(a); return (
            <tr key={a.uid || a.id} className="row-link" onClick={() => {location.hash = `#/accounts/${a.id}`;}}>
                <td>{a.label}{preStaged && (
                  <span title="Registered on the hub but not yet activated on the agent — add a strategy to activate." style={{
                    marginLeft: 7, fontFamily: "var(--mono)", fontSize: 9, fontWeight: 600,
                    letterSpacing: "0.05em", padding: "1px 5px", borderRadius: 3,
                    color: "var(--warn)", border: "1px solid var(--warn)", whiteSpace: "nowrap",
                  }}>◷ PRE-STAGED</span>
                )}{a.handsOff && (
                  <span title="HANDS-OFF — manual control; system auto-flatten suppressed" style={{
                    marginLeft: 7, fontFamily: "var(--mono)", fontSize: 9, fontWeight: 700,
                    letterSpacing: "0.05em", padding: "1px 5px", borderRadius: 3,
                    color: "var(--accent)", border: "1px solid var(--accent)", whiteSpace: "nowrap",
                  }}>✋ HANDS-OFF</span>
                )}{!a.handsOff && a.strategyPaused && Object.keys(a.strategyPaused).length > 0 && (
                  <span title="Trading paused — new entries halted for this account" style={{
                    marginLeft: 7, fontFamily: "var(--mono)", fontSize: 9, fontWeight: 600,
                    letterSpacing: "0.05em", padding: "1px 5px", borderRadius: 3,
                    color: "var(--warn)", border: "1px solid var(--warn)", whiteSpace: "nowrap",
                  }}>⏸ PAUSED</span>
                )}</td>
                <td><span className={"badge " + (a.kind || "unknown")} style={{ fontFamily: "var(--mono)", fontSize: 10, padding: "2px 6px", border: "1px solid currentColor" }}>{(a.kind || "unknown").toUpperCase()}</span></td>
                <td className="muted">{a.broker} · {a.code}</td>
                <td className="num tnum">{a.equityVerified ? window.fmt$(a.equity || 0, 2) : preStaged ? <span className="muted">—</span> : <window.Skel kind="line" w={84} style={{marginLeft:"auto"}} />}</td>
                <td className={"num tnum " + ((a.dayPnl || 0) >= 0 ? "pos" : "neg")}>{a.equityVerified ? window.fmt$(a.dayPnl || 0, 2) : preStaged ? <span className="muted">—</span> : <window.Skel kind="line" w={64} style={{marginLeft:"auto"}} />}</td>
                <td className={"num tnum " + ((a.totalPnl || 0) >= 0 ? "pos" : "neg")}>{a.equityVerified ? window.fmt$(a.totalPnl || 0, 0) : preStaged ? <span className="muted">—</span> : <window.Skel kind="line" w={60} style={{marginLeft:"auto"}} />}</td>
                <td className="num tnum muted">{a.equityVerified ? window.fmt$(typeof window.resolvedMllFloor === "function" ? window.resolvedMllFloor(a) : (a.mllFloor || 0), 0) : preStaged ? <span className="muted">—</span> : <window.Skel kind="line" w={64} style={{marginLeft:"auto"}} />}</td>
                <td className="num tnum">{a.equityVerified ? window.fmt$((a.equity || 0) - (typeof window.resolvedMllFloor === "function" ? window.resolvedMllFloor(a) : (a.mllFloor || 0)), 0) : preStaged ? <span className="muted">—</span> : <window.Skel kind="line" w={70} style={{marginLeft:"auto"}} />}</td>
                <td className="muted">{a.instance}</td>
                <td className="muted">{a.openedAt}</td>
                <td onClick={(e) => e.stopPropagation()} style={{textAlign:"right"}}>
                  <AccountRowDelete acct={a} hasStrategies={hasStrategies(a)}
                    onConfirm={() => setConfirmDel(a)} />
                </td>
              </tr>
            ); })}
          </tbody>
        </table>
      </div>
      )}

      <AccountCemetery rows={inactive} onPurge={(a) => setConfirmPurge(a)} />

      {showAdd && <AddAccountModal onClose={() => setShowAdd(false)} />}
      {confirmDel && <DeleteAccountModal acct={confirmDel} onClose={() => setConfirmDel(null)} />}
      {confirmPurge && <DeleteAccountModal acct={confirmPurge} hard onClose={() => setConfirmPurge(null)} />}
    </div>);

};

/* Per-row delete button. Disabled when strategies are still attached —
 * the guard mirrors the API (refuses with 409 on the same condition).
 * Keeping the click open in that state would surface as a confusing
 * server error instead of a clear "detach strategies first" hint. */
function AccountRowDelete({ acct, hasStrategies, onConfirm }) {
  const disabled = hasStrategies;
  const title = disabled
    ? "Detach all strategies before deleting this account"
    : `Delete ${acct.label} from the dashboard`;
  return (
    <button
      onClick={() => { if (!disabled) onConfirm(); }}
      title={title}
      disabled={disabled}
      style={{
        background: "transparent",
        color: disabled ? "var(--muted-2)" : "var(--neg)",
        border: `1px solid ${disabled ? "var(--rule)" : "var(--neg)"}`,
        fontFamily: "var(--mono)", fontSize: 10, padding: "2px 8px",
        letterSpacing: "0.06em", cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.55 : 1,
      }}>
      Delete
    </button>
  );
}

/* Failed-accounts (cemetery) panel — accounts the hub knows about with
 * active=0.  Rows arrive here from two sources:
 *   1. Operator soft-delete from the live table above.
 *   2. Agent HELLO replace_accounts() — the agent dropped this label
 *      from its config so the row was auto-deactivated.
 * Either way we keep the history rows around (events/fills/equity are
 * keyed on instance+label, not on this active flag) so the operator can
 * still open the detail page for post-mortem.  The Purge button hard-
 * deletes the dashboard row — history rows survive as orphans. */
function AccountCemetery({ rows, onPurge }) {
  if (!rows || rows.length === 0) return null;
  return (
    <div style={{marginTop: 32}}>
      <window.SectionHeader
        title="Failed accounts"
        count={rows.length}
        sub="soft-deleted or dropped from agent config — history preserved" />
      <div className="table-wrap">
        <table className="strat">
          <thead><tr>
            <th>Account</th><th>Kind</th><th>Broker</th>
            <th className="num">Last equity</th>
            <th className="num">Final P&L</th>
            <th>Instance</th><th></th>
          </tr></thead>
          <tbody>
            {rows.map((a) =>
              <tr key={`${a.instance}/${a.id}`} className="row-link"
                  onClick={() => { location.hash = `#/accounts/${a.id}`; }}>
                <td style={{color:"var(--muted)"}}>{a.label}</td>
                <td><span className={"badge " + (a.kind || "unknown")} style={{ fontFamily: "var(--mono)", fontSize: 10, padding: "2px 6px", border: "1px solid currentColor", opacity: 0.7 }}>{(a.kind || "unknown").toUpperCase()}</span></td>
                <td className="muted">{a.broker} · {a.code}</td>
                <td className="num tnum muted">{window.fmt$(a.equity || 0, 2)}</td>
                <td className={"num tnum " + ((a.totalPnl || 0) >= 0 ? "pos" : "neg")} style={{opacity:0.7}}>{window.fmt$(a.totalPnl || 0, 0)}</td>
                <td className="muted">{a.instance}</td>
                <td onClick={(e) => e.stopPropagation()} style={{textAlign:"right"}}>
                  <button
                    onClick={() => onPurge(a)}
                    title={`Permanently remove ${a.label} from the dashboard`}
                    style={{
                      background: "transparent", color: "var(--muted)",
                      border: "1px solid var(--rule-2)",
                      fontFamily: "var(--mono)", fontSize: 10, padding: "2px 8px",
                      letterSpacing: "0.06em", cursor: "pointer",
                    }}>
                    Purge
                  </button>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* Add-account picker.  Flow:
 *   1. Pick an instance (the agent we'll query).
 *   2. Pick a broker (only TopstepX today).
 *   3. Hub round-trips to the agent → TopstepX → returns real accounts.
 *   4. Operator picks a real account (kind/equity derived from broker),
 *      sets a short label, and submits.
 * Accounts already configured on the instance are shown but dimmed so
 * the operator can see context without re-registering them.
 */
function AddAccountModal({ onClose }) {
  const [brokers, setBrokers] = usePg(null);
  const [brokersErr, setBrokersErr] = usePg(null);
  const [closing, setClosing] = usePg(false);
  const handleClose = () => { setClosing(true); setTimeout(onClose, 160); };
  usePgE(() => {
    let mounted = true;
    window.fetchBrokers()
      .then((b) => { if (mounted) setBrokers(b); })
      .catch((e) => { if (mounted) setBrokersErr(String(e.message || e)); });
    return () => { mounted = false; };
  }, []);

  const instances = (window.INSTANCES || []);
  const [instanceId, setInstanceId] = usePg(instances[0]?.id || "");
  const [brokerName, setBrokerName] = usePg("TopstepX");

  // Live broker roster — null = not yet fetched, [] = empty response,
  // err string = round-trip failed (agent offline / 501 / 504).
  const [accounts, setAccounts] = usePg(null);
  const [acctsErr, setAcctsErr] = usePg(null);
  const [loadingAccts, setLoadingAccts] = usePg(false);
  const [reloadTick, setReloadTick] = usePg(0);

  const [selectedIdx, setSelectedIdx] = usePg(-1);
  const [label, setLabel] = usePg("");
  const [busy, setBusy] = usePg(false);
  const [submitErr, setSubmitErr] = usePg(null);

  // Fetch the broker roster whenever the operator switches instance/broker.
  // The picker is useless until that resolves — show a loader inline.
  usePgE(() => {
    if (!instanceId || !brokerName) return;
    let cancelled = false;
    setLoadingAccts(true);
    setAccounts(null);
    setAcctsErr(null);
    setSelectedIdx(-1);
    window.fetchBrokerAccounts(brokerName, instanceId)
      .then((res) => {
        if (cancelled) return;
        setAccounts(res.accounts || []);
      })
      .catch((e) => {
        if (cancelled) return;
        setAcctsErr(String(e.message || e));
      })
      .finally(() => { if (!cancelled) setLoadingAccts(false); });
    return () => { cancelled = true; };
  }, [instanceId, brokerName, reloadTick]);

  const selected = selectedIdx >= 0 && accounts ? accounts[selectedIdx] : null;

  async function submit() {
    setSubmitErr(null);
    if (!instanceId) { setSubmitErr("Pick an instance first."); return; }
    if (!selected) { setSubmitErr("Pick an account from the broker list."); return; }
    if (!label.trim()) { setSubmitErr("Label is required."); return; }
    setBusy(true);
    try {
      await window.registerAccount({
        instance_id: instanceId,
        label: label.trim(),
        broker: brokerName,
        account_name: selected.account_name,
        account_id: selected.account_id,
        // initial_capital from the broker's current balance for funded/xfa,
        // or null for combine/practice (the agent reads tier from config).
        initial_capital: typeof selected.balance === "number" ? selected.balance : null,
      });
      handleClose();
    } catch (e) {
      setSubmitErr(String(e.message || e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={"se-backdrop centered " + (closing ? "closing" : "")} onClick={handleClose}>
      <div className={"se-confirm-card " + (closing ? "closing" : "")}
           onClick={(e) => e.stopPropagation()}
           style={{width: 720, maxWidth: "92vw"}}>
        <div className="cf-head">
          <div>
            <div className="cf-title">Add account</div>
            <div className="cf-sub mono">pick a real broker account to register on a connected instance</div>
          </div>
          <button className="se-close" onClick={handleClose}>×</button>
        </div>

        <div className="cf-body" style={{display:"flex", flexDirection:"column", gap:14}}>
          {brokersErr && <div style={{color:"var(--neg)", fontFamily:"var(--mono)", fontSize:12}}>Failed to load brokers: {brokersErr}</div>}

          <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:12}}>
            <div>
              <div className="mono muted" style={{fontSize:10, letterSpacing:"0.08em", textTransform:"uppercase", marginBottom:6}}>Instance</div>
              {instances.length === 0 ? (
                <div className="muted mono" style={{fontSize:12}}>No instances connected. The agent must be online to query the broker.</div>
              ) : (
                <select value={instanceId} onChange={(e) => setInstanceId(e.target.value)}
                        style={{fontFamily:"var(--mono)", fontSize:12, padding:"6px 8px", width:"100%",
                                background:"var(--paper)", color:"var(--ink)", border:"1px solid var(--rule-2)"}}>
                  {instances.map((i) =>
                    <option key={i.id} value={i.id}>{i.id}{i.hostname ? ` · ${i.hostname}` : ""}</option>
                  )}
                </select>
              )}
            </div>
            <div>
              <div className="mono muted" style={{fontSize:10, letterSpacing:"0.08em", textTransform:"uppercase", marginBottom:6}}>Broker</div>
              {brokers === null ? (
                <div className="muted mono" style={{fontSize:12}}>Loading brokers…</div>
              ) : (
                <select value={brokerName} onChange={(e) => setBrokerName(e.target.value)}
                        style={{fontFamily:"var(--mono)", fontSize:12, padding:"6px 8px", width:"100%",
                                background:"var(--paper)", color:"var(--ink)", border:"1px solid var(--rule-2)"}}>
                  {brokers.map((b) => <option key={b.broker} value={b.broker}>{b.broker}</option>)}
                </select>
              )}
            </div>
          </div>

          <div>
            <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:6}}>
              <div className="mono muted" style={{fontSize:10, letterSpacing:"0.08em", textTransform:"uppercase"}}>
                Broker accounts {accounts ? `(${accounts.length} available)` : ""}
              </div>
              <button onClick={() => setReloadTick((t) => t + 1)} disabled={loadingAccts}
                      title="Re-query the agent for the latest broker roster"
                      style={{background:"transparent", color:"var(--muted)", border:"1px solid var(--rule-2)",
                              fontFamily:"var(--mono)", fontSize:10, padding:"2px 8px", cursor:loadingAccts?"wait":"pointer"}}>
                {loadingAccts ? "Loading…" : "Refresh"}
              </button>
            </div>
            {loadingAccts && <div className="muted mono" style={{fontSize:12, padding:"10px 0"}}>Querying {brokerName} via {instanceId}…</div>}
            {acctsErr && (
              <div style={{color:"var(--neg)", fontFamily:"var(--mono)", fontSize:12, padding:"8px 10px",
                           border:"1px solid var(--neg)", lineHeight:1.5}}>
                Couldn't load broker accounts: {acctsErr}
              </div>
            )}
            {accounts && accounts.length === 0 && !loadingAccts && !acctsErr && (
              <div className="muted mono" style={{fontSize:12, padding:"8px 0"}}>
                Broker returned no accounts. Check that the agent's API credentials are valid.
              </div>
            )}
            {accounts && accounts.length > 0 && (
              <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(260px, 1fr))",
                           gap:6, maxHeight:300, overflowY:"auto"}}>
                {accounts.map((a, i) => {
                  const sel = i === selectedIdx;
                  const dim = a.already_configured;
                  return (
                    <div key={`${a.account_id}-${i}`}
                         onClick={() => { if (!dim) setSelectedIdx(i); }}
                         title={dim ? "Already configured on this instance" : `Select ${a.account_name}`}
                         style={{
                           border: `1px solid ${sel ? "var(--ink)" : "var(--rule-2)"}`,
                           padding:"8px 10px",
                           cursor: dim ? "not-allowed" : "pointer",
                           opacity: dim ? 0.4 : 1,
                           background: sel ? "var(--rule)" : "transparent",
                         }}>
                      <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:4, gap:8}}>
                        <span className="mono" style={{fontSize:11, fontWeight:600, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>{a.account_name}</span>
                        <span className={"badge " + (a.kind || "unknown")} style={{fontFamily:"var(--mono)", fontSize:9, padding:"1px 5px", border:"1px solid currentColor"}}>{(a.kind || "").toUpperCase()}</span>
                      </div>
                      <div className="muted mono" style={{fontSize:10.5, display:"flex", justifyContent:"space-between"}}>
                        <span>id {a.account_id}</span>
                        <span>{typeof a.balance === "number" ? window.fmt$(a.balance, 0) : "—"}</span>
                      </div>
                      {dim && <div className="muted mono" style={{fontSize:9, marginTop:4, letterSpacing:"0.06em"}}>ALREADY CONFIGURED</div>}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div>
            <div className="mono muted" style={{fontSize:10, letterSpacing:"0.08em", textTransform:"uppercase", marginBottom:6}}>Label *</div>
            <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="combine1, prac, xfa…"
                   style={{fontFamily:"var(--mono)", fontSize:12, padding:"6px 8px", width:"100%",
                           background:"var(--paper)", color:"var(--ink)", border:"1px solid var(--rule-2)"}} />
            <div className="muted mono" style={{fontSize:10, marginTop:4}}>Short id shown on cards/tables. The broker account number is pulled from the picker above.</div>
          </div>

          {submitErr && <div style={{color:"var(--neg)", fontFamily:"var(--mono)", fontSize:12}}>Error: {submitErr}</div>}

          <div className="muted mono" style={{fontSize:10.5, padding:"8px 10px", border:"1px dashed var(--warn)", lineHeight:1.5, color:"var(--warn)"}}>
            <b>◷ This only PRE-STAGES the account.</b> The trading agent doesn't
            attach it until you add its <b>first strategy</b> — until then it
            shows as PRE-STAGED with no live data (no equity, no trading). After
            adding, open a strategies page and add a strategy targeting this
            account to activate it.
          </div>
        </div>

        <div className="cf-foot">
          <button className="ss-btn ghost" onClick={handleClose}>Cancel</button>
          <span style={{flex:1}} />
          <button className="ss-btn primary"
                  disabled={busy || !selected || !label.trim() || instances.length === 0}
                  onClick={submit}>
            {busy ? "Adding…" : "Add account"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* Confirm dialog used for both soft-delete (active rows) and hard-purge
 * (cemetery rows).  hard=true switches the destination endpoint. */
function DeleteAccountModal({ acct, hard = false, onClose }) {
  const [busy, setBusy] = usePg(false);
  const [err, setErr] = usePg(null);
  const [closing, setClosing] = usePg(false);
  const handleClose = () => { setClosing(true); setTimeout(onClose, 160); };

  async function submit() {
    setErr(null);
    setBusy(true);
    try {
      await window.deleteAccount(acct.instance, acct.id, { hard });
      handleClose();
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={"se-backdrop centered " + (closing ? "closing" : "")} onClick={handleClose}>
      <div className={"se-confirm-card " + (closing ? "closing" : "")} onClick={(e) => e.stopPropagation()}>
        <div className="cf-head">
          <div className="cf-icon">⚠</div>
          <div>
            <div className="cf-title">{hard ? "Purge" : "Delete"} {acct.label}?</div>
            <div className="cf-sub mono">on {acct.instance} · {acct.broker} {acct.code}</div>
          </div>
          <button className="se-close" onClick={handleClose}>×</button>
        </div>
        <div className="cf-body">
          {hard ? (
            <div className="muted mono" style={{fontSize:12, lineHeight:1.5}}>
              Permanently removes the row from the accounts table.
              Equity/fill/event history rows survive as orphans (keyed
              on instance+label so historical queries still work), but
              the dashboard will stop listing this account entirely.
            </div>
          ) : (
            <div className="muted mono" style={{fontSize:12, lineHeight:1.5}}>
              Marks the account inactive — it disappears from live
              cards & filters and lands in <b>Failed accounts</b>.
              History is preserved. If the agent's HELLO re-advertises
              this label, it comes back automatically.
            </div>
          )}
          {err && <div style={{marginTop:12, color:"var(--neg)", fontFamily:"var(--mono)", fontSize:12}}>Error: {err}</div>}
        </div>
        <div className="cf-foot">
          <button className="ss-btn ghost" onClick={handleClose}>Cancel</button>
          <span style={{flex:1}} />
          <button className="ss-btn danger" disabled={busy} onClick={submit}>
            {busy ? "Working…" : (hard ? "Purge permanently" : "Delete")}
          </button>
        </div>
      </div>
    </div>
  );
}

/* Account-detail header actions: Flatten all + Delete account.
 * Reconcile was removed — the agent runs its reconcile loop on a 10-
 * cycle cadence (see engine._run_periodic_checks_multi); a manual
 * "reconcile now" button added no value over the heartbeat dot the
 * Health panel already shows.
 *
 * Both buttons require the agent to be connected: flatten sends a
 * FLATTEN command, delete soft-deletes on the hub *and* sends
 * ACCOUNT_DISABLE so the running agent stops trading the account
 * immediately (not just at next HELLO).  If the agent is offline the
 * disable command 503's but the hub-side soft-delete still succeeds. */
function AccountDetailActions({ acct }) {
  const [flattening, setFlattening] = usePg(false);
  const [flattenErr, setFlattenErr] = usePg(null);
  const [confirmDel, setConfirmDel] = usePg(false);

  async function flatten() {
    setFlattenErr(null);
    const ok = window.confirmModal
      ? await window.confirmModal({
          title: "⚠ Flatten " + acct.label,
          body: `Sends market-close orders for every open position on ${acct.label}, across all strategies. This cannot be undone.`,
          confirmLabel: "Flatten all", danger: true,
        })
      : window.confirm(`Flatten ALL positions on ${acct.label}?`);
    if (!ok) return;
    setFlattening(true);
    try {
      await window.flattenAccount(acct.instance, acct.id);
      window.pushToast && window.pushToast({ kind: "success", title: "Flatten sent", msg: `${acct.label} — market-close orders sent` });
    } catch (e) {
      const m = String(e.message || e);
      setFlattenErr(m);
      window.pushToast && window.pushToast({ kind: "error", title: "Flatten failed", msg: `${acct.label}: ${m}` });
    } finally {
      setFlattening(false);
    }
  }
  const hasStrategies = Array.isArray(acct.strategies) && acct.strategies.length > 0;
  return (
    <>
      {flattenErr && (
        <span className="mono" style={{color:"var(--neg)", fontSize:11, marginRight:8}}>flatten failed: {flattenErr}</span>
      )}
      {/* Account-level pause/resume — halts / restores new entries for ALL
          strategies on this account (open positions untouched).  Same audited
          round-trip as the instance page; surfaced here so operators can
          control an account without leaving its detail view. */}
      <CmdBtn label="Pause" busyLabel="Pausing…" okMsg="paused"
        confirm={`Pause trading on ${acct.label}? Halts new entries for ALL strategies on this account. Open positions are left as-is.`}
        run={() => window.pauseInstance(acct.instance, acct.id)} />
      <CmdBtn label="Resume" kind="primary" busyLabel="Resuming…" okMsg="resumed"
        confirm={`Resume trading on ${acct.label}?`}
        run={() => window.resumeInstance(acct.instance, acct.id)} />
      {acct.handsOff
        ? <CmdBtn label="End hands-off" kind="primary" busyLabel="Releasing…" okMsg="hands-off released"
            confirm={`Return ${acct.label} to automated management? The reconciler and protective drawdown lock re-arm; strategies stay paused until you Resume.`}
            run={() => window.handsOffInstance(acct.instance, acct.id, false)} />
        : <CmdBtn label="Hands-off" kind="warn" busyLabel="Engaging…" okMsg="hands-off engaged"
            confirm={`HANDS-OFF ${acct.label} for manual trading?\n\nThe system stops touching this account: the reconciler will NOT flatten positions you open manually, and the protective drawdown (buffer) lock will NOT auto-flatten. Strategies are paused. TopStep's real MLL still applies — you own the risk.`}
            run={() => window.handsOffInstance(acct.instance, acct.id, true)} />}
      <window.Btn kind="danger" onClick={flatten}
                  title="Send FLATTEN to the agent — closes every open position on this account">
        {flattening ? "Flattening…" : "Flatten all"}
      </window.Btn>
      <window.Btn kind="danger"
                  onClick={() => setConfirmDel(true)}
                  title={hasStrategies
                    ? "Detach strategies first, then this account can be deleted"
                    : "Soft-delete this account — stops the agent from trading it and moves it to Failed accounts"}>
        Delete account
      </window.Btn>
      {confirmDel && (
        <DeleteAccountModal acct={acct} onClose={() => setConfirmDel(false)} />
      )}
    </>
  );
}

window.PageAccountDetail = function PageAccountDetail({ id, tweaks }) {
  // Keep the 250ms re-render ticker running so per-source loaded
  // flags propagate (same pattern as PageOverview).
  window.useDataLoad?.(1600);
  // Fall through to ACCOUNTS_INACTIVE when the active list misses —
  // operators clicking a cemetery row deserve the history view, not
  // a "not found" stub. The detail page reads from window.TRADES /
  // /api/equity which are keyed on (instance, label) and survive the
  // active=0 flip, so the page still renders correctly.
  const a = (window.ACCOUNTS || []).find((x) => x.id === id)
        || (window.ACCOUNTS_INACTIVE || []).find((x) => x.id === id);
  // NOTE: do NOT early-return on !a here — useDataLoad (above) and usePgM
  // (below) are hooks, and a conditional return between them changes the hook
  // count when `a` flips undefined→defined on a cold direct-load / refresh
  // (React #310).  All hooks must run unconditionally; the not-found bail-out
  // is moved BELOW usePgM.
  const acctsLoaded = window.__LIVE?.loaded?.accounts === true;
  const equityLoaded = window.__LIVE?.loaded?.equitySnapshot === true;
  const fillsLoaded = window.__LIVE?.loaded?.fills === true;

  // Total P&L is the AUTHORITATIVE realized P&L for the account —
  // computed at bootstrap as (current equity − allocated anchor).
  // For practice the agent reports the synthesized balance directly;
  // for combine/funded it's the broker balance.  Always non-null,
  // always honest regardless of the fills table state.  The
  // sum-from-trades version was zero for prac because the fills
  // table only has open entries (no exits yet) — but the equity has
  // grown from $150K anchor to $160,722.80 so there IS real P&L.
  const totalPnl = (a && typeof a.totalPnl === "number") ? a.totalPnl : 0;
  // The 4 Hz re-render cadence (App tickRate × useDataLoad 250ms) used
  // to walk window.TRADES — up to 5,000 rows on a busy account — for
  // every one of acctFills/trades/closedTrades/wins/losses/winRate/
  // avgWin/avgLoss/grossWin/grossLoss/pf/byDay/dayKeys/bestDay/
  // sortedDays/balSeries, FOUR times a second.  Wrap the whole bundle
  // in a usePgM so the heavy reductions only re-run when window.TRADES
  // is replaced (data-live.jsx replaces it wholesale on each fill
  // bootstrap) or the account identity changes.
  //
  // Every non-reconciler fill is a "trade event" the calendar should
  // surface — even when the agent's PnLAttributor failed to match it
  // against a prior lot (which happens on every agent restart because
  // the attributor's FIFO book is in-memory only).  Without this, days
  // where the agent restarted mid-session show up empty on the calendar
  // even though real fills landed and the equity curve moved.
  //
  // "closedTrades" stays the subset with explicit attribution
  // (is_exit=1 OR non-zero pnl_dollars).  WR / PF / avg-win-loss /
  // best-day / day-win-rate scope to closedTrades so unmatched entries
  // (pnl=0) don't dilute the rates.  Reconciler force-flattens still
  // get excluded from both — they're broker-side reconciliation, not
  // strategy fills.
  const TRADES = window.TRADES;
  const STRATS = window.STRATS;
  const {
    acctFills, trades, closedTrades, openEntries, reconcilerFills,
    wins, losses, winRate, avgWin, avgLoss, grossWin, grossLoss, pf,
    byDay, dayKeys, dayWins, dayWinRate, bestDay, bestDayPct,
    sortedDays, balSeries, acctStrats,
  } = usePgM(() => {
    if (!a) return {};   // not-found: hook still runs; values unused (bail below)
    const acctFills = (TRADES || []).filter((t) => t.account === a.id);
    const trades = acctFills.filter((t) => t.strategy !== "reconciler");
    const closedTrades = trades.filter((t) => t.isExit || (t.pnl && t.pnl !== 0));
    const openEntries = trades.filter((t) => !t.isExit && !(t.pnl && t.pnl !== 0));
    const reconcilerFills = acctFills.filter((t) => t.strategy === "reconciler");
    // Win-rate / PF / avg-win-loss are by definition closed-trade
    // metrics: an entry without a matched exit has pnl=0, which would
    // deflate WR toward 0 if counted in the denominator.
    const wins = closedTrades.filter((t) => (t.pnl || 0) > 0);
    const losses = closedTrades.filter((t) => (t.pnl || 0) < 0);
    const winRate = closedTrades.length ? wins.length / closedTrades.length : null;
    const avgWin = wins.length ? wins.reduce((s, t) => s + (t.pnl || 0), 0) / wins.length : null;
    const avgLoss = losses.length ? losses.reduce((s, t) => s + (t.pnl || 0), 0) / losses.length : null;
    const grossWin = wins.reduce((s, t) => s + (t.pnl || 0), 0);
    const grossLoss = Math.abs(losses.reduce((s, t) => s + (t.pnl || 0), 0));
    // No losses yet but real wins → PF is undefined as a ratio but
    // very clearly "all wins, no losses" — show ∞ instead of a dash
    // so the operator can tell the combine is healthy.  All-zeros
    // (no closed trades either way) stays as dash via the null branch.
    const pf = grossLoss > 0
      ? grossWin / grossLoss
      : (grossWin > 0 ? Infinity : null);
    // Bucket byDay by CME TRADING day (window.tradingDayKey, 17:00-CT
    // rollover) so each calendar cell matches TopStep's daily P&L exactly,
    // independent of the viewer's timezone.  The TradeCalendar grid cells
    // are labelled by calendar date — the same YYYY-MM-DD string a trade's
    // trading day resolves to — so cell and trades still align by key.
    const localDayKey = (iso) => window.tradingDayKey(iso);
    const byDay = {};
    trades.forEach((t) => {
      if (!t.exitTime) return;
      byDay[localDayKey(t.exitTime)] = (byDay[localDayKey(t.exitTime)] || 0) + (t.pnl || 0);
    });
    const dayKeys = Object.keys(byDay);
    const dayWins = dayKeys.filter((d) => byDay[d] > 0).length;
    const dayWinRate = dayKeys.length ? dayWins / dayKeys.length : null;
    const bestDay = dayKeys.length ? Math.max(0, ...Object.values(byDay)) : null;
    const bestDayPct = (bestDay != null && totalPnl > 0) ? bestDay / totalPnl : null;
    const sortedDays = Object.entries(byDay).sort(([x], [y]) => x.localeCompare(y));
    let bal = a.sessionStart;
    const balSeries = sortedDays.map(([d, p]) => {bal += p;return bal;});
    const acctStrats = (STRATS || []).filter((s) => s.acct === a.id && s.instance === a.instance);
    return {
      acctFills, trades, closedTrades, openEntries, reconcilerFills,
      wins, losses, winRate, avgWin, avgLoss, grossWin, grossLoss, pf,
      byDay, dayKeys, dayWins, dayWinRate, bestDay, bestDayPct,
      sortedDays, balSeries, acctStrats,
    };
  }, [TRADES, STRATS, a?.id, a?.sessionStart, totalPnl]);
  // All hooks have now run unconditionally — safe to bail when the account
  // isn't found (moved down from above usePgM to keep the hook count stable
  // across the cold-load undefined→defined transition; see #310 note above).
  if (!a) return <div style={{ padding: 32 }}>Account not found.</div>;
  // positions are derived from window.OPEN_POSITIONS / equity payload;
  // small array per call, leave outside the memo.
  const positions = window.positionsForAccount(a.id);
  const preStaged = window.acctPreStaged && window.acctPreStaged(a);

  return (
    <>
      <window.PageHeader
        crumbs={[{ label: "Accounts", href: "#/accounts" }, { label: a.label }]}
        actions={<AccountDetailActions acct={a} />} />
      {preStaged && (
        <div style={{
          margin: "0 var(--pad) 0", padding: "10px 14px",
          borderBottom: "1px solid var(--rule)",
          background: "rgba(255,165,0,0.07)", color: "var(--warn)",
          fontFamily: "var(--mono)", fontSize: 12, lineHeight: 1.5,
        }}>
          <b>◷ PRE-STAGED — not activated on the agent.</b> This account was added to the hub
          but the agent attaches an account only when its <b>first strategy</b> is added — until
          then there's no live equity, so the page can't populate. Add a strategy to activate it
          (add one from a strategies page and target this account), or delete it if it was a mistake.
        </div>
      )}


      <div className="acct-hero">
        <div className="left">
          <div style={{ display: "flex", alignItems: "baseline", gap: 12 }}>
            <span className={"badge " + a.kind} style={{ fontFamily: "var(--mono)", fontSize: 11, padding: "2px 8px", border: "1px solid currentColor", letterSpacing: "0.06em" }}>{a.kind.toUpperCase()}</span>
            <span style={{ fontFamily: "var(--mono)", color: "var(--muted)", fontSize: 12 }}>{a.broker} · {a.code} · acct/{a.id}</span>
          </div>
          <div className="equity-num tnum" style={{ marginTop: 14 }}>
            {(equityLoaded && a.equityVerified)
              ? window.fmt$(a.equity, 2)
              : preStaged ? <span className="muted">—</span>
              : <window.Skel kind="line-lg" w={180} />}
          </div>
          <div className={"equity-sub " + (preStaged ? "muted" : (a.dayPnl || 0) >= 0 ? "pos" : "neg")}>
            {(equityLoaded && a.equityVerified)
              ? <>{window.fmt$(a.dayPnl || 0, 2)} day · <span className="muted">{window.fmt$(a.totalPnl || 0, 0)} session · opened {a.openedAt}</span></>
              : preStaged ? <span className="muted">no live data · add a strategy to activate</span>
              : <window.Skel kind="line-sm" w={260} />}
          </div>
          {(() => {
            // Guard the MLL bar geometry: every leg can be 0/null on
            // a fresh load, and a NaN width breaks the SVG.  When the
            // account hasn't been verified by an agent equity push yet,
            // skip the geometry entirely and render a shimmer placeholder
            // — drawing the bar against an all-zero stack would read as
            // "this account is at the MLL floor", not "we're still
            // loading", which is exactly the wrong signal for a brand-new
            // combine that the operator just registered.
            if (!a.equityVerified && preStaged) {
              return <div style={{ marginTop: 12, fontFamily: "var(--mono)", fontSize: 11, color: "var(--muted)" }}>MLL / target unavailable until the account is activated.</div>;
            }
            if (!a.equityVerified) {
              return (
                <div style={{ marginTop: 18 }}>
                  <div className="mll-row" style={{ marginBottom: 6 }}>
                    <window.Skel kind="line-sm" w={120} />
                    <window.Skel kind="line-sm" w={160} />
                  </div>
                  <window.Skel h={6} style={{display:"block",width:"100%"}} />
                </div>
              );
            }
            const equity   = Number(a.equity) || 0;
            const mllFloor = typeof window.resolvedMllFloor === "function"
              ? window.resolvedMllFloor(a) : (Number(a.mllFloor) || 0);
            const sessStart = Number(a.sessionStart) || equity || (a.initial_capital || 0);
            // Same resolution chain as AccountCard — agent goal_state →
            // tier rule fallback → effective consistency-adjusted target.
            let target = Number(a.target) || 0;
            if (!target && typeof window.ACCOUNT_KIND_RULES === "function") {
              const r = window.ACCOUNT_KIND_RULES(a.kind, a.initial_capital);
              if (r && r.target_dollars > 0) target = r.target_dollars;
            }
            const consistency = Number(a.consistencyPct) || 0;
            const bestDay = Math.max(
              Number(a.bestDayPnl) || 0,
              Number(a.todayGoalPnl) || 0,
            );
            let effectiveTarget = Number(a.effectiveTarget) || 0;
            if (!effectiveTarget && target > 0 && consistency > 0 && bestDay > 0) {
              effectiveTarget = Math.max(target, bestDay / consistency);
            }
            if (!effectiveTarget) effectiveTarget = target;
            const targetIsBumped = effectiveTarget > target + 0.5;
            const rangeLo = mllFloor || sessStart * 0.95;
            const rangeHi = effectiveTarget > 0 ? sessStart + effectiveTarget : sessStart * 1.05;
            const denom = Math.max(1, rangeHi - rangeLo);
            const fillPct = Math.max(0, Math.min(1, (equity - rangeLo) / denom));
            const staticPos = (target > 0 && effectiveTarget > 0)
              ? Math.max(0, Math.min(1, (sessStart + target - rangeLo) / denom))
              : null;
            return (
              <div style={{ marginTop: 18 }}>
                <div className="mll-row" style={{ marginBottom: 6 }}>
                  <span>MLL floor {window.fmt$(mllFloor, 0)}</span>
                  <span title={targetIsBumped
                    ? `static target ${window.fmt$(target, 0)}; best day ${window.fmt$(bestDay, 0)} × (1/consistency_pct ${consistency.toFixed(2)}) = effective ${window.fmt$(effectiveTarget, 0)} required to pass`
                    : undefined}>
                    {effectiveTarget > 0
                      ? (targetIsBumped
                          ? `target ${window.fmt$(sessStart + effectiveTarget, 0)} (consistency: ${window.fmt$(target, 0)} → ${window.fmt$(effectiveTarget, 0)})`
                          : `target ${window.fmt$(sessStart + effectiveTarget, 0)}`)
                      : "no target — survival mode"}
                  </span>
                </div>
                <div className="mll-bar">
                  <div className="fill" style={{ width: (fillPct * 100).toFixed(2) + "%" }} />
                  <div className="floor" style={{ left: "0%" }} />
                  {targetIsBumped && staticPos != null && (
                    <div className="target" style={{
                      left: (staticPos * 100).toFixed(2) + "%",
                      background: "var(--muted)", opacity: 0.5,
                    }} title={`static target ${window.fmt$(target, 0)}`} />
                  )}
                  {effectiveTarget > 0 && (
                    <div className="target" style={{ left: "100%" }}
                      title={targetIsBumped
                        ? `effective target (consistency-adjusted) ${window.fmt$(effectiveTarget, 0)}`
                        : `target ${window.fmt$(effectiveTarget, 0)}`} />
                  )}
                </div>
              </div>);
          })()}
        </div>
        <div className="right">
          {(() => {
            // Positions staleness signal: a.strategiesLastUs is set on
            // every /api/positions read + every equity WS frame.  When
            // it's old, the position list (and the "25c MNQ short"
            // numbers next to it) is whatever the hub last heard from
            // the agent — NOT what the broker holds right now.
            // Surface a STALE badge so the operator doesn't trust a
            // ghost position that the broker already closed.
            const lastUs = Number(a.strategiesLastUs) || 0;
            const ageS = lastUs ? Math.floor((Date.now() * 1000 - lastUs) / 1_000_000) : null;
            const stale = ageS != null && ageS > 300;
            const ageTxt = ageS == null
              ? null
              : ageS < 60 ? `${ageS}s` : ageS < 3600 ? `${Math.round(ageS/60)}m` : `${Math.round(ageS/3600)}h`;
            return (
              <div style={{
                fontFamily: "var(--mono)",
                fontSize: 10.5,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "var(--muted)",
                marginBottom: 8,
                display: "flex",
                alignItems: "center",
                gap: 8,
                flexWrap: "wrap",
              }}>
                <span>Open positions · {positions.length}</span>
                {ageTxt && (
                  <span style={{
                    fontSize: 10,
                    padding: "1px 6px",
                    border: `1px solid ${stale ? "var(--neg)" : "var(--rule-2)"}`,
                    color: stale ? "var(--neg)" : "var(--muted)",
                  }} title={stale
                    ? `agent last reported positions ${ageTxt} ago — broker state may have changed; values below could be stale`
                    : `last position update ${ageTxt} ago`}>
                    {stale ? "STALE · " : ""}as of {ageTxt} ago
                  </span>
                )}
              </div>
            );
          })()}
          {positions.length === 0 ?
          <div style={{ color: "var(--muted)", fontFamily: "var(--mono)", fontSize: 12 }}>flat — nothing to flatten</div> :

          <div>
                <div style={{ maxHeight: 240, overflowY: "auto", borderTop: "1px solid var(--rule)" }}>
                  {positions.map((p, i) => {
                    const isOrphan = !!p.orphan || p.strategy_id === "(orphan)";
                    return (
                    <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr auto auto auto", gap: 12, padding: "8px 0", borderBottom: "1px dashed var(--rule)", fontFamily: "var(--mono)", alignItems: "center", background: isOrphan ? "rgba(255, 165, 0, 0.08)" : undefined }}>
                      <span>
                        <b>{p.sym ?? p.symbol ?? "—"}</b>{" "}
                        <span className={(p.side === "long") ? "pos" : "neg"} style={{ fontSize: 11, letterSpacing: "0.06em" }}>{(p.side || "").toUpperCase()}</span>
                        {isOrphan && (
                          <span className="warn" style={{ marginLeft: 8, fontSize: 10, letterSpacing: "0.08em", padding: "1px 4px", border: "1px solid currentColor" }}
                                title={`broker holds this position but no strategy on this account claims it. Reconciler will normally flatten on its next cycle; if it doesn't, contract_id ${p.contract_id || "?"} may not be mapped in market_data.contracts (see service.log for "UNMAPPED contract_id" CRITICAL).`}>
                            ORPHAN
                          </span>
                        )}
                        {!isOrphan && p.strategy_id && (
                          <span className="muted" style={{ marginLeft: 8, fontSize: 10, letterSpacing: "0.06em" }}>{p.strategy_id}</span>
                        )}
                      </span>
                      <span className="tnum">{p.qty}c</span>
                      <span className="muted tnum" style={{ fontSize: 11 }}>@ {Number(p.entry ?? p.entry_price ?? 0).toFixed(2)} → {Number(p.mark ?? p.entry ?? p.entry_price ?? 0).toFixed(2)}</span>
                      <span className={"tnum " + ((p.unreal ?? p.unrealized ?? 0) >= 0 ? "pos" : "neg")}>{window.fmt$(p.unreal ?? p.unrealized ?? 0, 2)}</span>
                    </div>);
                  })}
                </div>
                <div style={{ marginTop: 10, display: "flex", gap: 6 }}>
                  <window.Btn kind="danger">Close all</window.Btn>
                  <window.Btn>Hedge</window.Btn>
                </div>
              </div>
          }
        </div>
      </div>

      {(() => {
        // Shimmer the entire stats row until /api/fills resolved.
        // After that: real values when computable, "—" when the
        // computation is genuinely unknowable (no exit fills →
        // no win rate).
        // Shimmer the whole row until /api/fills resolved OR the account
        // hasn't been verified by an equity push yet — Total P&L is
        // derived from a.totalPnl = a.equity - anchor, which reads $0
        // before the first equity tick lands for a fresh combine.
        if (preStaged) {
          return (
            <div style={{ padding: "20px var(--pad)", color: "var(--muted)", fontFamily: "var(--mono)", fontSize: 12 }}>
              No trades — this account isn't active on the agent yet. Add a strategy to activate it.
            </div>
          );
        }
        if (!fillsLoaded || !a.equityVerified) {
          return (
            <div className="kpis" style={{ gridTemplateColumns: "repeat(6,1fr)" }}>
              {Array.from({length:6}).map((_,i) =>
                <div className="kpi" key={i}>
                  <div className="lbl"><window.Skel kind="line-sm" w={90} /></div>
                  <window.Skel kind="line-lg" w="60%" style={{marginTop:6}} />
                  <window.Skel kind="line-sm" w="80%" style={{marginTop:6}} />
                </div>
              )}
            </div>);
        }
        const dash = "—";
        const wrTxt = (winRate != null) ? (winRate * 100).toFixed(2) + "%" : dash;
        const dwTxt = (dayWinRate != null) ? (dayWinRate * 100).toFixed(2) + "%" : dash;
        const pfTxt = pf == null
          ? dash
          : (Number.isFinite(pf) ? pf.toFixed(2) : "∞");
        const awalTxt = (avgWin != null && avgLoss != null && avgLoss !== 0)
          ? Math.abs(avgWin / avgLoss).toFixed(2)
          : (avgWin != null && (avgLoss == null || avgLoss === 0))
            ? "∞"   // wins recorded but zero losses — same "all winners" signal as PF
            : dash;
        const bdpTxt = (bestDayPct != null) ? (bestDayPct * 100).toFixed(2) + "%" : dash;
        return (
          <div className="kpis" style={{ gridTemplateColumns: "repeat(6,1fr)" }}>
            <KpiCard lbl="Total P&L" val={window.fmt$(totalPnl || 0, 2)} cls={(totalPnl || 0) >= 0 ? "pos" : "neg"} />
            <KpiCard lbl="Trade Win %" val={wrTxt} sub={
              <span className="mono" style={{ fontSize: 11 }}><span className="pos">{wins.length}</span> / <span className="neg">{losses.length}</span></span>
            } dial={<HalfDial v={winRate || 0} />} />
            <KpiCard lbl="Avg Win / Avg Loss" val={awalTxt} sub={
              <span className="mono" style={{ fontSize: 11 }}><span className="pos">{window.fmt$(avgWin || 0, 2)}</span>  <span className="neg">{window.fmt$(avgLoss || 0, 2)}</span></span>
            } />
            <KpiCard lbl="Day Win %" val={dwTxt} sub={`${dayWins}/${dayKeys.length} days`} dial={<HalfDial v={dayWinRate || 0} />} />
            <KpiCard lbl="Profit Factor" val={pfTxt} cls={(pf != null && pf >= 1) ? "pos" : "neg"} sub={
              <span className="mono" style={{ fontSize: 11 }}><span className="pos">{window.fmt$(grossWin || 0, 0)}</span>  <span className="neg">−{window.fmt$(grossLoss || 0, 0)}</span></span>
            } />
            <KpiCard lbl="Best Day · % of Profit" val={bdpTxt} sub={window.fmt$(bestDay || 0, 2)} />
          </div>);
      })()}

      {(() => {
        // Performance · research vs live — gate on the strategies
        // flag (step 4b — backfills a.strategies from the latest
        // equity event when /api/accounts doesn't have them),
        // fills, AND the SPC fetch.  All three feed the row, so
        // shimmering until all three resolve is the honest
        // behavior — confirmed-empty states ("No strategies
        // deployed", "No z-score data") only appear after the
        // data layer has actually checked.
        const strategiesLoaded = window.__LIVE?.loaded?.strategies === true;
        const fillsLoaded2 = window.__LIVE?.loaded?.fills === true;
        const spcLoaded = window.__LIVE?.loaded?.spc === true;
        const ready = strategiesLoaded && fillsLoaded2 && spcLoaded;
        return (<>
          <window.SectionHeader title="Performance · research vs live"
            sub={ready ? `${acctStrats.length} deployment${acctStrats.length === 1 ? "" : "s"}` : ""} />
          {!ready
            ? <div style={{padding:"14px var(--pad) 22px"}}>
                <div className="skel-stack" style={{display:"flex",gap:14,marginBottom:12}}>
                  <window.Skel kind="line" w={68} />
                  <window.Skel kind="line" w={68} />
                  <window.Skel kind="line" w={68} />
                </div>
                <window.Skel h={240} style={{display:"block",width:"100%"}} />
                <div className="skel-stack" style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:18,marginTop:14}}>
                  <window.Skel kind="line-lg" />
                  <window.Skel kind="line-lg" />
                  <window.Skel kind="line-lg" />
                  <window.Skel kind="line-lg" />
                </div>
              </div>
            : (<>
                <div className="perf-split">
                  <div className="perf-plot">
                    <ResearchVsLivePlot strats={acctStrats} />
                  </div>
                  <div className="perf-zscore">
                    <h3 className="perf-zscore-h">SPC · z-band by session</h3>
                    <AccountSpcPanel acctStrats={acctStrats} />
                  </div>
                </div>
                {acctStrats.length > 0 && <DivergenceMetricsRow strats={acctStrats} />}
              </>)}
        </>);
      })()}

      {(() => {
        const strategiesLoaded = window.__LIVE?.loaded?.strategies === true;
        if (!strategiesLoaded) {
          return (
            <div style={{padding:"14px var(--pad) 22px"}}>
              <window.Skel kind="line-lg" w={180} style={{marginBottom:10}} />
              {[0,1,2].map(i =>
                <div key={i} style={{display:"grid",gridTemplateColumns:"10px 1fr auto",gap:14,padding:"10px 0",borderBottom:"1px dashed var(--rule)"}}>
                  <window.Skel kind="square" />
                  <window.Skel kind="line" />
                  <window.Skel kind="line-lg" w={80} />
                </div>
              )}
            </div>);
        }
        return <StrategyManager account={a} acctStrats={acctStrats} />;
      })()}

      <window.SectionHeader title="Trade calendar"
        sub={fillsLoaded
          ? (trades.length === 0
              ? "no fills yet — calendar populates as the agent sends fills"
              : `${new Date().toLocaleDateString("en-US", { month: "long", year: "numeric" })} · ${closedTrades.length} closed · ${openEntries.length} unmatched`)
          : "loading…"} />
      {!fillsLoaded
        ? <div style={{padding:"14px var(--pad)"}}><window.Skel h={220} style={{display:"block",width:"100%"}} /></div>
        : <TradeCalendar byDay={byDay} trades={trades} />}

      {fillsLoaded && (() => {
        // Data-freshness audit: surface what the hub HAS vs what
        // looks suspicious.  Helps the operator immediately see
        // "the broker says 3 trades today, the hub has 1" without
        // having to mentally diff the two systems.
        const untracked = trades.filter((t) => !t.strategy || !String(t.strategy).trim()).length;
        const lastFillMs = trades.reduce((m, t) => {
          const ts = t.exitTime ? Date.parse(t.exitTime) : 0;
          return Number.isFinite(ts) && ts > m ? ts : m;
        }, 0);
        const lastFillAgo = lastFillMs
          ? Math.max(0, Math.floor((Date.now() - lastFillMs) / 1000))
          : null;
        const fmtAge = (s) => s == null ? "—" :
          s < 60 ? `${s}s` : s < 3600 ? `${Math.round(s/60)}m` : `${Math.round(s/3600)}h`;
        const stratsLastUs = Number(a.strategiesLastUs) || 0;
        const stratsAgeS = stratsLastUs
          ? Math.floor((Date.now() * 1000 - stratsLastUs) / 1_000_000)
          : null;
        const anyConcern = untracked > 0 || (lastFillAgo != null && lastFillAgo > 7200) ||
          (stratsAgeS != null && stratsAgeS > 300);
        if (!anyConcern && trades.length > 0) return null;
        return (
          <div style={{
            margin: "8px var(--pad) 0",
            padding: "10px 14px",
            border: `1px solid ${anyConcern ? "var(--warn)" : "var(--rule)"}`,
            fontFamily: "var(--mono)",
            fontSize: 11,
            color: "var(--ink-2)",
            background: anyConcern ? "rgba(255, 165, 0, 0.04)" : undefined,
            lineHeight: 1.6,
          }}>
            <div style={{
              fontSize: 10,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: anyConcern ? "var(--warn)" : "var(--muted)",
              marginBottom: 6,
            }}>data freshness · what the hub knows</div>
            <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(180px, 1fr))", gap: 10}}>
              <div>
                <span className="muted">fills in hub</span>{" "}
                <b>{trades.length}</b>
                {untracked > 0 && <span className="warn"> · {untracked} untracked</span>}
              </div>
              <div>
                <span className="muted">last fill</span>{" "}
                <b>{fmtAge(lastFillAgo)}</b> ago
              </div>
              <div>
                <span className="muted">position state</span>{" "}
                <b>{fmtAge(stratsAgeS)}</b> ago
                {stratsAgeS != null && stratsAgeS > 300 && <span className="neg"> · stale</span>}
              </div>
            </div>
            {(untracked > 0 || (stratsAgeS != null && stratsAgeS > 300)) && (
              <div style={{marginTop: 8, color: "var(--muted)", fontSize: 10.5}}>
                If broker history shows trades the hub doesn't, the agent
                wasn't connected at fill time, or it lost the WS link between
                fill and ack — fills committed to the broker but never landed
                in <code>/api/fills</code>.  Untracked fills are real but lack
                a <code>strategy_id</code>: manual orders, agent restart
                fills, or pre-attribution builds.
              </div>
            )}
          </div>
        );
      })()}

      <window.SectionHeader title="Trades"
        count={fillsLoaded ? trades.length : "—"}
        sub={fillsLoaded
          ? `${openEntries.length} open entr${openEntries.length === 1 ? "y" : "ies"} · ${reconcilerFills.length} reconciler`
          : "loading…"} />
      {!fillsLoaded
        ? <div style={{padding:"14px var(--pad)"}}>
            <window.SkelText lines={4} widths={["95%","90%","92%","88%"]} />
          </div>
        : trades.length === 0
          ? <div style={{padding:"24px var(--pad)",color:"var(--muted)",fontFamily:"var(--mono)",fontSize:12,lineHeight:1.6}}>
              No closed trades yet for this account.
              {openEntries.length > 0 && <> The account has <b>{openEntries.length} open entry fill{openEntries.length === 1 ? "" : "s"}</b> — round-trip P&L will appear once the agent emits the matching exit fills.</>}
              {reconcilerFills.length > 0 && <> <br/>(<b>{reconcilerFills.length}</b> reconciler force-flatten{reconcilerFills.length === 1 ? "" : "s"} hidden from this view.)</>}
            </div>
          : <TradesTable trades={trades} />}
    </>);

};

function KpiCard({ lbl, val, sub, cls, dial }) {
  return (
    <div className="kpi">
      <div className="lbl">{lbl}</div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <div className={"val mono tnum " + (cls || "")}>{val}</div>
        {dial}
      </div>
      {sub && <div className="delta mono">{sub}</div>}
    </div>);

}
function HalfDial({ v, c = "pos" }) {
  const r = 18,cx = 22,cy = 22;const a = Math.PI * (1 - Math.max(0, Math.min(1, v)));
  const x2 = cx + r * Math.cos(a),y2 = cy - r * Math.sin(a);
  const fill = c === "pos" ? "var(--pos)" : "var(--neg)";
  return (
    <svg width="44" height="26" viewBox="0 0 44 26">
      <path d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`} fill="none" stroke="var(--rule-2)" strokeWidth="3" />
      <path d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${x2.toFixed(2)} ${y2.toFixed(2)}`} fill="none" stroke={fill} strokeWidth="3" />
    </svg>);

}

function TradeCalendar({ byDay, trades }) {
  const today = new Date();
  const [view, setView] = usePg({ yr: today.getFullYear(), mo: today.getMonth() });
  const yr = view.yr,mo = view.mo;
  const first = new Date(yr, mo, 1);const startWk = (first.getDay() + 6) % 7; // Mon=0..Sun=6
  const daysInMo = new Date(yr, mo + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startWk; i++) cells.push({ blank: true, n: new Date(yr, mo, 1 - (startWk - i)).getDate() });
  for (let d = 1; d <= daysInMo; d++) cells.push({ n: d, date: new Date(yr, mo, d) });
  while (cells.length % 7 !== 0) cells.push({ blank: true, n: cells.length - daysInMo - startWk + 1 });
  const weeks = [];for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  // Use LOCAL date components — toISOString converts to UTC, so a
  // user in UTC-5 viewing the dashboard at 11pm local on Sunday May 17
  // would get "2026-05-18" (Monday) as todayKey and the highlight
  // would land on the wrong cell.
  const fmtKey = (d) => {
    const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, "0"), dd = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${dd}`;
  };
  // Trades bucket by CME TRADING day (17:00-CT rollover) to match TopStep;
  // grid cells stay calendar-dated (fmtKey) and align by the same date key.
  const tradeCount = (k) => (trades || []).filter((t) => window.tradingDayKey(t.exitTime) === k).length;
  const todayKey = window.tradingDayKey(today);
  const monthLbl = new Date(yr, mo, 1).toLocaleDateString("en-US", { month: "long", year: "numeric" });
  const step = (d) => setView((v) => {
    const dt = new Date(v.yr, v.mo + d, 1);
    return { yr: dt.getFullYear(), mo: dt.getMonth() };
  });

  // month days with trades, used both for stats and to pick a default selection
  const monthKeys = [];
  for (let d = 1; d <= daysInMo; d++) {
    const k = fmtKey(new Date(yr, mo, d));
    if (byDay[k] !== undefined) monthKeys.push(k);
  }

  // selected-day state — defaults to today (or 1st of month when viewing
  // a different month). Clicking a cell sets it.
  const pickDefault = () => {
    const inMonth = today.getFullYear() === yr && today.getMonth() === mo;
    return inMonth ? todayKey : fmtKey(new Date(yr, mo, 1));
  };
  const [selectedDay, setSelectedDay] = usePg(pickDefault());
  usePgE(() => {setSelectedDay(pickDefault()); /* eslint-disable-next-line */}, [yr, mo]);

  return (
    <div className="cal-layout">
      <div className="cal-main">
        <div className="cal-head">
          <div className="nav">
            <button onClick={() => step(-1)} title="Previous month">‹</button>
            <span className="title">{monthLbl}</span>
            <button onClick={() => step(1)} title="Next month">›</button>
          </div>
          <button className="today" onClick={() => setView({ yr: today.getFullYear(), mo: today.getMonth() })}>Today</button>
        </div>
        <div className="cal-grid">
          {["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"].map((d) => <div key={d} className="cal-dow">{d}</div>)}
          <div className="cal-dow right">Week</div>
          {weeks.map((wk, wi) => {
            const wkPnl = wk.filter((c) => !c.blank).reduce((s, c) => s + (byDay[fmtKey(c.date)] || 0), 0);
            const wkN = wk.filter((c) => !c.blank).reduce((s, c) => s + tradeCount(fmtKey(c.date)), 0);
            return (
              <React.Fragment key={wi}>
                {wk.map((c, i) => {
                  if (c.blank) return <div key={i} className="cal-cell blank"><span className="n">{c.n}</span></div>;
                  const k = fmtKey(c.date);const pnl = byDay[k] || 0;const n = tradeCount(k);
                  const isToday = k === todayKey;
                  const isSelected = k === selectedDay;
                  const isInteractive = true; // every real day is clickable
                  const bg = pnl > 0 ? "oklch(0.58 0.13 155 / 0.10)" : pnl < 0 ? "oklch(0.55 0.18 25 / 0.10)" : "transparent";
                  const cls = pnl > 0 ? "pos" : pnl < 0 ? "neg" : "";
                  return (
                    <div key={i}
                    className={"cal-cell" + (isToday ? " today" : "") + (isSelected ? " selected" : "") + (isInteractive ? " clickable" : "")}
                    style={{ background: bg }}
                    onClick={isInteractive ? () => setSelectedDay(k) : undefined}>
                      
                      <span className="n">{c.n}</span>
                      {n > 0 && <>
                        <div className={"pnl " + cls}>{window.fmt$(pnl, 2)}</div>
                        <div className="nt">{n} trade{n !== 1 ? "s" : ""}</div>
                      </>}
                    </div>);

                })}
                <div className="cal-week">
                  <div className="lbl">Week {wi + 1}</div>
                  {wkN > 0 ?
                  <><div className={"v " + (wkPnl > 0 ? "pos" : wkPnl < 0 ? "neg" : "")}>{window.fmt$(wkPnl, 2)}</div><div className="nt">{wkN} trades</div></> :
                  <div className="v muted">—</div>}
                </div>
              </React.Fragment>);

          })}
        </div>
      </div>
      <aside className="cal-day">
        <DayTradesPanel day={selectedDay} trades={trades || []} byDay={byDay} />
      </aside>
    </div>);

}

function DayTradesPanel({ day, trades, byDay }) {
  if (!day) {
    return (
      <div className="day-empty mono">
        <div className="day-empty-h">No day selected</div>
        <div className="day-empty-s">Click any trading day on the calendar to see the trades and exits for that session.</div>
      </div>);

  }
  // Match the calendar's CME trading-day key (window.tradingDayKey,
  // 17:00-CT rollover) so the day panel lists exactly the trades the cell
  // counted — and both agree with TopStep's daily grouping.
  const localDayKey = (iso) => window.tradingDayKey(iso);
  const dayTrades = trades.
  filter((t) => localDayKey(t.exitTime) === day).
  sort((a, b) => new Date(a.exitTime) - new Date(b.exitTime));
  // Closed-only subset for WR/PF/biggest — entries dilute the rates.
  const dayClosed = dayTrades.filter((t) => t.isExit || (t.pnl && t.pnl !== 0));
  const dayEntries = dayTrades.length - dayClosed.length;
  const date = new Date(day);
  const headLine = date.toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" });
  const wins = dayClosed.filter((t) => t.pnl > 0);
  const losses = dayClosed.filter((t) => t.pnl < 0);
  const dayPnl = byDay[day] || 0;
  const gross = dayTrades.reduce((s, t) => s + (t.commissions + t.fees), 0);
  const wr = dayClosed.length ? wins.length / dayClosed.length : 0;
  const big = dayClosed.reduce((b, t) => Math.abs(t.pnl) > Math.abs(b.pnl) ? t : b, { pnl: 0 });

  return (
    <div className="day-panel">
      <div className="day-head">
        <div>
          <div className="day-date mono">{headLine}</div>
          <div className="day-date-sub mono">
            {dayClosed.length} closed
            {dayEntries > 0 ? <> · <span title="Fills without a matched closing leg — typically a position opened before the last agent restart (the PnLAttributor lot book is in-memory only and resets on restart)">{dayEntries} unmatched</span></> : null}
            {dayClosed.length > 0 ? <> · WR {(wr * 100).toFixed(0)}%</> : null}
          </div>
        </div>
        <div className={"day-pnl tnum mono " + (dayPnl >= 0 ? "pos" : "neg")}>{window.fmt$(dayPnl, 2)}</div>
      </div>

      <div className="day-stats mono">
        <div className="day-stat">
          <div className="lbl">Wins</div>
          <div className="v tnum pos">{wins.length}</div>
        </div>
        <div className="day-stat">
          <div className="lbl">Losses</div>
          <div className="v tnum neg">{losses.length}</div>
        </div>
        <div className="day-stat">
          <div className="lbl">Fees</div>
          <div className="v tnum">{window.fmt$(gross, 2)}</div>
        </div>
        <div className="day-stat">
          <div className="lbl">Biggest</div>
          <div className={"v tnum " + (big.pnl >= 0 ? "pos" : "neg")}>{big.pnl ? window.fmt$(big.pnl, 0) : "—"}</div>
        </div>
      </div>

      <div className="day-trades">
        {dayTrades.length === 0 ?
        <div className="day-empty mono"><div className="day-empty-h">No trades on this day</div></div> :
        dayTrades.map((t, i) => {
          const exit = new Date(t.exitTime);
          const entry = new Date(t.entryTime);
          const exitT = exit.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false });
          const entryT = entry.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false });
          return (
            <div className="day-trade" key={i}>
                  <div className="dt-h">
                    <span className={"dt-led " + (t.pnl >= 0 ? "pos" : "neg")} />
                    <span className="dt-sym mono">{t.symbol}</span>
                    <span className={"dt-side mono " + (t.side === "long" ? "pos" : "neg")}>{t.side.toUpperCase()}</span>
                    <span className="dt-size mono tnum muted">{t.size}c</span>
                    <span className={"dt-pnl mono tnum " + (t.pnl >= 0 ? "pos" : "neg")}>{window.fmt$(t.pnl, 2)}</span>
                  </div>
                  <div className="dt-meta mono">
                    <span className="tnum">{entryT}</span>
                    <span>→</span>
                    <span className="tnum">{exitT}</span>
                    <span className="dt-dot">·</span>
                    <span className="tnum">{t.entryPrice.toLocaleString("en-US", { minimumFractionDigits: 2 })}</span>
                    <span>→</span>
                    <span className="tnum">{t.exitPrice.toLocaleString("en-US", { minimumFractionDigits: 2 })}</span>
                    <span className="dt-dot">·</span>
                    <span className="tnum muted">{t.duration}</span>
                  </div>
                  <div className="dt-strat mono muted">{t.strategy || "—"} · #{t.id}</div>
                </div>);

        })}
      </div>
    </div>);

}

/* ── Strategy Manager (account detail) ───────────────────── */
function StrategyManager({ account, acctStrats }) {
  const [showAdd, setShowAdd] = usePg(false);
  const [editing, setEditing] = usePg(null); // {mode:"edit"|"add", strat?, def?}
  const [confirmDel, setConfirmDel] = usePg(null); // strat
  const [, forceUpdate] = usePg(0);
  const bump = () => forceUpdate((x) => x + 1);
  const usedDefIds = new Set(acctStrats.map((s) => s.defId));
  const available = window.STRATEGY_DEFS.filter((d) => !usedDefIds.has(d.id));
  const running = acctStrats.filter((s) => s.state !== "stopped");
  const stopped = acctStrats.filter((s) => s.state === "stopped");

  return (
    <div className="sm-wrap">
      <window.SectionHeader
        title="Strategies"
        count={acctStrats.length}
        sub={`${running.length} active · ${stopped.length} stopped`}
        right={
        <window.Btn kind={showAdd ? "ghost" : "primary"} onClick={() => setShowAdd((v) => !v)}>
            {showAdd ? "Cancel" : "+ Add strategy"}
          </window.Btn>
        } />
      

      {showAdd &&
      <div className="sm-add">
          <div className="sm-add-head">
            <span className="lbl">Available strategies · {available.length}</span>
            <span className="lbl muted" style={{ color: "var(--muted)" }}>click to configure & add to {account.label}</span>
          </div>
          {available.length === 0 ?
        <div style={{ color: "var(--muted)", fontFamily: "var(--mono)", fontSize: 12, padding: "6px 0" }}>
                All catalog strategies are already deployed on this account.
              </div> :

        <div className="sm-add-grid">
                {available.map((d) =>
          <div className="sm-cat" key={d.id}
          onClick={() => setEditing({ mode: "add", def: d })}>
                    <div>
                      <div className="ttl">{d.name}</div>
                      <div className="fam">{d.family} · {d.symbol}.{d.venue}</div>
                    </div>
                    <button className="add-btn">+ Add</button>
                    <div className="summary">{d.summary}</div>
                    <div className="stats">
                      <span>WR <b>{(d.bench.wr * 100).toFixed(1)}%</b></span>
                      <span>PF <b>{d.bench.pf.toFixed(2)}</b></span>
                      <span>E[R] <b className={d.bench.eR > 0 ? "pos" : "neg"}>{window.fmtP(d.bench.eR, 2)}</b></span>
                      <span>P(pass) <b>{(d.bench.ppass * 100).toFixed(0)}%</b></span>
                    </div>
                  </div>
          )}
              </div>
        }
        </div>
      }

      {acctStrats.length === 0 ?
      <div className="sm-empty">No strategies deployed yet. Click <b>+ Add strategy</b> to start.</div> :

      <div className="sm-list">
            {acctStrats.map((s) =>
              <window.ErrorBoundary key={s.id} label={`strategy ${s.name || s.id}`}>
                <StrategyManagerRow s={s}
                  onOpen={() => setEditing({ mode: "edit", strat: s })} />
              </window.ErrorBoundary>
            )}
          </div>
      }

      {editing && <StrategyEditor account={account} editing={editing} onClose={() => {setEditing(null);setShowAdd(false);}} onApply={bump} onDelete={(s) => {setEditing(null);setShowAdd(false);setConfirmDel(s);}} />}
      {confirmDel && <ConfirmRemove account={account} strat={confirmDel} onClose={() => setConfirmDel(null)} />}
    </div>);

}

function StrategyManagerRow({ s, onOpen }) {
  return (
    <div className="sm-row" data-state={s.state} onClick={onOpen} style={{ cursor: "pointer" }}>
      <div className="led" />
      <div className="body">
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <span className="name">{s.name}</span>
          <window.StateBadge state={s.state} />
          <span className="mono muted" style={{ fontSize: 11 }}>
            {[s.symbol && s.symbol !== "—" ? s.symbol : null,
              s.contracts ? `${s.contracts}c` : null,
              s.since && s.since !== "—" ? `since ${s.since}` : null,
            ].filter(Boolean).join(" · ") || "no metadata"}
          </span>
        </div>
        <div className="meta">
          <span>WR <b>{s.wr != null ? (s.wr * 100).toFixed(1) + "%" : "—"}</b></span>
          <span>PF <b>{(s.pf != null && Number.isFinite(s.pf)) ? s.pf.toFixed(2) : "—"}</b></span>
          <span>30d <b className={(s.pnl30 || 0) > 0 ? "pos" : (s.pnl30 || 0) < 0 ? "neg" : ""}>{window.fmt$(s.pnl30 || 0, 0)}</b></span>
          <span>risk <b>{window.fmt$(s.riskPerTrade || 0, 0)}</b>/trade</span>
          {s.lastSig && <span className="muted">last: {window.cleanSig(s.lastSig)}</span>}
        </div>
      </div>
      <div className="pnl-day">
        <div className="lbl">Today</div>
        <div className={"v " + ((s.today || 0) > 0 ? "pos" : (s.today || 0) < 0 ? "neg" : "muted")}>
          {(s.today == null || s.today === 0) ? "—" : window.fmt$(s.today, 0)}
        </div>
      </div>
    </div>);

}

/* Side-sheet editor — used for both add (mode=add) and edit (mode=edit) */
function StrategyEditor({ account, editing, onClose, onDelete, onApply }) {
  const isAdd = editing.mode === "add";
  const def = isAdd ? editing.def : window.STRATEGY_DEFS.find((d) => d.id === editing.strat.defId);
  // window.STRATEGY_DEFS is the (mock) catalog from data.jsx — it only
  // has 3 sd-* entries, none of which match the real agent slugs
  // ("orb", "mr", "pair_divergence").  When the user clicks a row
  // whose defId isn't in the catalog, `def` is undefined and the
  // editor used to crash on `def.keyParams`.  Render a friendly
  // panel with the live strategy stats instead of pretending to
  // have a template.
  if (!def) {
    // Use the design's .se-backdrop / .se-sheet so the rollout
    // animation + look match the real editor.  Renders read-only
    // live stats since we have no parameter template.
    return (
      <div className="se-backdrop" onClick={onClose}>
        <aside className="se-sheet" onClick={(e) => e.stopPropagation()}>
          <header className="ss-hero">
            <div className="ss-kicker">
              <span>Configure</span>
              <span className="ss-arrow">→</span>
              <span className="ss-acct">
                <span className={"badge " + (account?.kind || "unknown")} style={{ fontFamily: "var(--mono)", fontSize: 9.5, padding: "1px 5px", border: "1px solid currentColor", letterSpacing: "0.06em" }}>{(account?.kind || "unknown").toUpperCase()}</span>
                {account?.label}
              </span>
            </div>
            <div className="ss-title-row">
              <div>
                <h2 className="ss-title">{editing.strat?.name || editing.strat?.defId || "Strategy"}</h2>
                <div className="ss-sub">
                  <span className="mono muted">no template for {editing.strat?.defId}</span>
                </div>
              </div>
              <button className="se-close" onClick={onClose} aria-label="Close">×</button>
            </div>
          </header>
          <div className="ss-body">
            <div className="ss-card">
              <div className="ss-card-t">Live stats</div>
              <div className="ss-card-d">
                The agent hasn't shipped a <code>strategy_def_inventory</code> entry for
                this slug yet, so the parameter editor is read-only.
              </div>
              <div style={{marginTop:14, display:"grid", gridTemplateColumns:"max-content 1fr", gap:"8px 18px", fontFamily:"var(--mono)", fontSize:12}}>
                <span className="muted">state</span><span>{editing.strat?.state || "—"}</span>
                <span className="muted">contracts</span><span>{editing.strat?.contracts ?? "—"}</span>
                <span className="muted">risk / trade</span><span>{editing.strat?.riskPerTrade != null ? window.fmt$(editing.strat.riskPerTrade, 0) : "—"}</span>
                <span className="muted">trades</span><span>{editing.strat?.trades ?? 0}</span>
                <span className="muted">win rate</span><span>{editing.strat?.wr != null ? (editing.strat.wr * 100).toFixed(1) + "%" : "—"}</span>
                <span className="muted">profit factor</span><span>{(editing.strat?.pf != null && Number.isFinite(editing.strat.pf)) ? editing.strat.pf.toFixed(2) : "—"}</span>
                <span className="muted">30d P&L</span><span>{window.fmt$(editing.strat?.pnl30 || 0, 0)}</span>
              </div>
            </div>
          </div>
          <footer className="ss-foot">
            <button className="ss-btn ghost" onClick={onClose}>Close</button>
          </footer>
        </aside>
      </div>);
  }
  const baseParams = isAdd ? def.keyParams : editing.strat.params ? flattenParams(editing.strat.params) : def.keyParams;
  const baseContracts = isAdd ? 1 : editing.strat.contracts;
  const baseRisk = isAdd ? 700 : editing.strat.riskPerTrade;
  const tickValue = def.symbol === "NQ" ? 5 : def.symbol === "MGC" ? 1 : 2;

  const [tab, setTab] = usePg("sizing");
  const [params, setParams] = usePg({ ...baseParams });
  const [contracts, setContracts] = usePg(baseContracts);
  const [risk, setRisk] = usePg(baseRisk);
  const [maxDailySL, setMaxDailySL] = usePg(2);
  const [scheduleStart, setScheduleStart] = usePg("08:30");
  const [scheduleEnd, setScheduleEnd] = usePg("15:00");
  const [pauseFomc, setPauseFomc] = usePg(true);
  const [closing, setClosing] = usePg(false);

  // mount/unmount animation
  usePgE(() => {
    const onKey = (e) => {if (e.key === "Escape") handleClose();};
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const handleClose = () => {setClosing(true);setTimeout(onClose, 180);};
  const [applyBusy, setApplyBusy] = usePg(false);
  const [applyErr, setApplyErr] = usePg(null);
  const handleApply = async () => {
    setApplyErr(null);
    if (isAdd) {
      // Real agent round-trip: POST /api/commands/{instance}/strategy_add.
      // Slug must match an agent-known kind (orb, mr, pair_divergence,
      // ma_cross, orb_test).  We derive it from the def — falling back to
      // the def.id minus the "sd-" prefix used in the mock catalog.
      const slug = (def.agentSlug || def.id || "").replace(/^sd-/, "").toLowerCase();
      if (!slug) { setApplyErr("Strategy slug is empty — pick a real strategy"); return; }
      setApplyBusy(true);
      try {
        await window.strategyAdd(account.instance, account.id, slug);
      } catch (e) {
        setApplyErr(String(e.message || e));
        setApplyBusy(false);
        return;
      }
      setApplyBusy(false);
    } else {
      // Edit-only path: parameter tweaks land via set_param, not strategy_add.
      // (set_param routing is out of scope here; the editor used to mutate
      // window.STRATS in-place — we keep that for backward compat until the
      // per-param API is wired.)
      const s = window.STRATS.find((x) => x.id === editing.strat.id);
      if (s) {
        s.contracts = contracts;
        s.riskPerTrade = risk;
        s.params = { ...params };
      }
    }
    if (onApply) onApply();
    handleClose();
  };

  const setP = (k, v) => setParams((p) => ({ ...p, [k]: v }));

  // estimated capital deployed (illustrative)
  const estCapital = contracts * (def.symbol === "NQ" ? 21500 * 20 : def.symbol === "MGC" ? 4600 * 10 : 5000 * 10);
  const headroomMult = (account.equity - account.mllFloor) / risk;

  // benchSeries used to be a fake genEquity sequence; render nothing
  // (the editor still ships) until research benchmarks are real.
  const benchSeries = [];

  const liveStrat = !isAdd ? editing.strat : null;

  return (
    <div className={"se-backdrop " + (closing ? "closing" : "")} onClick={handleClose}>
      <aside className={"se-sheet " + (closing ? "closing" : "")} onClick={(e) => e.stopPropagation()}>
        {/* HERO */}
        <header className="ss-hero">
          <div className="ss-kicker">
            <span>{isAdd ? "Add" : "Configure"}</span>
            <span className="ss-arrow">→</span>
            <span className="ss-acct">
              <span className={"badge " + account.kind} style={{ fontFamily: "var(--mono)", fontSize: 9.5, padding: "1px 5px", border: "1px solid currentColor", letterSpacing: "0.06em" }}>{account.kind.toUpperCase()}</span>
              {account.label}
            </span>
          </div>
          <div className="ss-title-row">
            <div>
              <h2 className="ss-title">{def.name}</h2>
              <div className="ss-sub">
                <span>{def.family}</span>
                <span className="dotsep">·</span>
                <span className="mono">{def.symbol}.{def.venue}</span>
                <span className="dotsep">·</span>
                <span className="mono muted">validated {def.validated}</span>
              </div>
            </div>
            <button className="se-close" onClick={handleClose} aria-label="Close">×</button>
          </div>

          {/* benchmark / live mini stats */}
          <div className="ss-statgrid">
            <div className="ss-stat">
              <div className="lbl">Win rate</div>
              <div className="v tnum">{(() => {
                const wr = liveStrat ? liveStrat.wr : def.bench.wr;
                return Number.isFinite(wr) ? (wr * 100).toFixed(1) + "%" : "—";
              })()}</div>
              {liveStrat && <div className="cmp tnum muted">vs {(def.bench.wr * 100).toFixed(0)}% bench</div>}
            </div>
            <div className="ss-stat">
              <div className="lbl">Profit factor</div>
              <div className={"v tnum " + ((liveStrat && Number.isFinite(liveStrat.pf) ? liveStrat.pf : def.bench.pf) >= 1 ? "pos" : "neg")}>{(() => {
                const pf = liveStrat ? liveStrat.pf : def.bench.pf;
                if (pf === Infinity) return "∞";
                return Number.isFinite(pf) ? pf.toFixed(2) : "—";
              })()}</div>
              {liveStrat && <div className="cmp tnum muted">vs {def.bench.pf.toFixed(2)}</div>}
            </div>
            <div className="ss-stat">
              <div className="lbl">E[R]</div>
              <div className={"v tnum " + (def.bench.eR >= 0 ? "pos" : "neg")}>{window.fmtP(def.bench.eR, 2)}</div>
              <div className="cmp tnum muted">benchmark</div>
            </div>
            <div className="ss-stat">
              <div className="lbl">{liveStrat ? "30d P&L" : "P(pass)"}</div>
              <div className={"v tnum " + (liveStrat ? liveStrat.pnl30 > 0 ? "pos" : "neg" : "")}>
                {liveStrat ? window.fmt$(liveStrat.pnl30, 0) : (def.bench.ppass * 100).toFixed(0) + "%"}
              </div>
              <div className="cmp tnum muted">{liveStrat ? "this account" : "60-day combine"}</div>
            </div>
            <div className="ss-stat ss-spark">
              <div className="lbl">{liveStrat ? "30d" : "benchmark"}</div>
              <span style={{ color: (liveStrat ? liveStrat.pnl30 : 0) >= 0 ? "var(--pos)" : "var(--neg)" }}>
                <window.Sparkline data={benchSeries} width={120} height={28} fill />
              </span>
            </div>
          </div>

          {/* tabs */}
          <nav className="ss-tabs">
            {[
            ["sizing", "Sizing & risk"],
            ["params", "Parameters"],
            ["schedule", "Schedule"],
            ["about", "Strategy"]].
            map(([k, l]) =>
            <button key={k} data-active={tab === k} onClick={() => setTab(k)}>{l}</button>
            )}
          </nav>
        </header>

        {/* BODY */}
        <div className="ss-body">
          {tab === "sizing" &&
          <>
              <div className="ss-card">
                <div className="ss-card-h">
                  <div>
                    <div className="ss-card-t">Position size</div>
                    <div className="ss-card-d">How many contracts per signal.</div>
                  </div>
                  <div className="ss-bigstepper">
                    <button onClick={() => setContracts((v) => Math.max(0, v - 1))}>−</button>
                    <span className="tnum">{contracts}<small>c</small></span>
                    <button onClick={() => setContracts((v) => v + 1)}>+</button>
                  </div>
                </div>
                <div className="ss-derived">
                  <span><span className="lbl">notional</span> <span className="tnum">{window.fmt$(estCapital, 0)}</span></span>
                  <span><span className="lbl">tick value</span> <span className="tnum">${tickValue}/c</span></span>
                </div>
              </div>

              <div className="ss-card">
                <div className="ss-card-h">
                  <div>
                    <div className="ss-card-t">Risk per trade</div>
                    <div className="ss-card-d">Max $ loss per signal — used for ATR sizing.</div>
                  </div>
                  <div className="ss-input-wrap">
                    <span className="ss-input-prefix">$</span>
                    <input className="ss-input tnum" type="number" value={risk}
                  onChange={(e) => setRisk(+e.target.value)} />
                  </div>
                </div>
                <div className="ss-slider-row">
                  <input type="range" min={100} max={3000} step={50} value={risk}
                onChange={(e) => setRisk(+e.target.value)} className="ss-range" />
                  <div className="ss-ticks">
                    <span>$100</span><span>$1.5k</span><span>$3k</span>
                  </div>
                </div>
                <div className="ss-derived">
                  <span><span className="lbl">headroom</span> <span className="tnum">{window.fmt$(account.equity - account.mllFloor, 0)}</span></span>
                  <span><span className="lbl">capacity</span> <span className="tnum">{Math.floor(headroomMult)} losing trades</span></span>
                </div>
                {risk > (account.equity - account.mllFloor) * 0.05 &&
              <div className="ss-warn">
                    Risk per trade exceeds 5% of MLL headroom. Consider reducing.
                  </div>
              }
              </div>

              <div className="ss-card">
                <div className="ss-card-h">
                  <div>
                    <div className="ss-card-t">Daily stop-loss cap</div>
                    <div className="ss-card-d">Pause strategy after N consecutive SLs in one session.</div>
                  </div>
                  <div className="ss-bigstepper">
                    <button onClick={() => setMaxDailySL((v) => Math.max(1, v - 1))}>−</button>
                    <span className="tnum">{maxDailySL}</span>
                    <button onClick={() => setMaxDailySL((v) => v + 1)}>+</button>
                  </div>
                </div>
              </div>
            </>
          }

          {tab === "params" &&
          <div className="ss-card">
              <div className="ss-card-h">
                <div>
                  <div className="ss-card-t">Strategy parameters</div>
                  <div className="ss-card-d">Recipe defaults from <span className="mono">{def.id}</span>. Edits scope to this deployment only.</div>
                </div>
                <button className="ss-link" onClick={() => setParams({ ...def.keyParams })}>↻ Reset to default</button>
              </div>
              <div className="ss-params">
                {Object.entries(params).map(([k, v]) => {
                const isDefault = String(v) === String(def.keyParams[k]);
                return (
                  <div className={"ss-param " + (isDefault ? "" : "changed")} key={k}>
                      <label className="mono">{k}</label>
                      <input className="ss-input tnum" defaultValue={String(v)}
                    onChange={(e) => setP(k, isNaN(+e.target.value) ? e.target.value : +e.target.value)} />
                      {!isDefault && <span className="ss-changed-tag">edited · default {String(def.keyParams[k])}</span>}
                    </div>);

              })}
              </div>
            </div>
          }

          {tab === "schedule" &&
          <>
              <div className="ss-card">
                <div className="ss-card-h">
                  <div>
                    <div className="ss-card-t">Trading window</div>
                    <div className="ss-card-d">CT · only generate signals between these times.</div>
                  </div>
                </div>
                <div className="ss-time-row">
                  <div className="ss-time">
                    <label>Start</label>
                    <input type="time" value={scheduleStart}
                  onChange={(e) => setScheduleStart(e.target.value)} className="ss-input tnum" />
                  </div>
                  <div className="ss-arrow-mid">→</div>
                  <div className="ss-time">
                    <label>End</label>
                    <input type="time" value={scheduleEnd}
                  onChange={(e) => setScheduleEnd(e.target.value)} className="ss-input tnum" />
                  </div>
                </div>
              </div>
              <div className="ss-card">
                <div className="ss-card-h">
                  <div>
                    <div className="ss-card-t">Macro pauses</div>
                    <div className="ss-card-d">Auto-pause around scheduled events.</div>
                  </div>
                  <label className="ss-toggle">
                    <input type="checkbox" checked={pauseFomc} onChange={(e) => setPauseFomc(e.target.checked)} />
                    <span>FOMC, CPI, NFP</span>
                  </label>
                </div>
              </div>
            </>
          }

          {tab === "about" &&
          <>
              <div className="ss-card">
                <div className="ss-card-t">Summary</div>
                <p className="ss-prose">{def.summary}</p>
              </div>
              <div className="ss-card">
                <div className="ss-card-t">Provenance</div>
                <div className="se-kv"><span>Author</span><span className="mono">{def.author}</span></div>
                <div className="se-kv"><span>Validated</span><span className="mono tnum">{def.validated}</span></div>
                <div className="se-kv"><span>Basis</span><span className="mono" style={{ fontSize: 11 }}>{def.basis}</span></div>
                <div className="se-kv"><span>Family</span><span className="mono">{def.family}</span></div>
              </div>
            </>
          }
        </div>

        {/* FOOTER */}
        <footer className="ss-foot">
          {!isAdd &&
          <button className="ss-btn danger" onClick={() => onDelete(editing.strat)}>Delete</button>
          }
          <button className="ss-btn ghost" onClick={handleClose}>Cancel</button>
          <span style={{ flex: 1 }} />
          {applyErr && <span className="mono" style={{color:"var(--neg)", fontSize:11}}>Error: {applyErr}</span>}
          <span className="ss-foot-hint mono">⌘S to apply</span>
          <button className="ss-btn primary" onClick={handleApply} disabled={applyBusy}>
            {applyBusy
              ? "Sending…"
              : (isAdd ? `Apply on ${account.label}` : "Apply changes")}
          </button>
        </footer>
      </aside>
    </div>);

}

function flattenParams(p) {
  const out = {};
  Object.values(p).forEach((group) => {
    if (group && typeof group === "object") Object.assign(out, group);
  });
  return out;
}

function ConfirmRemove({ account, strat, onClose }) {
  // open positions on this account that belong to THIS strategy.
  // Agent tags each position with strategy_id (lifecycle.py, 2026-05-20);
  // p.strat is the pre-rollout key, kept as a transition fallback.
  const positions = window.positionsForAccount(account.id).filter(
    (p) => (p.strategy_id || p.strat) === strat.id
  );
  const hasPos = positions.length > 0;
  const isLive = strat.state !== "stopped";

  // auto-flatten checked by default — per requirement, open positions should be flattened automatically
  const [flatten, setFlatten] = usePg(true);
  const [closing, setClosing] = usePg(false);
  const [busy, setBusy] = usePg(false);
  const [err, setErr] = usePg(null);
  const handleClose = () => {setClosing(true);setTimeout(onClose, 160);};

  async function confirmRemove() {
    setErr(null);
    setBusy(true);
    try {
      // Resolve the agent-facing slug. The mock catalog uses sd-* ids,
      // but the agent expects "orb"/"mr"/"pair_divergence"/"ma_cross".
      // Try strat.defId stripped of the prefix; fall back to strat.name.
      const slug = (strat.defId || strat.id || "").replace(/^sd-/, "").toLowerCase()
                || (strat.name || "").toLowerCase();
      if (hasPos && flatten) {
        // Best-effort flatten before strategy_remove. The agent's
        // FLATTEN command closes positions on the account; strategy
        // unwiring follows. If flatten fails we surface and bail —
        // don't detach a strategy that still has open exposure.
        try {
          await window.flattenAccount(account.instance, account.id);
        } catch (e) {
          throw new Error(`flatten failed: ${e.message || e}`);
        }
      }
      await window.strategyRemove(account.instance, account.id, slug);
      handleClose();
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setBusy(false);
    }
  }

  const exposure = positions.reduce((s, p) => s + Math.abs(p.unreal), 0);
  const unrealTotal = positions.reduce((s, p) => s + p.unreal, 0);

  return (
    <div className={"se-backdrop centered " + (closing ? "closing" : "")} onClick={handleClose}>
      <div className={"se-confirm-card " + (closing ? "closing" : "")} onClick={(e) => e.stopPropagation()}>
        <div className="cf-head">
          <div className="cf-icon">⚠</div>
          <div>
            <div className="cf-title">Remove {strat.name}?</div>
            <div className="cf-sub mono">from {account.label} · {account.code}</div>
          </div>
          <button className="se-close" onClick={handleClose}>×</button>
        </div>

        <div className="cf-body">
          <ol className="cf-steps">
            {isLive &&
            <li>
                <span className="cf-num">1</span>
                <div>
                  <div className="cf-step-t">Stop <b>{strat.name}</b></div>
                  <div className="cf-step-d">Cancels pending orders, halts new signals.</div>
                </div>
              </li>
            }
            {hasPos &&
            <li>
                <span className="cf-num">{isLive ? 2 : 1}</span>
                <div>
                  <div className="cf-step-t">
                    {flatten ? "Flatten" : "Keep"} {positions.length} open position{positions.length > 1 ? "s" : ""}
                    <label className="cf-flatten">
                      <input type="checkbox" checked={flatten} onChange={(e) => setFlatten(e.target.checked)} />
                      <span>auto-flatten</span>
                    </label>
                  </div>
                  <div className="cf-step-d">
                    {flatten ?
                  "Market-close all positions opened by this strategy." :
                  "Positions will remain open and unmanaged. You'll need to close them manually."}
                  </div>
                  <div className="cf-positions">
                    {positions.map((p, i) =>
                  <div className="cf-pos" key={i}>
                        <span><b>{p.sym}</b> <span className={p.side === "long" ? "pos" : "neg"} style={{ fontSize: 10, letterSpacing: "0.06em" }}>{p.side.toUpperCase()}</span></span>
                        <span className="mono tnum">{p.qty}c @ {p.entry.toFixed(2)}</span>
                        <span className={"mono tnum " + (p.unreal >= 0 ? "pos" : "neg")}>{window.fmt$(p.unreal, 2)}</span>
                      </div>
                  )}
                    <div className="cf-pos cf-total">
                      <span className="muted mono" style={{ fontSize: 10.5, letterSpacing: "0.06em", textTransform: "uppercase" }}>Net unrealized</span>
                      <span></span>
                      <span className={"mono tnum " + (unrealTotal >= 0 ? "pos" : "neg")}>{window.fmt$(unrealTotal, 2)}</span>
                    </div>
                  </div>
                </div>
              </li>
            }
            <li>
              <span className="cf-num">{(isLive ? 1 : 0) + (hasPos ? 1 : 0) + 1}</span>
              <div>
                <div className="cf-step-t">Detach from account</div>
                <div className="cf-step-d">30-day perf history is preserved on the strategy detail page.</div>
              </div>
            </li>
          </ol>
        </div>

        <div className="cf-foot">
          {err && <span className="mono" style={{color:"var(--neg)", fontSize:11, marginRight:8}}>Error: {err}</span>}
          <button className="ss-btn ghost" onClick={handleClose} disabled={busy}>Cancel</button>
          <span style={{ flex: 1 }} />
          <button className="ss-btn danger" onClick={confirmRemove} disabled={busy}>
            {busy ? "Working…" : (hasPos && flatten ? "Flatten & remove" : "Remove")}
          </button>
        </div>
      </div>
    </div>);

}

function TradesTable({ trades }) {
  const [sortBy, setSortBy] = usePg("exitTime");
  const [dir, setDir] = usePg(-1);
  const [showAll, setShowAll] = usePg(false);
  const click = (k) => {if (sortBy === k) setDir((d) => -d);else {setSortBy(k);setDir(-1);}};
  const rows = usePgM(() => [...trades].sort((a, b) => (a[sortBy] > b[sortBy] ? 1 : -1) * dir), [trades, sortBy, dir]);
  const arrow = (k) => sortBy === k ? dir < 0 ? " ↓" : " ↑" : "";
  const fmtTime = (s) => new Date(s).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", hour12: false });
  // Render up to 200 by default — was 30, which truncated busy days
  // silently.  "Show all" expands to the full set (5000 cap upstream).
  const visibleLimit = showAll ? rows.length : 200;
  const visible = rows.slice(0, visibleLimit);
  // Per-strategy attribution lets the operator spot fills that the
  // agent didn't tag (manual broker activity, agent restart fills,
  // reconciler artifacts).  "untracked" → no strategy on the fill.
  const stratLabel = (t) => {
    const s = (t.strategy || "").trim();
    if (!s) return null;
    if (s === "reconciler") return "reconciler";
    // Resolve via display label (mr → mr_v3 etc) when available so the
    // column reads consistently with the strategy panel.
    if (window.strategyLabelFor) return window.strategyLabelFor(t.account, s);
    return s;
  };
  return (
    <div className="table-wrap">
      <table className="strat">
        <thead><tr>
          <th>ID</th><th>Symbol</th>
          <th>Strategy</th>
          <th className="num">Size</th>
          <th onClick={() => click("entryTime")} style={{ cursor: "pointer" }}>Entry{arrow("entryTime")}</th>
          <th onClick={() => click("exitTime")} style={{ cursor: "pointer" }}>Exit{arrow("exitTime")}</th>
          <th>Duration</th>
          <th className="num">Entry Price</th><th className="num">Exit Price</th>
          <th className="num" onClick={() => click("pnl")} style={{ cursor: "pointer" }}>P&L{arrow("pnl")}</th>
          <th className="num">Comm.</th><th className="num">Fees</th><th>Direction</th>
        </tr></thead>
        <tbody>
          {visible.map((t) => {
            const lbl = stratLabel(t);
            return (
            <tr key={t.id}>
              <td className="muted">{t.id}</td>
              <td>{t.symbol}</td>
              <td>
                {lbl ? (
                  <span className="mono" style={{fontSize: 11, color: t.strategy === "reconciler" ? "var(--muted)" : "var(--ink-2)"}}>
                    {lbl}
                  </span>
                ) : (
                  <span title="No strategy on this fill — manual broker activity, agent restart, or pre-attribution build"
                        style={{
                          fontSize: 10,
                          padding: "1px 6px",
                          border: "1px solid var(--warn)",
                          color: "var(--warn)",
                          letterSpacing: "0.04em",
                          fontFamily: "var(--mono)",
                        }}>
                    UNTRACKED
                  </span>
                )}
              </td>
              <td className="num tnum">{t.size}</td>
              <td className="muted tnum" style={{ fontSize: 11 }}>{fmtTime(t.entryTime)}</td>
              <td className="muted tnum" style={{ fontSize: 11 }}>{fmtTime(t.exitTime)}</td>
              <td className="muted tnum">{t.duration}</td>
              <td className="num tnum">{t.entryPrice != null ? Number(t.entryPrice).toLocaleString("en-US", { minimumFractionDigits: 2 }) : "—"}</td>
              <td className="num tnum">{t.exitPrice != null ? Number(t.exitPrice).toLocaleString("en-US", { minimumFractionDigits: 2 }) : "—"}</td>
              <td className={"num tnum " + ((t.pnl || 0) >= 0 ? "pos" : "neg")}>{t.pnl != null ? window.fmt$(t.pnl, 2) : "—"}</td>
              <td className="num tnum neg">{window.fmt$(t.commissions || 0, 2)}</td>
              <td className="num tnum neg">{window.fmt$(t.fees || 0, 2)}</td>
              <td style={{ textTransform: "capitalize" }}>{t.side}</td>
            </tr>);
          })}
        </tbody>
      </table>
      {rows.length > visibleLimit && (
        <div style={{
          padding: "8px var(--pad)",
          fontFamily: "var(--mono)",
          fontSize: 11,
          color: "var(--muted)",
          borderTop: "1px dashed var(--rule)",
        }}>
          showing {visibleLimit} of {rows.length} fills ·
          <button
            onClick={() => setShowAll(true)}
            style={{
              background: "transparent",
              border: "1px solid var(--rule-2)",
              color: "var(--ink-2)",
              padding: "2px 8px",
              marginLeft: 8,
              fontFamily: "var(--mono)",
              fontSize: 11,
              cursor: "pointer",
            }}>show all</button>
        </div>
      )}
    </div>);

}

/* ── Research · notebook + LLM proposals + regime ─────────── */
/* Continuous-research snapshot — latest research_state event (agent emits
 * 6-hourly from research/knowledge_bank: open-question backlog, sample
 * questions, audit-log verdict tail). Operator ask 2026-07-16. */
function _verdictTag(title) {
  const t = (title || "").toUpperCase();
  if (/(RESOLVED|PASS|SHIPPED|FIXED|DEPLOY|CONFIRMED)/.test(t)) return ["ok", "RESOLVED"];
  if (/(KILL|DEAD|REFUTED|REJECT|NEGATIVE|CLOSED)/.test(t)) return ["risk", "KILL"];
  return ["warn", "FINDING"];
}
function ContinuousResearchPanel() {
  const [st, setSt] = usePg(null);
  usePgE(() => {
    let dead = false;
    (async () => {
      try {
        const evs = await window.getJson("/api/events?kind=research_state&limit=1");
        if (!dead && evs && evs.length) setSt(evs[0].payload || null);
      } catch (_) {}
    })();
    return () => { dead = true; };
  }, []);
  if (!st) return (
    <div className="prop-card" style={{ opacity: 0.6 }}>
      <div className="body">continuous research — no snapshot yet (agent emits 6-hourly)</div>
    </div>
  );
  const ageDays = st.audit_last_ts ? Math.floor((Date.now() / 1000 - st.audit_last_ts) / 86400) : null;
  return (
    <div>
      <div className="prop-card">
        <div className="ph">
          <span className="kind-tag">KB LOOP</span>
          <span className="ptitle">
            backlog <b>{st.n_open_questions}</b> open questions · {st.n_cards} cards
          </span>
          <span className="strength">
            last activity {ageDays == null ? "—" : ageDays === 0 ? "today" : ageDays + "d ago"}
          </span>
        </div>
        <div className="body" style={{ color: "var(--muted)" }}>
          sample (unranked): {(st.sample_questions || []).slice(0, 3).map((q, i) =>
            <div key={i} style={{ marginTop: 3 }}>• {q}</div>)}
        </div>
      </div>
      {(st.audit_tail || []).slice().reverse().map((e, i) => {
        const [cls, tag] = _verdictTag(e.title);
        return (
          <div className="feed-row" key={i} style={{ alignItems: "baseline" }}>
            <span className="t" style={{ minWidth: 84 }}>{e.date}</span>
            <span className="ev">
              <span className={"tag " + cls}>{tag}</span>{" "}
              {e.title}
            </span>
          </div>
        );
      })}
    </div>
  );
}

window.PageResearch = function PageResearch() {
  return (
    <div>
      <window.SectionHeader title="Continuous research · knowledge-bank loop"
        sub="audit-log verdicts + open-question backlog (agent research_state)" />
      <NodeCrStrip />
      <ContinuousResearchPanel />

      <window.SectionHeader title="Market regime · live" sub={window.REGIME.asOf} />
      <div className="regime-grid">
        {window.REGIME.symbols.map((s) =>
        <div className="regime-cell" key={s.sym}>
            <div className="sym">{s.sym}<span style={{ color: "var(--muted)", fontSize: 11, marginLeft: 6 }}>· {s.venue}</span></div>
            <div className="px tnum">{s.price.toLocaleString("en-US", { minimumFractionDigits: 2 })} <span style={{ color: s.trend === "up" ? "var(--pos)" : s.trend === "down" ? "var(--neg)" : "var(--muted)" }}>{s.trend === "up" ? "↑" : s.trend === "down" ? "↓" : "→"}</span></div>
            <div className="reg">regime <b>{s.regime}</b></div>
            <div style={{ display: "flex", gap: 14, fontFamily: "var(--mono)", fontSize: 11, color: "var(--muted)", marginTop: 6 }}>
              <span>ATR% <span className="tnum">{(s.atrPct * 100).toFixed(2)}</span></span>
              <span>z <span className={"tnum " + (s.atrZ > 1 ? "warn" : s.atrZ < -1 ? "neg" : "")}>{s.atrZ.toFixed(1)}</span></span>
              <span>ADX <span className="tnum">{s.adx}</span></span>
            </div>
            <div className="note">{s.note}</div>
          </div>
        )}
      </div>

      <window.SectionHeader title="Proposals · LLM analysis" count={window.PROPOSALS.length} sub="new approaches & tuning suggestions based on regime" right={
      <><button data-active="true">All</button><button>New</button><button>Tune</button><button>Warn</button></>
      } />
      <div>
        {window.PROPOSALS.map((p) =>
        <div className="prop-card" data-kind={p.kind} key={p.id}>
            <div className="ph">
              <span className="kind-tag">{p.kind.toUpperCase()}</span>
              <span className="ptitle">{p.title}</span>
              <span className="strength">strength {p.strength.toFixed(2)} · {p.date}</span>
            </div>
            <div className="body"><b>Why:</b> {p.rationale}</div>
            <div className="body"><b>Backtest:</b> {p.backtest}</div>
            <div className="row2">
              {p.target && <span className="muted">target → <a href={`#/strategies/${p.target}`} style={{ color: "var(--ink)", textDecoration: "none" }}>{p.target}</a></span>}
              <span style={{ flex: 1 }} />
              {p.actions.map((a, i) => <window.Btn key={i} kind={i === 0 ? "primary" : "ghost"}>{a}</window.Btn>)}
              <window.Btn>Dismiss</window.Btn>
            </div>
          </div>
        )}
      </div>

      <window.SectionHeader title="Experiments" count={window.RESEARCH.length} sub="ablations · monte carlo · parameter sweeps" right={
      <><button data-active="true">All</button><button>Shipped</button><button>Drafts</button><window.Btn kind="primary">+ New experiment</window.Btn></>
      } />
      <div className="res-grid">
        {window.RESEARCH.map((r) =>
        <div className="res-card" key={r.id}>
            <div className="h">
              <div className="title">{r.title}</div>
              <div className="meta">{r.date}</div>
            </div>
            <div className="body">{r.summary}</div>
            <div className="foot">
              <span style={{ padding: "1px 6px", border: "1px solid currentColor", letterSpacing: "0.06em", color: r.status === "shipped" ? "var(--pos)" : "var(--warn)" }}>{r.status.toUpperCase()}</span>
              <span>{r.tag}</span>
              <span>· {r.author}</span>
              <span style={{ flex: 1 }} />
              {r.produces.length > 0 &&
            <span>produces: {r.produces.map((id, i) => <a key={i} href={`#/strategies/${id}`} style={{ color: "var(--ink)", textDecoration: "none", marginLeft: i ? 6 : 0 }}>{id}</a>)}</span>
            }
            </div>
          </div>
        )}
      </div>
    </div>);

};

/* ── Configs · snapshots ──────────────────────────────────── */
window.PageConfigs = function PageConfigs() {
  return (
    <div>
      <window.SectionHeader title="Configs" count={window.CONFIGS.length} sub="snapshots binding strategies + risk + account" right={
      <><button data-active="true">All</button><button>In use</button><button>Archived</button><window.Btn kind="primary">+ New config</window.Btn></>
      } />
      <div className="strat-cat">
        {window.CONFIGS.map((c) => {
          const usedByInsts = window.INSTANCES.filter((i) => i.config === c.id);
          return (
            <div className="strat-card" key={c.id} onClick={() => {location.hash = `#/configs/${c.id}`;}}>
              <div className="top">
                <div>
                  <h3>{c.label}</h3>
                  <div className="fam">{c.file} · edited {c.lastEdit}</div>
                </div>
                <span style={{ fontFamily: "var(--mono)", fontSize: 11, color: "var(--muted)" }}>{c.strategyDefs.length} strat</span>
              </div>
              <div className="summary">{c.note}</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", borderTop: "1px solid var(--rule)", paddingTop: 10, fontFamily: "var(--mono)" }}>
                <div><div style={{ fontSize: 9.5, color: "var(--muted)", letterSpacing: "0.08em", textTransform: "uppercase" }}>Daily loss cap</div><div className="tnum" style={{ fontSize: 13 }}>{window.fmt$(c.dailyLossCap, 0)}</div></div>
                <div><div style={{ fontSize: 9.5, color: "var(--muted)", letterSpacing: "0.08em", textTransform: "uppercase" }}>Max concurrent</div><div className="tnum" style={{ fontSize: 13 }}>{c.maxConcurrent}</div></div>
                <div><div style={{ fontSize: 9.5, color: "var(--muted)", letterSpacing: "0.08em", textTransform: "uppercase" }}>Used by</div><div style={{ fontSize: 13 }}>{usedByInsts.length} instance{usedByInsts.length === 1 ? "" : "s"}</div></div>
              </div>
              <div className="deploys">
                {c.strategyDefs.map((sd) => {
                  const def = window.STRATEGY_DEFS.find((x) => x.id === sd);
                  return <span key={sd} className="pill" style={{ color: "var(--ink)", borderColor: "var(--ink)" }}>{def ? def.name : sd}</span>;
                })}
              </div>
            </div>);

        })}
      </div>
    </div>);

};

/* ── Collectors · tick_recorder side ──────────────────────── */
window.PageCollectors = function PageCollectors() {
  const total = window.STORAGE_ROLLUP;
  const grouped = {};
  window.COLLECTORS.forEach((c) => {(grouped[c.host] = grouped[c.host] || []).push(c);});
  return (
    <div>
      <div className="kpis">
        <div className="kpi"><div className="lbl">Collectors</div><div className="val mono tnum">{window.COLLECTORS.length}</div><div className="delta mono">{window.COLLECTORS.filter((c) => c.state === "recording").length} recording · {window.COLLECTORS.filter((c) => c.state === "degraded").length} degraded · {window.COLLECTORS.filter((c) => c.state === "backfilling").length} backfill</div></div>
        <div className="kpi"><div className="lbl">Aggregate ticks/s</div><div className="val mono tnum pos">{total.tps.toLocaleString()}</div><div className="delta mono">across {window.COLLECTORS.reduce((s, c) => s + c.symbols.length, 0)} symbols</div></div>
        <div className="kpi"><div className="lbl">Ticks today</div><div className="val mono tnum">{(total.ticksToday / 1e6).toFixed(1)}M</div><div className="delta mono">session start 09:30 CT</div></div>
        <div className="kpi"><div className="lbl">Storage</div><div className="val mono tnum">{total.used >= 1024 ? `${(total.used / 1024).toFixed(2)} TB` : `${total.used.toFixed(1)} GB`}</div><div className="delta mono">{total.cap > 0 ? `of ${total.cap >= 1024 ? `${(total.cap / 1024).toFixed(1)} TB` : `${total.cap.toFixed(0)} GB`} · ${(total.used / total.cap * 100).toFixed(1)}%` : "capacity unknown"}</div></div>
        <div className="kpi"><div className="lbl">Coverage · 24h</div><div className="val mono tnum">{(window.COLLECTORS.reduce((s, c) => s + c.quality.coverage, 0) / window.COLLECTORS.length * 100).toFixed(2)}%</div><div className="delta mono">{window.COLLECTORS.reduce((s, c) => s + c.gapsToday, 0)} gaps today</div></div>
        <div className="kpi"><div className="lbl">Median feed lag</div><div className="val mono tnum">{(() => {const l = window.COLLECTORS.map((c) => c.lagMs).filter((x) => x > 0).sort((a, b) => a - b);return l[Math.floor(l.length / 2)];})()}ms</div><div className="delta mono">p99 {Math.max(...window.COLLECTORS.map((c) => c.lagMs))}ms</div></div>
      </div>

      <div className="main">
        <div className="col-left">
          {Object.keys(grouped).map((host) =>
          <div key={host}>
              <window.SectionHeader title={host} count={grouped[host].length} sub="tick_recorder processes" />
              {grouped[host].map((c) => {
              const usedPct = c.storage.used / c.storage.capacity;
              const fillCls = usedPct > 0.9 ? "neg" : usedPct > 0.7 ? "warn" : "";
              const max = Math.max(...c.rate, 0.01);
              return (
                <div key={c.id} className="col-card" data-state={c.state} onClick={() => {location.hash = `#/collectors/${c.id}`;}}>
                    <div className="ident">
                      <div className="venue">{c.venue} · {c.feed}</div>
                      <h3>{c.id}</h3>
                      <div className="feed">pid {c.pid} · {c.primarySymbol}</div>
                      <div style={{ marginTop: 6 }}><window.StateBadge state={c.state === "recording" ? "running" : c.state === "degraded" ? "degraded" : c.state === "backfilling" ? "armed" : "stopped"} size="md" /></div>
                      <div className="syms">
                        {c.symbols.slice(0, 8).map((s, i) => <span key={i} className="sym-pill">{s}</span>)}
                        {c.symbols.length > 8 && <span className="sym-pill">+{c.symbols.length - 8}</span>}
                      </div>
                    </div>
                    <div>
                      <div className="stats">
                        <div><div className="lbl">Ticks/s</div><div className="v tnum">{c.ticksPerSec.toLocaleString()}</div><div className="sub">peak {c.peakTps.toLocaleString()}</div></div>
                        <div><div className="lbl">Ticks today</div><div className="v tnum">{(c.ticksToday / 1e6).toFixed(2)}M</div><div className="sub">{c.quality.lastBar}</div></div>
                        <div><div className="lbl">Feed lag</div><div className={"v tnum " + (c.lagMs > 80 ? "warn" : "")}>{c.lagMs}ms</div><div className="sub">CPU {c.cpu.toFixed(1)}% · {c.mem}M</div></div>
                        <div><div className="lbl">Coverage 24h</div><div className={"v tnum " + (c.quality.coverage < 0.999 ? "warn" : "pos")}>{(c.quality.coverage * 100).toFixed(2)}%</div><div className="sub">{c.gapsToday} gap{c.gapsToday === 1 ? "" : "s"} today</div></div>
                      </div>
                      <div className="tps-strip">
                        {c.rate.map((v, i) => <div key={i} className="b" style={{ height: Math.max(2, v / max * 36) + "px" }} />)}
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", fontFamily: "var(--mono)", fontSize: 10.5, color: "var(--muted)", marginTop: 4 }}>
                        <span>last 60s tick rate</span>
                        <span className="tnum">μ {Math.round(c.rate.reduce((s, v) => s + v, 0) / c.rate.length * c.peakTps).toLocaleString()}</span>
                      </div>
                    </div>
                    <div className="right-col">
                      <div className="big tnum">{c.storage.used.toFixed(1)}<span style={{ fontSize: 11, color: "var(--muted)" }}> GB</span></div>
                      <div>of {c.storage.capacity.toLocaleString()} GB · {(usedPct * 100).toFixed(1)}%</div>
                      <div className="storage-bar"><div className={"fill " + fillCls} style={{ width: (usedPct * 100).toFixed(2) + "%" }} /></div>
                      <div style={{ marginTop: 8 }}>{c.storage.format}</div>
                      <div>{c.storage.shards.toLocaleString()} shards · ret {c.storage.retention}</div>
                      <div style={{ marginTop: 8, fontSize: 10.5 }}>up <span className="tnum" style={{ color: "var(--ink)" }}>{c.uptime}</span></div>
                      {c.backfill &&
                    <div style={{ marginTop: 10, padding: "6px 8px", background: "var(--paper-2)", border: "1px solid var(--rule-2)" }}>
                          <div style={{ fontSize: 10, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--accent)" }}>backfill</div>
                          <div className="tnum" style={{ color: "var(--ink)" }}>{(c.backfill.progress * 100).toFixed(0)}% · ETA {c.backfill.etaMin}m</div>
                          <div className="storage-bar"><div className="fill" style={{ width: (c.backfill.progress * 100).toFixed(0) + "%", background: "var(--accent)" }} /></div>
                        </div>
                    }
                    </div>
                  </div>);

            })}
            </div>
          )}
        </div>

        <div className="col-right">
          <div className="right-block">
            <h3>Storage utilization</h3>
            {window.COLLECTORS.map((c) => {
              const u = c.storage.used / c.storage.capacity;
              return (
                <div key={c.id} style={{ padding: "6px 0", borderBottom: "1px dashed var(--rule)" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontFamily: "var(--mono)", fontSize: 11, marginBottom: 3 }}>
                    <span>{c.id}</span>
                    <span className="tnum muted">{c.storage.used.toFixed(1)} / {c.storage.capacity} GB</span>
                  </div>
                  <div className="storage-bar"><div className={"fill " + (u > 0.9 ? "neg" : u > 0.7 ? "warn" : "")} style={{ width: u * 100 + "%" }} /></div>
                </div>);

            })}
          </div>

          <div className="right-block">
            <h3>Data quality · 24h</h3>
            {window.COLLECTORS.map((c) =>
            <div className="kv" key={c.id}>
                <div className="k">{c.id}</div>
                <div className="v tnum">
                  <span className={c.quality.coverage < 0.999 ? "warn" : "pos"}>{(c.quality.coverage * 100).toFixed(2)}%</span>
                  <span className="muted" style={{ marginLeft: 8 }}>dup {(c.quality.dupRate * 1e6).toFixed(0)}ppm</span>
                </div>
              </div>
            )}
          </div>

          <div className="right-block" style={{ borderBottom: 0 }}>
            <h3>Collector events</h3>
            <div className="feed">
              {window.COL_EVENTS.map((e, i) =>
              <div className="feed-row" key={i}>
                  <span className="t">{e.t}</span>
                  <span className="ev"><span className={"tag " + e.tag}>{e.tag.toUpperCase()}</span><span style={{ color: "var(--muted)", marginRight: 6 }}>{e.col}</span>{e.txt}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>);

};

window.PageCollectorDetail = function PageCollectorDetail({ id }) {
  const c = window.COLLECTORS.find((x) => x.id === id);
  if (!c) return <div style={{ padding: 32 }}>Collector not found.</div>;
  const max = Math.max(...c.rate, 0.01);
  const usedPct = c.storage.used / c.storage.capacity;
  return (
    <>
      <window.PageHeader crumbs={[{ label: "Collectors", href: "#/collectors" }, { label: c.id }]} actions={
      <><window.Btn>Tail logs</window.Btn><window.Btn>Reload symbols</window.Btn>{c.state === "recording" ? <window.Btn kind="danger">Stop recording</window.Btn> : <window.Btn kind="primary">Start</window.Btn>}</>
      } />
      <div style={{ padding: "16px var(--pad) 0", display: "grid", gridTemplateColumns: "repeat(4, 1fr)", borderBottom: "1px solid var(--rule)" }}>
        {[
        { l: "State", v: <window.StateBadge state={c.state === "recording" ? "running" : c.state === "degraded" ? "degraded" : c.state === "backfilling" ? "armed" : "stopped"} size="md" /> },
        { l: "Venue · Feed", v: <span className="mono">{c.venue} · <span className="muted">{c.feed}</span></span> },
        { l: "Host · PID", v: <span className="mono tnum">{c.host}<span className="muted"> · {c.pid}</span></span> },
        { l: "Uptime", v: <span className="mono tnum">{c.uptime}</span> },
        { l: "Started", v: <span className="mono tnum">{c.started}</span> },
        { l: "Branch", v: <span className="mono" style={{ fontSize: 11 }}>{c.branch}</span> },
        { l: "CPU · Mem", v: <span className="mono tnum">{c.cpu.toFixed(1)}% · {c.mem}M</span> },
        { l: "Feed lag", v: <span className={"mono tnum " + (c.lagMs > 80 ? "warn" : "")}>{c.lagMs}ms</span> }].
        map((x, i) =>
        <div key={i} style={{ padding: "6px 20px 18px", borderRight: i % 4 !== 3 ? "1px solid var(--rule)" : "none", minWidth: 0 }}>
            <div style={{ fontFamily: "var(--mono)", fontSize: 10.5, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--muted)", marginBottom: 6 }}>{x.l}</div>
            <div style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>{x.v}</div>
          </div>
        )}
      </div>

      <div className="kpis">
        <div className="kpi"><div className="lbl">Ticks/s</div><div className="val mono tnum pos">{c.ticksPerSec.toLocaleString()}</div><div className="delta mono">peak {c.peakTps.toLocaleString()}</div></div>
        <div className="kpi"><div className="lbl">Ticks today</div><div className="val mono tnum">{(c.ticksToday / 1e6).toFixed(2)}M</div><div className="delta mono">last bar {c.quality.lastBar}</div></div>
        <div className="kpi"><div className="lbl">Coverage 24h</div><div className={"val mono tnum " + (c.quality.coverage < 0.999 ? "warn" : "pos")}>{(c.quality.coverage * 100).toFixed(2)}%</div><div className="delta mono">{c.gapsToday} gaps today · {c.gapsTotal} total</div></div>
        <div className="kpi"><div className="lbl">Dup rate</div><div className="val mono tnum">{(c.quality.dupRate * 1e6).toFixed(0)} ppm</div><div className="delta mono">OOS bars {c.quality.oosBars}</div></div>
        <div className="kpi"><div className="lbl">Storage used</div><div className="val mono tnum">{c.storage.used.toFixed(1)} GB</div><div className="delta mono">of {c.storage.capacity} GB · {(usedPct * 100).toFixed(1)}%</div></div>
        <div className="kpi"><div className="lbl">Shards</div><div className="val mono tnum">{c.storage.shards.toLocaleString()}</div><div className="delta mono">ret {c.storage.retention}</div></div>
      </div>

      <window.SectionHeader title="Tick rate · last 60s" sub={c.primarySymbol} />
      <div style={{ padding: "16px var(--pad) 24px" }}>
        <div className="tps-strip" style={{ height: 80 }}>
          {c.rate.map((v, i) => <div key={i} className="b" style={{ height: Math.max(2, v / max * 80) + "px", width: "5px" }} />)}
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", fontFamily: "var(--mono)", fontSize: 11, color: "var(--muted)", marginTop: 6 }}>
          <span>−60s</span>
          <span className="tnum">μ {Math.round(c.rate.reduce((s, v) => s + v, 0) / c.rate.length * c.peakTps).toLocaleString()} ticks/s</span>
          <span>now</span>
        </div>
      </div>

      <window.SectionHeader title="Symbols · captured" count={c.symbols.length} />
      <div style={{ padding: "4px var(--pad) 16px", display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 8 }}>
        {c.symbols.map((s, i) =>
        <div key={i} style={{ padding: "10px 12px", border: "1px solid var(--rule-2)", fontFamily: "var(--mono)" }}>
            <div style={{ fontSize: 13, fontWeight: 500 }}>{s}</div>
            <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>{c.venue}</div>
          </div>
        )}
      </div>

      <window.SectionHeader title="Storage" sub={c.storage.path} />
      <div style={{ padding: "4px var(--pad) 24px" }}>
        <div className="storage-bar" style={{ height: 10 }}><div className={"fill " + (usedPct > 0.9 ? "neg" : usedPct > 0.7 ? "warn" : "")} style={{ width: usedPct * 100 + "%" }} /></div>
        <div style={{ display: "flex", justifyContent: "space-between", fontFamily: "var(--mono)", fontSize: 12, color: "var(--muted)", marginTop: 8 }}>
          <span>{c.storage.format} · {c.storage.shards.toLocaleString()} shards</span>
          <span className="tnum"><span style={{ color: "var(--ink)" }}>{c.storage.used.toFixed(1)} GB</span> / {c.storage.capacity} GB · retention {c.storage.retention}</span>
        </div>
      </div>

      {c.backfill &&
      <>
          <window.SectionHeader title="Backfill progress" sub={`${c.backfill.from} → ${c.backfill.to}`} />
          <div style={{ padding: "4px var(--pad) 24px" }}>
            <div className="storage-bar" style={{ height: 10 }}><div className="fill" style={{ width: c.backfill.progress * 100 + "%", background: "var(--accent)" }} /></div>
            <div style={{ display: "flex", justifyContent: "space-between", fontFamily: "var(--mono)", fontSize: 12, marginTop: 8 }}>
              <span className="tnum">{c.backfill.gbProcessed.toLocaleString()} / {c.backfill.gbTotal.toLocaleString()} GB</span>
              <span className="tnum"><span style={{ color: "var(--accent)" }}>{(c.backfill.progress * 100).toFixed(1)}%</span> <span className="muted">· ETA {c.backfill.etaMin}m</span></span>
            </div>
          </div>
        </>
      }

      <window.SectionHeader title="Recent events" />
      <div style={{ padding: "4px var(--pad) 24px" }}>
        <div className="feed">
          {window.COL_EVENTS.filter((e) => e.col === c.id).map((e, i) =>
          <div className="feed-row" key={i}>
              <span className="t">{e.t}</span>
              <span className="ev"><span className={"tag " + e.tag}>{e.tag.toUpperCase()}</span>{e.txt}</span>
            </div>
          )}
        </div>
      </div>
    </>);

};

window.PageConfigDetail = function PageConfigDetail({ id }) {
  const c = window.CONFIGS.find((x) => x.id === id);
  if (!c) return <div style={{ padding: 32 }}>Config not found.</div>;
  const usedByInsts = window.INSTANCES.filter((i) => i.config === c.id);
  const defs = c.strategyDefs.map((sd) => window.STRATEGY_DEFS.find((x) => x.id === sd)).filter(Boolean);
  return (
    <>
      <window.PageHeader crumbs={[{ label: "Configs", href: "#/configs" }, { label: c.label }]} actions={
      <><window.Btn>Clone</window.Btn><window.Btn>Archive</window.Btn><window.Btn kind="primary">Apply to instance</window.Btn></>
      } />
      <div className="kpis" style={{ gridTemplateColumns: "repeat(4,1fr)" }}>
        <div className="kpi"><div className="lbl">File</div><div className="val mono" style={{ fontSize: 13 }}>{c.file}</div><div className="delta mono">edited {c.lastEdit}</div></div>
        <div className="kpi"><div className="lbl">Strategies</div><div className="val mono tnum">{c.strategyDefs.length}</div></div>
        <div className="kpi"><div className="lbl">Daily loss cap</div><div className="val mono tnum">{window.fmt$(c.dailyLossCap, 0)}</div></div>
        <div className="kpi"><div className="lbl">Used by</div><div className="val mono tnum">{usedByInsts.length}</div></div>
      </div>
      <window.SectionHeader title="Strategies in this config" count={defs.length} />
      <div>
        {defs.map((d) =>
        <div key={d.id} style={{ padding: "14px var(--pad)", borderBottom: "1px solid var(--rule)", display: "grid", gridTemplateColumns: "1fr auto", gap: 12, alignItems: "center", cursor: "pointer" }} className="row-link" onClick={() => {location.hash = `#/strategies/${d.id}`;}}>
            <div>
              <div style={{ fontFamily: "var(--mono)", fontSize: 14, fontWeight: 500 }}>{d.name}</div>
              <div style={{ fontFamily: "var(--mono)", fontSize: 11, color: "var(--muted)", marginTop: 2 }}>{d.family} · {d.symbol}.{d.venue}</div>
              <div style={{ fontSize: 12, color: "var(--ink-2)", marginTop: 6, maxWidth: "60ch" }}>{d.summary}</div>
            </div>
            <div style={{ fontFamily: "var(--mono)", fontSize: 11, color: "var(--muted)", textAlign: "right" }}>
              <div>WR <span className="tnum" style={{ color: "var(--ink)" }}>{(d.bench.wr * 100).toFixed(1)}%</span></div>
              <div>PF <span className="tnum" style={{ color: "var(--ink)" }}>{d.bench.pf.toFixed(2)}</span></div>
              <div>E[R] <span className="tnum pos">{window.fmtP(d.bench.eR, 3)}</span></div>
            </div>
          </div>
        )}
      </div>
      <window.SectionHeader title="Deployed on" count={usedByInsts.length} />
      <div className="table-wrap">
        <table className="strat">
          <thead><tr><th>Instance</th><th>Host</th><th>Account</th><th>State</th><th className="num">Uptime</th></tr></thead>
          <tbody>
            {usedByInsts.map((i) =>
            <tr key={i.id} className="row-link" onClick={() => {location.hash = `#/instances/${i.id}`;}}>
                <td>{i.id}</td><td className="muted">{i.host}</td><td className="muted">{i.account || "—"}</td>
                <td><window.StateBadge state={i.state} /></td><td className="num tnum muted">{i.uptime}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>);

};

/* ── Alerts inbox (#/alerts) ───────────────────────────────────
 * Two-pane: filterable list + detail with the raw stored row.
 * Backend: GET /api/alerts (DAL row schema), POST /api/alerts/{pk}/ack.
 * window.ALERTS is the raw row array — data-live.jsx populates from
 * bootstrap step 7 and WS `kind:"alert"` push.
 *
 * The DB schema doesn't persist the original event payload (only
 * severity/source/code/message), so the "payload" panel shows the
 * stored DAL row verbatim — truthful re: what we kept, and useful
 * for tracebacks (alert_pk + ts_us + instance_id). */

window.normalizeAlertSeverity = function(s) {
  const v = String(s || "").toLowerCase();
  if (v === "crit" || v === "critical" || v === "alarm" || v === "error") return "crit";
  if (v === "warn" || v === "warning") return "warn";
  return "info";
};

window.alertSourceBucket = function(src) {
  const s = String(src || "").toLowerCase();
  if (s.startsWith("execution.") || s.startsWith("risk")) return "risk";
  if (s.startsWith("ingest") || s.startsWith("collector") || s.startsWith("feed")) return "ingest";
  if (s.startsWith("research")) return "research";
  return "system";
};

function relAgeFromUs(tsUs) {
  if (!tsUs || !Number.isFinite(tsUs)) return "—";
  const secs = Math.max(0, Math.round((Date.now() * 1000 - tsUs) / 1_000_000));
  if (secs < 60) return `${secs}s`;
  const m = Math.floor(secs / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60), mm = m % 60;
  if (h < 24) return mm ? `${h}h ${mm}m` : `${h}h`;
  const d = Math.floor(h / 24), hh = h % 24;
  return hh ? `${d}d ${hh}h` : `${d}d`;
}

// Map an alert's instance_id to a human-friendly host label using the
// live INSTANCES roster (id → host/hostname).  Falls back to the raw
// instance_id when the instance isn't in the roster (e.g. an alert from
// a since-deregistered agent), and to null when there's no id at all.
// This is what lets the operator tell alerts from different hosts apart.
function alertHostLabel(instanceId) {
  if (!instanceId) return null;
  const insts = window.INSTANCES || [];
  const m = insts.find(i => i.id === instanceId);
  if (m && m.host && m.host !== instanceId) return m.host;
  return instanceId;
}

// Exposed on window so mobile.jsx can render the live raw alerts in
// the same normalized shape as the desktop list.
window.normalizeAlertRow = normalizeAlertRow;

function normalizeAlertRow(row) {
  const sev = window.normalizeAlertSeverity(row.severity);
  const bucket = window.alertSourceBucket(row.source);
  const msg = row.message || "";
  const code = row.code || "";
  // Split only on newline (NOT period): broker/agent messages routinely
  // include period-bearing tokens — TopstepX contract IDs are dotted
  // (e.g. "CON.F.US.MNQ.U26"), reconciler messages reference paths,
  // file logs include filenames.  The old `/[\n.]/` split truncated
  // "broker holds position on UNMAPPED contract_id(s) ['CON.F.US.MNQ…']"
  // at the first dot, leaving the operator with "['CON" and no way
  // to know which contract failed.  200-char cap (was 120) gives
  // enough room for typical reconciler / risk messages without
  // overflowing a single-line head row.
  const head = msg ? msg.split(/\n/)[0].slice(0, 200) : (code || `${row.source || "alert"}`);
  return {
    id: String(row.alert_pk),
    alert_pk: row.alert_pk,
    sev,
    bucket,
    sourceRaw: row.source || "—",
    head,
    msg: msg || code || "(no message)",
    instance: row.instance_id || null,
    host: alertHostLabel(row.instance_id),
    account: row.account_label || null,
    age: relAgeFromUs(row.ts_us),
    ack: !!row.acknowledged,
    ackBy: row.ack_user || null,
    payload: row,   // the full stored DAL row
  };
}

function renderAlertJson(o, depth) {
  depth = depth || 0;
  if (o === null || o === undefined) return '<span class="null">null</span>';
  if (typeof o === "string") return '<span class="s">"' + escapeAlertHtml(o) + '"</span>';
  if (typeof o === "number") return '<span class="n">' + o + '</span>';
  if (typeof o === "boolean") return '<span class="b">' + o + '</span>';
  const indent = "  ".repeat(depth);
  if (Array.isArray(o)) {
    if (!o.length) return "[]";
    return "[\n" + o.map(v => indent + "  " + renderAlertJson(v, depth + 1)).join(",\n") + "\n" + indent + "]";
  }
  if (typeof o === "object") {
    const ks = Object.keys(o);
    if (!ks.length) return "{}";
    return "{\n" + ks.map(k => indent + '  <span class="k">"' + escapeAlertHtml(k) + '"</span>: ' + renderAlertJson(o[k], depth + 1)).join(",\n") + "\n" + indent + "}";
  }
  return String(o);
}
function escapeAlertHtml(s) {
  return String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

/* Public helper used by app.jsx to badge the NAV with the live count of
 * open crit alerts.  Defined here so the severity-mapping rules live in
 * one place. */
window.openCritAlertCount = function() {
  const arr = window.ALERTS || [];
  let n = 0;
  for (let i = 0; i < arr.length; i++) {
    const a = arr[i];
    if (!a.acknowledged && window.normalizeAlertSeverity(a.severity) === "crit") n++;
  }
  return n;
};

/* Inline "Enable browser notifications" pill — only renders when the
 * Notification API exists AND permission is still "default" (no user
 * decision yet).  Once the user grants, the WS handler in data-live.jsx
 * (maybeNotifyCrit) fires a system notification for every crit alert. */
function NotifyOptIn() {
  const [perm, setPerm] = usePg(() =>
    typeof window.Notification === "undefined" ? "unsupported" : Notification.permission,
  );
  if (perm === "unsupported" || perm === "granted" || perm === "denied") return null;
  return (
    <button onClick={async () => {
      try { setPerm(await Notification.requestPermission()); } catch (_) { setPerm("denied"); }
    }} style={{
      background: "var(--accent-soft)", color: "var(--ink)",
      border: "1px solid var(--accent)",
      fontFamily: "var(--mono)", fontSize: 10.5, padding: "4px 10px",
      letterSpacing: "0.04em", cursor: "pointer", borderRadius: 0,
    }} title="Browser will notify you for crit-severity alerts only">
      Enable crit notifications
    </button>
  );
}

/* Alerts page constants — split-pane bounds and persistence key.
 * `aviaf.alerts.listW` keeps the operator's preferred list-pane width
 * across reloads.  Bounds keep the list usable (search input legible at
 * 280px) without starving the detail pane (cap at 800px). */
const AL_LIST_W_KEY = "aviaf.alerts.listW";
const AL_LIST_W_MIN = 300;
const AL_LIST_W_MAX = 820;
const AL_LIST_W_DEFAULT = 460;

/* Pretty-print one payload value for the formatted-view KV grid.
 * Mirrors aviaf-next/s7-alerts.jsx _aFmtVal, with one extra rule:
 * us-timestamps (integer > 1e15) render as ISO + faint raw value. */
function alFmtVal(v) {
  if (v === null || v === undefined) return { text: "—", cls: "muted" };
  if (typeof v === "boolean") return { text: v ? "true" : "false", cls: v ? "pos" : "neg" };
  if (typeof v === "number") {
    if (Number.isInteger(v) && Math.abs(v) > 1e15) {
      try {
        const d = new Date(v / 1000);
        return { text: d.toISOString().replace("T", " ").replace(/\.\d+Z$/, "Z"), cls: "muted", sub: String(v) };
      } catch { /* fall through */ }
    }
    if (!Number.isInteger(v)) return { text: v.toLocaleString(undefined, { maximumFractionDigits: 6 }), cls: "" };
    return { text: v.toLocaleString(), cls: "" };
  }
  if (typeof v === "string") return { text: v, cls: "" };
  return { text: JSON.stringify(v), cls: "muted" };
}
function alFmtKey(k) { return String(k).replace(/_/g, " "); }
function alPayloadEntries(p) {
  if (!p || typeof p !== "object") return [];
  const ks = Object.keys(p);
  const head = ["code", "severity", "source", "account_label", "ts_us"].filter(k => ks.includes(k));
  const rest = ks.filter(k => !head.includes(k));
  return [...head, ...rest].map(k => [k, p[k]]);
}
function alCopyJson(payload, setCopied) {
  const text = JSON.stringify(payload, null, 2);
  const ok = () => { setCopied("copied"); setTimeout(() => setCopied(null), 1600); };
  const bad = () => { setCopied("error"); setTimeout(() => setCopied(null), 1600); };
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(text).then(ok, bad);
  } else {
    try {
      const ta = document.createElement("textarea");
      ta.value = text; document.body.appendChild(ta); ta.select();
      document.execCommand("copy"); document.body.removeChild(ta); ok();
    } catch { bad(); }
  }
}
function AlertKVRow({ k, v }) {
  if (v !== null && typeof v === "object") {
    return (
      <div className="al-kv-row al-kv-nested">
        <div className="al-kv-k">{alFmtKey(k)}</div>
        <div className="al-kv-v">
          <div className="al-kv-nested-card">
            {Object.entries(v).map(([k2, v2]) => <AlertKVRow key={k2} k={k2} v={v2} />)}
          </div>
        </div>
      </div>
    );
  }
  const fmt = alFmtVal(v);
  return (
    <div className="al-kv-row">
      <div className="al-kv-k">{alFmtKey(k)}</div>
      <div className={"al-kv-v mono " + (fmt.cls || "")}>
        {fmt.text}
        {fmt.sub && <span className="al-kv-sub mono"> {fmt.sub}</span>}
      </div>
    </div>
  );
}

/* Ops actions attached to an alert — client-side join against the incident
 * timeline already in memory (techops_actions.trigger_alert_pk). Whole
 * incident is returned when ANY of its actions was triggered by the alert,
 * so the thread shows diagnosis AND remediation rows. */
function opsActionsForAlert(pk) {
  if (pk == null) return [];
  const out = [];
  for (const inc of (window.TECHOPS_INCIDENTS || [])) {
    const acts = inc.actions || [];
    if (acts.some((x) => x.trigger_alert_pk === pk)) out.push(...acts);
  }
  return out.sort((a, b) => (a.ts_us || 0) - (b.ts_us || 0));
}

window.PageAlerts = function PageAlerts({ initialId } = {}) {
  const [, setTick] = usePg(0);
  // When the route deep-links to an alert (e.g. #/alerts/42 from the
  // Overview mini-panel), the user almost always wants to see THAT
  // alert — even if it has been acked.  Default the status filter to
  // "all" in that case so we don't immediately filter it out.
  const [filters, setFilters] = usePg(() => ({
    status: initialId ? "all" : "open",
    sev: new Set(["info", "warn", "crit"]),
    source: new Set(["risk", "ingest", "system", "research"]),
    // Hosts the operator has toggled OFF.  Empty = show every host.
    // Stored as the hidden set (not the shown set) so a newly-appearing
    // host is visible by default without re-seeding state.
    hostHidden: new Set(),
  }));
  const [query, setQuery] = usePg("");
  const [selected, setSelected] = usePg(initialId ? String(initialId) : null);
  const [acking, setAcking] = usePg(() => new Set());
  const [listOpen, setListOpen] = usePg(true);
  const [view, setView] = usePg("formatted"); // formatted | raw
  const [copied, setCopied] = usePg(null);

  // splitter width — persisted across reloads, clamped on rehydrate
  const [listW, setListW] = usePg(() => {
    try {
      const stored = Number(localStorage.getItem(AL_LIST_W_KEY));
      if (Number.isFinite(stored) && stored > 0) {
        return Math.min(AL_LIST_W_MAX, Math.max(AL_LIST_W_MIN, stored));
      }
    } catch (_) {}
    return AL_LIST_W_DEFAULT;
  });
  usePgE(() => {
    try { localStorage.setItem(AL_LIST_W_KEY, String(listW)); } catch (_) {}
  }, [listW]);
  const dragRef = usePgR(null);
  const onSplitterDown = (e) => {
    e.preventDefault();
    dragRef.current = { startX: e.clientX, startW: listW };
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    const onMove = (ev) => {
      const w = Math.min(AL_LIST_W_MAX, Math.max(AL_LIST_W_MIN, dragRef.current.startW + (ev.clientX - dragRef.current.startX)));
      setListW(w);
    };
    const onUp = () => {
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };
  // double-click splitter resets to default
  const onSplitterDouble = () => setListW(AL_LIST_W_DEFAULT);

  // Re-render on __LIVE.rev bumps (WS push mutates window.ALERTS).
  usePgE(() => {
    const id = setInterval(() => setTick(t => t + 1), 500);
    return () => clearInterval(id);
  }, []);

  const loading = window.useDataLoad ? window.useDataLoad() : false;
  const raw = window.ALERTS || [];
  const liveRev = window.__LIVE?.rev || 0;
  const normalized = usePgM(() => raw.map(normalizeAlertRow), [raw, liveRev]);

  // Distinct hosts present across all alerts (sorted, stable).  Drives the
  // optional host filter row, which only appears when alerts span more
  // than one host — single-host hubs keep the filter bar uncluttered.
  const hosts = usePgM(() => {
    const s = new Set();
    for (const a of normalized) if (a.host) s.add(a.host);
    return Array.from(s).sort();
  }, [normalized]);

  const toggleSet = (k, v) => setFilters(f => {
    const s = new Set(f[k]);
    if (s.has(v)) s.delete(v); else s.add(v);
    return { ...f, [k]: s };
  });

  const q = query.trim().toLowerCase();
  const filtered = normalized.filter(a => {
    if (filters.status === "open" && a.ack) return false;
    if (filters.status === "ack" && !a.ack) return false;
    if (!filters.sev.has(a.sev)) return false;
    if (!filters.source.has(a.bucket)) return false;
    if (a.host && filters.hostHidden.has(a.host)) return false;
    if (q) {
      const hay = [
        a.id, a.head, a.msg, a.sourceRaw, a.bucket, a.account, a.ackBy,
        a.host, a.instance,
        ...Object.entries(a.payload || {}).map(([k, v]) => k + ":" + JSON.stringify(v)),
      ].filter(Boolean).join(" ").toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  // Always honor an explicit selection — even if the current filters
  // would have hidden it (deep-links from the Overview mini-panel land
  // here with #/alerts/<pk> and the user expects THAT alert to render,
  // not the first thing the filters happened to leave behind).
  const sel = (selected
      ? (normalized.find(a => a.id === selected) || filtered.find(a => a.id === selected))
      : null) || filtered[0];
  const openCount = normalized.reduce((n, a) => n + (a.ack ? 0 : 1), 0);
  const ackCount  = normalized.length - openCount;

  async function ack(alertPk) {
    if (acking.has(alertPk)) return;
    setAcking(s => { const n = new Set(s); n.add(alertPk); return n; });
    // Optimistic in-place mutation of window.ALERTS so siblings (NAV
    // badge, list opacity) reflect the ack immediately.  We restore the
    // prior row if the POST fails.
    const arr = window.ALERTS || [];
    const idx = arr.findIndex(a => a.alert_pk === alertPk);
    const prev = idx >= 0 ? arr[idx] : null;
    if (prev) {
      arr[idx] = { ...prev, acknowledged: 1, ack_user: "you", ack_ts_us: Date.now() * 1000 };
      if (window.__LIVE) window.__LIVE.rev = (window.__LIVE.rev || 0) + 1;
      setTick(t => t + 1);
    }
    try {
      const r = await fetch(`/api/alerts/${alertPk}/ack`, {
        method: "POST", credentials: "same-origin",
      });
      if (!r.ok) throw new Error(`ack ${r.status}`);
    } catch (e) {
      if (prev) {
        const cur = (window.ALERTS || []).findIndex(a => a.alert_pk === alertPk);
        if (cur >= 0) window.ALERTS[cur] = prev;
        if (window.__LIVE) window.__LIVE.rev = (window.__LIVE.rev || 0) + 1;
        setTick(t => t + 1);
      }
      console.error("[alerts] ack failed", e);
    } finally {
      setAcking(s => { const n = new Set(s); n.delete(alertPk); return n; });
    }
  }

  // Show the loading shim while bootstrap is in flight.  Without this,
  // a hard refresh on #/alerts/<pk> renders the empty "pick an alert"
  // state for the first ~500ms before window.ALERTS arrives.
  const alertsLoaded = !!(window.__LIVE?.loaded?.alerts);
  if ((loading || !alertsLoaded) && normalized.length === 0) {
    return (
      <>
        <window.PageHeader crumbs={[{ label: "Alerts" }]} />
        <div className="al-empty">loading…</div>
      </>
    );
  }

  const ackVisibleOpen = async () => {
    const open = filtered.filter(a => !a.ack && a.alert_pk > 0);
    if (open.length === 0) return;
    if (!confirm(`Ack ${open.length} visible open alert${open.length === 1 ? "" : "s"}?`)) return;
    for (const a of open) { try { await ack(a.alert_pk); } catch (_) {} }
  };

  const entries = sel ? alPayloadEntries(sel.payload) : [];
  const sevColor = (s) => s === "crit" ? "var(--neg)" : s === "warn" ? "var(--warn)" : "var(--muted-2)";
  const sevLabel = (s) => (s || "info").toUpperCase();
  const runbook = (sel && window.RUNBOOK && window.RUNBOOK[sel.payload?.code]) || [
    "verify the event in the source surface (link below)",
    "decide: acknowledge, escalate, or mute the rule",
    "if related to risk · check the account's current position before acking",
  ];

  return (
    <>
      <window.PageHeader
        crumbs={[{ label: "Alerts" }]}
        actions={
          <>
            <NotifyOptIn />
            <window.Btn onClick={() => setListOpen(o => !o)}>
              {listOpen ? "▣ Hide list" : "▢ Show list"}
            </window.Btn>
            <window.Btn
              onClick={ackVisibleOpen}
              disabled={!filtered.some(a => !a.ack && a.alert_pk > 0)}
            >Ack visible</window.Btn>
            <CmdBtn label="Ack all open" okMsg="acked"
              disabled={openCount === 0}
              confirm={`Acknowledge ALL ${openCount} open alert${openCount === 1 ? "" : "s"} (including crits)?`}
              run={async () => { const b = await window.ackAllAlerts(false); setTick(t => t + 1); return b; }} />
            <span style={{ fontFamily: "var(--mono)", fontSize: 11, color: "var(--muted)" }}>
              {openCount} open · {ackCount} ack
            </span>
          </>
        }
      />
      <div
        className={"al-shell" + (listOpen ? "" : " al-collapsed")}
        style={listOpen ? { "--al-list-w": listW + "px" } : undefined}
      >
        {listOpen && (
          <div className="al-list">
            <div className="al-search">
              <span className="al-search-icon" aria-hidden>⌕</span>
              <input
                type="text"
                className="al-search-input mono"
                placeholder="search id, source, account, message, payload…"
                value={query}
                onChange={e => setQuery(e.target.value)}
                autoComplete="off"
                spellCheck={false}
              />
              {query && (
                <button className="al-search-clear" onClick={() => setQuery("")} aria-label="clear">✕</button>
              )}
            </div>
            <div className="al-filters">
              <span className="al-filter-lbl">status</span>
              {["all", "open", "ack"].map(s =>
                <button key={s} className="al-filter" data-on={filters.status === s} onClick={() => setFilters(f => ({ ...f, status: s }))}>{s}</button>
              )}
              <span className="al-filter-sep">·</span>
              <span className="al-filter-lbl">sev</span>
              {["info", "warn", "crit"].map(s =>
                <button key={s} className={"al-filter al-sev-" + s} data-on={filters.sev.has(s)} onClick={() => toggleSet("sev", s)}>{s}</button>
              )}
              <span className="al-filter-sep">·</span>
              <span className="al-filter-lbl">src</span>
              {["risk", "ingest", "system", "research"].map(s =>
                <button key={s} className="al-filter" data-on={filters.source.has(s)} onClick={() => toggleSet("source", s)}>{s}</button>
              )}
              {hosts.length > 1 && <>
                <span className="al-filter-sep">·</span>
                <span className="al-filter-lbl">host</span>
                {hosts.map(h =>
                  <button key={h} className="al-filter" data-on={!filters.hostHidden.has(h)}
                    onClick={() => setFilters(f => {
                      const s = new Set(f.hostHidden);
                      if (s.has(h)) s.delete(h); else s.add(h);
                      return { ...f, hostHidden: s };
                    })}>{h}</button>
                )}
              </>}
            </div>
            <div className="al-listcount mono">
              {filtered.length} {filtered.length === 1 ? "alert" : "alerts"}
              {query && <span style={{ color: "var(--muted)" }}> · matching “{query}”</span>}
            </div>

            {filtered.length === 0 && (() => {
              if (filters.status === "open" && openCount === 0 && ackCount > 0 && !q) {
                return (
                  <div className="al-empty">
                    <div className="al-empty-h" style={{ color: "var(--pos)" }}>✓ all clear · 0 open</div>
                    <div className="al-empty-s">{ackCount} alert{ackCount === 1 ? "" : "s"} in history</div>
                    <button className="al-filter" data-on={false}
                      style={{ marginTop: 12 }}
                      onClick={() => setFilters(f => ({ ...f, status: "all" }))}>show history</button>
                  </div>
                );
              }
              if (normalized.length === 0) {
                return (
                  <div className="al-empty">
                    <div className="al-empty-h">no alerts recorded yet</div>
                    <div className="al-empty-s">events will appear here as soon as the hub stores them.</div>
                  </div>
                );
              }
              if (q) {
                return (
                  <div className="al-empty">
                    <div className="al-empty-h">no alerts match “{query}”</div>
                    <div className="al-empty-s">clear the search above or adjust the filters.</div>
                  </div>
                );
              }
              return (
                <div className="al-empty">
                  <div className="al-empty-h">no alerts match these filters</div>
                  <div className="al-empty-s">adjust status / severity / source above.</div>
                </div>
              );
            })()}

            <div className="al-rows">
              {filtered.map(a =>
                <div key={a.id} className="al-row"
                  data-sev={a.sev} data-ack={a.ack} data-on={sel?.id === a.id}
                  onClick={() => setSelected(a.id)}>
                  <span className="al-row-sev sev" />
                  <div className="al-row-body">
                    <div className="al-row-head head" title={a.msg}>{a.head}</div>
                    <div className="al-row-meta meta">
                      <span className="al-row-src"><b>{a.bucket}</b></span>
                      {a.host && <span>host <b>{a.host}</b></span>}
                      {a.account && <span>acct <b>{a.account}</b></span>}
                      <span className="al-row-id">#{a.id}</span>
                    </div>
                  </div>
                  <div className="al-row-action age">
                    <span className="al-row-ageval">{a.age}</span>
                    {!a.ack &&
                      <button className="al-ack-btn ack-btn"
                        disabled={acking.has(a.alert_pk)}
                        onClick={e => { e.stopPropagation(); ack(a.alert_pk); }}>
                        {acking.has(a.alert_pk) ? "…" : "ack"}
                      </button>
                    }
                    {a.ack && <span className="al-ack-tag ack-tag">ack · {a.ackBy || "?"}</span>}
                    {opsActionsForAlert(a.alert_pk).some(x => x.status === "done") &&
                      <span className="al-ack-tag ack-tag" style={{ color: "var(--ok, #3fb950)" }}
                        title="an ops action resolved this alert">fixed</span>}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {listOpen && (
          <div
            className="al-split"
            role="separator"
            aria-orientation="vertical"
            aria-label="Drag to resize alerts list"
            title="Drag to resize · double-click to reset"
            onMouseDown={onSplitterDown}
            onDoubleClick={onSplitterDouble}
          >
            <span className="al-split-grip" aria-hidden />
          </div>
        )}

        <div className="al-detail">
          {sel ? (
            <div className="al-detail-wrap">
              {/* Hero strip */}
              <div className="al-hero">
                <div className="al-hero-l">
                  <div className="al-hero-tags">
                    <span className="al-sev-pill" data-sev={sel.sev}>
                      <span className="al-sev-dot" style={{ background: sevColor(sel.sev) }} />
                      {sevLabel(sel.sev)}
                    </span>
                    <span className="al-src-pill">{sel.sourceRaw}</span>
                    {sel.ack && <span className="al-ack-pill">ACKED · {sel.ackBy || "?"}</span>}
                    {opsActionsForAlert(sel.alert_pk).some(x => x.status === "done") &&
                      <span className="al-ack-pill" style={{ color: "var(--ok, #3fb950)" }}>REMEDIATED</span>}
                    {!sel.ack && sel.sev === "crit" && <span className="al-live-pill">LIVE</span>}
                  </div>
                  <h2 className="al-hero-title">{sel.head}</h2>
                  <div className="al-hero-sub">
                    <span>id <b>#{sel.id}</b></span>
                    <span className="al-dotsep">·</span>
                    {sel.host && <>
                      <span>host <b>{sel.host}</b></span>
                      <span className="al-dotsep">·</span>
                    </>}
                    {sel.account && <>
                      <span>account <b>{sel.account}</b></span>
                      <span className="al-dotsep">·</span>
                    </>}
                    <span>age <b>{sel.age}</b></span>
                  </div>
                </div>
                <div className="al-hero-r">
                  {!sel.ack
                    ? <button
                        className="al-cta al-cta-primary"
                        disabled={acking.has(sel.alert_pk)}
                        onClick={() => ack(sel.alert_pk)}
                      >{acking.has(sel.alert_pk) ? "Acking…" : "Ack alert"}</button>
                    : <button className="al-cta" disabled>✓ Acked</button>}
                  <div className="al-hero-actions">
                    <InvestigateAlertBtn alertPk={sel.alert_pk} />
                    <button className="al-cta-sm" onClick={() => alCopyJson(sel.payload, setCopied)} data-copied={copied || undefined}>
                      {copied === "copied" ? "✓ Copied" : copied === "error" ? "✕ Failed" : "⧉ Copy JSON"}
                    </button>
                    {sel.account && (
                      <button className="al-cta-sm" onClick={() => location.hash = "#/accounts/" + sel.account}>
                        Open {sel.account} →
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Message */}
              <div className="al-msg">
                <div className="al-section-lbl">message</div>
                <p className="al-msg-body">{sel.msg}</p>
              </div>

              {/* Ops action thread (techops_actions joined on trigger_alert_pk) */}
              {opsActionsForAlert(sel.alert_pk).length > 0 && (
                <div className="al-card">
                  <div className="al-card-h">
                    <div className="al-section-lbl">ops actions</div>
                  </div>
                  <div style={{ padding: "4px 10px 8px" }}>
                    {opsActionsForAlert(sel.alert_pk).map((x, i) =>
                      <TechopsAction a={x} key={x.action_pk || i} />)}
                  </div>
                </div>
              )}

              {/* Payload card */}
              <div className="al-card">
                <div className="al-card-h">
                  <div className="al-section-lbl">event payload</div>
                  <div className="al-view-tabs">
                    <button data-on={view === "formatted"} onClick={() => setView("formatted")}>Formatted</button>
                    <button data-on={view === "raw"} onClick={() => setView("raw")}>Raw JSON</button>
                  </div>
                  <div className="al-card-tools">
                    {sel.payload?.code && <span className="al-payload-kind mono">{sel.payload.code}</span>}
                    <button className="al-cta-sm" onClick={() => alCopyJson(sel.payload, setCopied)} data-copied={copied || undefined}>
                      {copied === "copied" ? "✓" : "⧉"} Copy for LLM
                    </button>
                  </div>
                </div>
                {view === "formatted" ? (
                  <div className="al-kv">
                    {entries.length === 0
                      ? <div className="al-kv-empty mono">(no payload fields)</div>
                      : entries.map(([k, v]) => <AlertKVRow key={k} k={k} v={v} />)}
                  </div>
                ) : (
                  <pre className="al-raw payload"
                    dangerouslySetInnerHTML={{ __html: renderAlertJson(sel.payload) }} />
                )}
              </div>

              {/* Runbook */}
              <div className="al-related">
                <div className="al-section-lbl">runbook</div>
                <ol className="al-runbook">
                  {runbook.map((step, i) => <li key={i}>{step}</li>)}
                </ol>
              </div>
            </div>
          ) : (
            <div className="al-empty" style={{ padding: "60px 24px" }}>
              <div className="al-empty-h">nothing selected</div>
              <div className="al-empty-s">pick an alert from the list to inspect its event body.</div>
            </div>
          )}
        </div>
      </div>
    </>);

};

/* ── Ops · TechOps incidents (alert → diagnosis → action → outcome) ───────
 * Renders window.TECHOPS_INCIDENTS: every fleet action the command router
 * journalled (operator clicks, autopilot, and the aviaf-claude techops
 * agent), grouped into incident timelines.  Live-updated from the "techops"
 * WS channel (see data-live.applyTechops); this page just repaints on tick. */
function _tsClock(us) {
  if (!us) return "";
  try { return new Date(us / 1000).toLocaleTimeString("en-US", { hour12: false }); }
  catch (_) { return ""; }
}
function _statusTag(s) {
  if (s === "done") return "ok";
  if (s === "failed") return "risk";
  return "warn"; // proposed | approved | executing
}
function TechopsAction({ a }) {
  return (
    <div className="feed-row" style={{ alignItems: "baseline" }}>
      <span className="t" style={{ minWidth: 68 }}>{_tsClock(a.ts_us)}</span>
      <span className="ev">
        <span className={"tag " + _statusTag(a.status)}>{(a.status || "?").toUpperCase()}</span>
        {a.tier === "destructive" && <span className="tag risk" style={{ marginLeft: 4 }}>DESTR</span>}
        {" "}<b style={{ fontWeight: 600 }}>{a.kind}</b>
        {a.account_label ? ` · ${a.account_label}` : ""}
        <span style={{ color: "var(--muted)" }}> · {(a.actor || "").replace(/^mcp:/, "@")}</span>
        {a.outcome ? <span style={{ color: "var(--muted)" }}> → {a.outcome}</span> : null}
      </span>
    </div>
  );
}
/* ── Agent-node panel (autonomy B2/B4) ──────────────────────────────────
 * The hub is the source of truth for the aviaf-claude node's autonomy
 * policy (model / effort / session budget / tier / enable flags); the node
 * pulls it via policy-sync before every tick and reports its own health
 * back.  This panel is the "manage the whole ecosystem" control surface:
 * edit → PUT /api/node/policy (journalled to the Ops timeline) → the node
 * picks it up on its next sync. */
async function _nodeApi(path, opts) {
  const r = await fetch(path, { credentials: "same-origin", ...(opts || {}) });
  const body = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(body.detail || `${r.status}`);
  return body;
}

function _useNodeState() {
  const [st, setSt] = usePg({ policy: null, status: null, err: null });
  const load = async () => {
    try {
      const [policy, status] = await Promise.all([
        _nodeApi("/api/node/policy"), _nodeApi("/api/node/status"),
      ]);
      setSt({ policy, status, err: null });
    } catch (e) { setSt(s => ({ ...s, err: String(e.message || e) })); }
  };
  usePgE(() => { load(); const id = setInterval(load, 30000); return () => clearInterval(id); }, []);
  return [st, load];
}

function _nodeAge(us) {
  if (!us) return null;
  const s = Math.max(0, (Date.now() * 1000 - us) / 1e6);
  if (s < 90) return `${Math.round(s)}s`;
  if (s < 5400) return `${Math.round(s / 60)}m`;
  if (s < 172800) return `${(s / 3600).toFixed(1)}h`;
  return `${(s / 86400).toFixed(1)}d`;
}

function NodePanel() {
  const [{ policy, status, err }, reload] = _useNodeState();
  const [draft, setDraft] = usePg(null);
  const [saving, setSaving] = usePg(false);
  const p = draft || policy;

  const dirty = draft && policy && ["model", "effort", "max_sessions_per_day",
    "techops_tier", "cr_enabled", "techops_enabled"].some(k => draft[k] !== policy[k]);

  const save = async () => {
    if (!dirty || saving) return;
    setSaving(true);
    try {
      await _nodeApi("/api/node/policy", {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(draft),
      });
      setDraft(null);
      await reload();
      window.pushToast && window.pushToast({ kind: "success", title: "Node policy saved",
        msg: "The node applies it on its next policy-sync (≤15 min / next tick)." });
    } catch (e) {
      window.pushToast && window.pushToast({ kind: "error", title: "Policy save failed",
        msg: String(e.message || e) });
    } finally { setSaving(false); }
  };

  const set = (k, v) => setDraft({ ...(draft || policy), [k]: v });
  const repoStale = status && status.repo_age_days != null && status.repo_age_days > 7;
  const beatAge = status && status.ts_us ? _nodeAge(status.ts_us) : null;
  const beatFresh = status && status.ts_us && (Date.now() * 1000 - status.ts_us) < 20 * 60 * 1e6;
  const sessions = status && status.sessions_today != null ? status.sessions_today : "?";

  const selSt = { background: "var(--paper-2)", color: "var(--ink)", border: "1px solid var(--rule)",
    borderRadius: 4, padding: "3px 6px", fontFamily: "var(--mono)", fontSize: 12 };
  const lblSt = { color: "var(--muted)", fontSize: 10, textTransform: "uppercase", letterSpacing: ".06em" };

  return (
    <div className="right-block" style={{ marginBottom: 10 }}>
      <h3 style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
        <span className={"tag " + (beatFresh ? "ok" : "warn")}>{beatFresh ? "NODE UP" : "NODE STALE"}</span>
        aviaf-claude · autonomy control
        {err && <span style={{ color: "var(--neg)", fontSize: 11, fontFamily: "var(--mono)" }}>⨯ {err}</span>}
      </h3>

      {/* health card (B2) */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 18, padding: "6px 0 10px",
        fontFamily: "var(--mono)", fontSize: 12 }}>
        <span style={lblSt}>heartbeat<br />
          <b style={{ fontSize: 13, color: beatFresh ? "var(--ink)" : "var(--warn)" }}>{beatAge ? beatAge + " ago" : "never"}</b></span>
        <span style={lblSt}>repo (aviaf)<br />
          <b style={{ fontSize: 13, color: repoStale ? "var(--neg)" : "var(--ink)" }}
             title={repoStale ? "workspace clone is stale — CR runs on old code (the 'husk' failure)" : ""}>
            {status && status.repo_sha ? String(status.repo_sha).slice(0, 7) : "?"}
            {status && status.repo_age_days != null ? ` · ${Math.round(status.repo_age_days)}d old` : ""}
            {repoStale ? " ⚠ STALE" : ""}</b></span>
        <span style={lblSt}>last CR tick<br />
          <b style={{ fontSize: 13 }}>{status && status.last_tick_us ? _nodeAge(status.last_tick_us) + " ago" : "—"}
            {status && status.last_tick_result ? ` · ${status.last_tick_result}` : ""}</b></span>
        <span style={lblSt}>sessions today<br />
          <b style={{ fontSize: 13 }}>{sessions}{p ? ` / ${p.max_sessions_per_day}` : ""}</b></span>
        {policy && <span style={lblSt}>last edit<br />
          <b style={{ fontSize: 13 }}>{policy.updated_by || "?"} · {_nodeAge(policy.updated_us)} ago</b></span>}
      </div>

      {/* policy editor (B4) */}
      {p && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 14, alignItems: "flex-end",
          borderTop: "1px solid var(--rule)", paddingTop: 10 }}>
          <label style={lblSt}>model<br />
            <select style={selSt} value={p.model} onChange={e => set("model", e.target.value)}>
              {["haiku", "sonnet", "opus", "fable"].map(m => <option key={m} value={m}>{m}</option>)}
            </select></label>
          <label style={lblSt}>effort<br />
            <select style={selSt} value={p.effort} onChange={e => set("effort", e.target.value)}>
              {["low", "medium", "high", "max"].map(m => <option key={m} value={m}>{m}</option>)}
            </select></label>
          <label style={lblSt}>sessions/day<br />
            <input type="number" min={0} max={100} style={{ ...selSt, width: 64 }}
              value={p.max_sessions_per_day}
              onChange={e => set("max_sessions_per_day", Number(e.target.value))} /></label>
          <label style={lblSt} title="safe: MCP hard-blocks money-moving actions; all: destructive tools enabled (Slack approval still gates)">techops tier<br />
            <select style={selSt} value={p.techops_tier} onChange={e => set("techops_tier", e.target.value)}>
              <option value="safe">safe</option><option value="all">all</option>
            </select></label>
          <label style={{ ...lblSt, display: "flex", alignItems: "center", gap: 6, paddingBottom: 4 }}>
            <input type="checkbox" checked={!!p.cr_enabled}
              onChange={e => set("cr_enabled", e.target.checked)} /> CR tick
          </label>
          <label style={{ ...lblSt, display: "flex", alignItems: "center", gap: 6, paddingBottom: 4 }}>
            <input type="checkbox" checked={!!p.techops_enabled}
              onChange={e => set("techops_enabled", e.target.checked)} /> auto-techops
          </label>
          <span style={{ flex: 1 }} />
          {dirty && <window.Btn kind="ghost" onClick={() => setDraft(null)}>discard</window.Btn>}
          <window.Btn kind="primary" onClick={save} disabled={!dirty || saving}>
            {saving ? "saving…" : dirty ? "Save policy" : "Saved"}</window.Btn>
        </div>
      )}
    </div>
  );
}

/* B1: operator-triggered TechOps investigation of one alert.  POSTs to the
 * hub, which journals incident inc-<pk> and delivers a focused report to the
 * node through the autopilot pipeline. */
function InvestigateAlertBtn({ alertPk }) {
  const [busy, setBusy] = usePg(false);
  const onClick = async () => {
    if (busy || alertPk == null) return;
    setBusy(true);
    try {
      const r = await _nodeApi(`/api/alerts/${alertPk}/investigate`, { method: "POST" });
      window.pushToast && window.pushToast({
        kind: r.ok ? "success" : "warn",
        title: r.ok ? "TechOps investigating" : "Journalled — delivery failed",
        msg: `incident ${r.incident_id}` + (r.ok ? " → see Ops timeline" : " (node unreachable; alert stays open)"),
      });
    } catch (e) {
      window.pushToast && window.pushToast({ kind: "error", title: "Investigate failed",
        msg: String(e.message || e) });
    } finally { setBusy(false); }
  };
  return (
    <button className="al-cta-sm" onClick={onClick} disabled={busy}
      title="Send this alert to the aviaf-claude techops agent — diagnosis + safe-tier remediation, journalled to the Ops timeline">
      {busy ? "dispatching…" : "→ TechOps"}
    </button>
  );
}

/* Compact node strip for the Research page: is the CR loop actually alive,
 * and is the workspace it runs on fresh enough to matter. */
function NodeCrStrip() {
  const [{ policy, status }] = _useNodeState();
  if (!policy && !status) return null;
  const repoStale = status && status.repo_age_days != null && status.repo_age_days > 7;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 16, padding: "6px var(--pad) 10px",
      fontFamily: "var(--mono)", fontSize: 12, color: "var(--muted)" }}>
      <span>CR tick: <b style={{ color: policy && policy.cr_enabled ? "var(--pos)" : "var(--neg)" }}>
        {policy ? (policy.cr_enabled ? "enabled" : "DISABLED") : "?"}</b> · daily 21:15 UTC</span>
      <span>last: <b>{status && status.last_tick_us ? _nodeAge(status.last_tick_us) + " ago" : "—"}</b>
        {status && status.last_tick_result ? ` (${status.last_tick_result})` : ""}</span>
      <span>sessions: <b>{status && status.sessions_today != null ? status.sessions_today : "?"}
        {policy ? `/${policy.max_sessions_per_day}` : ""}</b></span>
      <span>model: <b>{policy ? `${policy.model}·${policy.effort}` : "?"}</b></span>
      {repoStale && <span style={{ color: "var(--neg)" }}
        title="the node's aviaf clone is stale — research runs against old code">
        ⚠ node repo {Math.round(status.repo_age_days)}d stale</span>}
      <a href="#/ops" style={{ marginLeft: "auto" }}>node panel →</a>
    </div>
  );
}

window.PageTechops = function PageTechops() {
  const [, setTick] = usePg(0);
  usePgE(() => {
    const id = setInterval(() => setTick((t) => t + 1), 500);
    return () => clearInterval(id);
  }, []);
  const incidents = window.TECHOPS_INCIDENTS || [];
  const openN = incidents.filter((i) => i.open).length;

  return (
    <div>
      <NodePanel />
      <window.SectionHeader
        title="Ops · TechOps actions"
        count={incidents.length}
        sub={`${openN} open · alert → diagnosis → action → outcome (audited)`}
      />
      {incidents.length === 0 ? (
        <div style={{ padding: "18px var(--pad)", color: "var(--muted)", fontFamily: "var(--mono)", fontSize: 12 }}>
          No TechOps actions recorded yet. Every pause / config-reload / flatten /
          restart routed through the hub — by an operator, the autopilot, or the
          aviaf-claude techops agent — is journalled here.
        </div>
      ) : incidents.map((inc) => (
        <div className="right-block" key={inc.incident_id} style={{ marginBottom: 8 }}>
          <h3 style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
            <span>
              <span className={"tag " + (inc.open ? "warn" : "ok")}>{inc.open ? "OPEN" : "DONE"}</span>
              {" "}Incident <span style={{ fontFamily: "var(--mono)", color: "var(--muted)" }}>{String(inc.incident_id).slice(0, 20)}</span>
              {inc.trigger_alert_pk != null && (
                <a href={`#/alerts/${inc.trigger_alert_pk}`} style={{ marginLeft: 8, fontSize: 11 }}>
                  ↳ alert #{inc.trigger_alert_pk}
                </a>
              )}
            </span>
            <span className="tnum" style={{ color: "var(--muted)", fontSize: 11 }}>
              {inc.n_actions} action{inc.n_actions === 1 ? "" : "s"} · {_tsClock(inc.last_us)}
            </span>
          </h3>
          <div className="feed">
            {(inc.actions || []).map((a, i) => <TechopsAction a={a} key={a.action_pk || i} />)}
          </div>
        </div>
      ))}
    </div>
  );
};